import { useCallback, useEffect, useState } from 'react';

export type Lang = 'en' | 'ar';
export type Dir = 'ltr' | 'rtl';

export const I18N: Record<Lang, Record<string, string>> = {
  en: {
    dashboard: 'Dashboard',
    accounting: 'Accounting',
    subscriptions: 'Subscriptions',
    expenses: 'Expenses',
    payroll: 'Payroll',
    employees: 'Employees',
    contracts: 'Contracts',
    invoices: 'Invoices',
    quotations: 'Quotations',
    bills: 'Bills',
    payments: 'Payments',
    bank: 'Bank',
    customers: 'Customers',
    vendors: 'Vendors',
    chart_of_accounts: 'Chart of Accounts',
    journal: 'Journal',
    reports: 'Reports',
    users: 'Users & Roles',
    settings: 'Settings',
    finance: 'Finance',
    people: 'People',
    sales: 'Sales',
    system: 'System',
    recurring: 'Recurring',
    welcome: 'Welcome back',
    search: 'Search anything…',
    new_: 'New',
    export_: 'Export',
    print: 'Print',
    refresh: 'Refresh',
    revenue: 'Revenue',
    net_profit: 'Net profit',
    burn: 'Monthly burn',
    cash: 'Cash on hand',
    name: 'Name',
    email: 'Email',
    role: 'Role',
    status: 'Status',
    amount: 'Amount',
    date: 'Date',
    actions: 'Actions',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    view: 'View',
    active: 'Active',
    paused: 'Paused',
    overdue: 'Overdue',
    draft: 'Draft',
    sent: 'Sent',
    signed: 'Signed',
    paid: 'Paid',
    pending: 'Pending',
    this_week: 'Week',
    this_month: 'Month',
    this_year: 'Year',
    revenue_vs_expenses: 'Revenue vs Expenses',
    vs_last: 'vs last month',
    quick_actions: 'Quick actions',
    recent: 'Recent activity',
    bhd: 'BHD',
    usd: 'USD',
    collapse_sidebar: 'Collapse',
    expand_sidebar: 'Expand',
    /* The + on a navigation row. Read out as "New: Invoices", because every one
       of these buttons would otherwise be announced as the same bare "New" and
       a screen reader would offer twelve identical controls. */
    nav_new: 'New',
    sign_in: 'Sign in',
    sign_in_lede: 'Use your company credentials. New here? Ask your admin to invite you.',
    password: 'Password',
    keep_signed_in: 'Keep me signed in',
    forgot: 'Forgot?',
    continue_: 'Continue',
    invalid_credentials: 'Invalid email or password.',
    internal_system: 'Internal system — access restricted to Join Future personnel.',
    coming_soon: 'Coming soon',
    coming_soon_sub: 'This module is part of a later build unit and is not available yet.',
    back_to_dashboard: 'Back to dashboard',
    not_found: 'Page not found',
    not_found_sub: 'The route you tried to open does not exist.',
    forbidden: 'Forbidden',
    forbidden_sub: 'Your role does not have access to this page.',
    logout: 'Sign out',
    notifications: 'Notifications',
    loading: 'Loading',
    no_data: 'No data yet',
    dashboard_unavailable: 'Dashboard data is not available yet.',

    // shared / table
    search_placeholder: 'Search…',
    show_inactive: 'Show inactive',
    only_active: 'Active only',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    optional: 'optional',
    confirm: 'Confirm',
    saving: 'Saving…',
    creating: 'Creating…',
    deleting: 'Deleting…',
    inactive: 'Inactive',
    restore: 'Restore',
    set_default: 'Set default',
    default_: 'Default',
    none: 'None',
    error_generic: 'Something went wrong.',
    saved: 'Saved.',
    created: 'Created.',
    deleted: 'Deleted.',
    restored: 'Restored.',
    posted: 'Posted.',
    uploaded: 'Uploaded.',
    uploading: 'Uploading…',
    preview_pdf: 'Preview PDF',
    /* The document preview at the top of a detail pane. `{number}` is the
       document number, and it is a placeholder rather than a concatenation
       because the number goes on the other side of the noun in Arabic. */
    document_preview_of: 'Preview of document {number}',
    document_preview_preparing: 'Preparing this document',
    document_preview_failed: 'This document could not be prepared.',
    open_document_full: 'Open full size',
    open_in_new_tab: 'Open in a new tab',
    try_again: 'Try again',
    attachments: 'Attachments',
    no_attachments: 'No attachments yet',
    confirm_delete_attachment: 'Delete this file?',

    // customers / vendors
    new_customer: 'New customer',
    edit_customer: 'Edit customer',
    new_vendor: 'New vendor',
    edit_vendor: 'Edit vendor',
    customer_details: 'Customer details',
    vendor_details: 'Vendor details',
    name_ar: 'Name (Arabic)',
    phone: 'Phone',
    vat_number: 'VAT number',
    cr_number: 'CR number',
    address: 'Address',
    notes: 'Notes',
    recent_invoices: 'Recent invoices',
    recent_quotations: 'Recent quotations',
    recent_bills: 'Recent bills',
    recent_expenses: 'Recent expenses',
    no_invoices: 'No invoices yet',
    no_bills: 'No bills yet',
    no_expenses: 'No expenses yet',
    no_quotations: 'No quotations yet',
    confirm_delete_customer: 'Mark this customer as inactive?',
    confirm_delete_vendor: 'Mark this vendor as inactive?',
    confirm_delete_account: 'Delete this account? This cannot be undone.',

    // accounts
    chart_of_accounts_sub: 'All ledger accounts grouped by type.',
    new_account: 'New account',
    edit_account: 'Edit account',
    code: 'Code',
    type: 'Type',
    normal_balance: 'Normal balance',
    parent: 'Parent',
    debit: 'Debit',
    credit: 'Credit',
    asset: 'Assets',
    liability: 'Liabilities',
    equity: 'Equity',
    expense: 'Expenses',
    balance: 'Balance',
    system_account: 'System',

    // bank accounts
    bank_accounts: 'Bank accounts',
    new_bank_account: 'New bank account',
    edit_bank_account: 'Edit bank account',
    opening_balance: 'Opening balance',
    opening_balance_hint: 'Optional. Posts owner capital to the ledger against Owner Equity.',
    bank_name: 'Bank name',
    iban: 'IBAN',
    currency: 'Currency',

    // journal
    journal_viewer: 'Journal viewer',
    post_manual_entry: 'Post manual entry',
    journal_entry: 'Journal entry',
    entry_number: 'Entry #',
    entry_date: 'Entry date',
    memo: 'Memo',
    source: 'Source',
    total: 'Total',
    from_date: 'From',
    to_date: 'To',
    account: 'Account',
    add_line: 'Add line',
    remove: 'Remove',
    debits_total: 'Debits',
    credits_total: 'Credits',
    balanced: 'Balanced',
    out_of_balance: 'Out of balance',
    difference: 'Difference',
    reversed: 'Reversed',
    manual: 'Manual',
    invoice: 'Invoice',
    bill: 'Bill',
    payment: 'Payment',
    payroll_src: 'Payroll',
    subscription: 'Subscription',

    // === Wave 3 Group A ===
    new_invoice: 'New invoice',
    edit_invoice: 'Edit invoice',
    invoice_details: 'Invoice details',
    customer: 'Customer',
    issue_date: 'Issue date',
    due_date: 'Due date',
    bill_date: 'Bill date',
    expires_at: 'Expires at',
    expense_date: 'Expense date',
    vendor: 'Vendor',
    category: 'Category',
    method: 'Method',
    bank_account: 'Bank account',
    bank_account_in: 'Bank account that will receive the payment',
    bank_account_out: 'Bank account that will disburse the payment',
    expense_account: 'Expense account',
    description: 'Description',
    qty: 'Qty',
    rate: 'Rate',
    line_amount: 'Amount',
    subtotal: 'Subtotal',
    vat: 'VAT',
    vat_rate: 'VAT rate',
    vat_amount: 'VAT amount',
    discount: 'Discount',
    grand_total: 'Total',
    amount_paid: 'Paid',
    amount_due: 'Outstanding',
    total_due: 'Total due',
    add_line_item: 'Add line',
    no_lines: 'No line items yet',
    select_customer: 'Select a customer',
    select_vendor: 'Select a vendor',
    select_account: 'Select an account',
    select_bank_account: 'Select a bank account',

    // Advance payment
    advance_section: 'Advance payment',
    advance_type: 'Bill an advance',
    advance_none: 'None — bill the full amount',
    advance_type_percent: 'Percent of net',
    advance_type_amount: 'Fixed amount',
    advance_percent_value: 'Advance %',
    advance_amount_value: 'Advance amount',
    advance_percent_hint: '0–100 (e.g. 50 for 50%)',
    advance_amount_hint: 'At most subtotal − discount',
    advance_custom_label_en: 'Line label (English)',
    advance_custom_label_ar: 'Line label (Arabic)',
    advance_default_label: 'Advance Payment',
    advance_balance_later: 'Balance to be invoiced later',
    advance_badge: 'Advance',
    advance_value_required: 'Enter the advance value',
    advance_invalid: 'The advance rounds to zero — raise it or bill the full amount',
    advance_percent_invalid: 'Advance cannot exceed 100%',
    advance_exceeds_net: 'Advance cannot exceed subtotal minus discount',
    advance_type_required: 'Select an advance type',

    new_quotation: 'New quotation',
    edit_quotation: 'Edit quotation',
    quotation_details: 'Quotation details',

    new_bill: 'New bill',
    edit_bill: 'Edit bill',
    bill_details: 'Bill details',

    new_expense: 'New expense',
    edit_expense: 'Edit expense',
    expense_details: 'Expense details',

    // Action labels
    send_: 'Send',
    sign_: 'Sign',
    convert_to_invoice: 'Convert to invoice',
    record_payment: 'Record payment',
    void_: 'Void',
    download_pdf: 'Download PDF',
    submit_: 'Submit',
    approve_: 'Approve',
    reject_: 'Reject',
    post_: 'Post',
    sent_ok: 'Sent.',
    signed_ok: 'Signed.',
    rejected_ok: 'Rejected.',
    voided_ok: 'Voided.',
    payment_recorded: 'Payment recorded.',
    submitted_ok: 'Submitted.',
    approved_ok: 'Approved.',
    converted_ok: 'Converted to invoice.',

    // Statuses
    status_draft: 'Draft',
    status_sent: 'Sent',
    status_signed: 'Signed',
    status_expired: 'Expired',
    status_rejected: 'Rejected',
    status_partial: 'Partial',
    status_paid: 'Paid',
    status_overdue: 'Overdue',
    status_void: 'Void',
    status_open: 'Open',
    status_submitted: 'Submitted',
    status_approved: 'Approved',

    // Filters
    filter_status: 'Status',
    all_statuses: 'All statuses',

    // Pagination
    prev: 'Previous',
    next: 'Next',
    page: 'Page',
    of: 'of',

    // Inline validation
    discount_exceeds_subtotal: 'Discount cannot exceed subtotal',
    invalid_url: 'Enter a valid URL (https://…)',

    // Confirms
    confirm_void_invoice: 'Void this invoice? Its journal entry will be reversed.',
    confirm_void_bill: 'Void this bill? Its journal entry will be reversed.',
    confirm_delete_invoice: 'Delete this draft invoice?',
    confirm_delete_quotation: 'Delete this draft quotation?',
    confirm_delete_bill: 'Delete this draft bill?',
    confirm_delete_expense: 'Delete this draft expense?',
    confirm_reject_quotation: 'Reject this quotation?',
    confirm_reject_expense: 'Reject this expense?',

    // Modal labels
    payment_amount: 'Amount',
    paid_at: 'Paid at',
    payment_method: 'Method',
    payment_reference: 'Reference',
    payment_memo: 'Memo',
    method_bank: 'Bank',
    method_cash: 'Cash',
    method_card: 'Card',
    method_wire: 'Wire',
    payments_history: 'Payments',
    no_payments: 'No payments recorded',
    journal_summary: 'Journal entry',
    no_journal_entry: 'No journal entry posted yet',

    // Misc
    converted_invoice: 'Converted invoice',
    convert: 'Convert',
    notes_optional: 'Notes (optional)',
    line_subtotal: 'Subtotal',
    line_total_due: 'Outstanding',
    invoice_summary: 'Summary',
    quotation_summary: 'Summary',
    bill_summary: 'Summary',
    expense_summary: 'Summary',
    submitted_by: 'Submitted by',
    approved_by: 'Approved by',
    receipt_url: 'Receipt URL',
    save_draft: 'Save draft',

    // === Wave 3 Group B ===
    // employees
    new_employee: 'New employee',
    edit_employee: 'Edit employee',
    employee_details: 'Employee details',
    employee_number: 'Employee #',
    department: 'Department',
    contract_type: 'Contract type',
    base_salary: 'Base salary',
    joined_at: 'Joined at',
    terminated_at: 'Terminated at',
    full_time: 'Full-time',
    part_time: 'Part-time',
    contractor: 'Contractor',
    terminated: 'Terminated',
    terminate: 'Terminate',
    confirm_terminate_employee:
      'Terminate this employee? Any linked user account will be deactivated and all sessions revoked. This cannot be undone.',
    recent_payslips: 'Recent payslips',
    no_payslips: 'No payslips yet',
    period: 'Period',
    gross: 'Gross',
    deductions: 'Deductions',
    net_amount: 'Net',
    linked_user: 'Linked user',
    no_linked_user: 'No linked user',
    // payroll
    payroll_runs: 'Payroll runs',
    new_payroll_run: 'New payroll run',
    payroll_run: 'Payroll run',
    payroll_run_details: 'Payroll run details',
    period_start: 'Period start',
    period_end: 'Period end',
    pay_date: 'Pay date',
    total_gross: 'Total gross',
    total_deductions: 'Total deductions',
    total_net: 'Total net',
    payslips: 'Payslips',
    employee: 'Employee',
    payment_status: 'Payment status',
    post_run: 'Post run',
    pay_run: 'Pay run',
    delete_run: 'Delete run',
    confirm_delete_payroll_run: 'Delete this draft payroll run?',
    confirm_post_payroll_run:
      'Post this payroll run? Each payslip will be ledger-posted and cannot be deleted afterwards.',
    pick_bank_account: 'Pick a bank account',
    pay_payroll_title: 'Pay payroll run',
    optional_employees: 'Specific employees (optional)',
    optional_employees_hint: 'Leave empty to include all active employees.',
    no_payslips_in_run: 'No payslips in this run',
    // subscriptions
    new_subscription: 'New subscription',
    edit_subscription: 'Edit subscription',
    subscription_details: 'Subscription details',
    service: 'Service',
    cost: 'Cost',
    cycle: 'Cycle',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
    auto_charge: 'Auto-charge',
    auto: 'Auto',
    manual_charge: 'Manual',
    pick_vendor: 'Pick a vendor',
    pick_expense_account: 'Pick an expense account',
    owner: 'Owner',
    pick_owner_employee: 'Pick an owner employee',
    pause: 'Pause',
    resume: 'Resume',
    charge_now: 'Charge now',
    recent_charges: 'Recent charges',
    no_charges: 'No charges yet',
    charged_at: 'Charged at',
    cancelled: 'Cancelled',
    confirm_cancel_subscription:
      'Cancel this subscription? It will stop generating new charges.',
    confirm_charge_now:
      'Generate a vendor bill for this subscription now? The next charge date will advance.',
    confirm_delete_subscription: 'Delete this subscription?',
    // contracts
    new_contract: 'New contract',
    edit_contract: 'Edit contract',
    contract_details: 'Contract details',
    party: 'Party',
    party_name: 'Party name',
    kind: 'Kind',
    contract_value: 'Value',
    ends_at: 'Ends at',
    body: 'Body',
    body_md: 'Body (English Markdown)',
    body_md_ar: 'Body (Arabic Markdown)',
    service_agreement: 'Service agreement',
    saas_subscription: 'SaaS subscription',
    employment: 'Employment',
    nda: 'NDA',
    mou: 'MOU',
    other: 'Other',
    expired: 'Expired',
    sign: 'Sign',
    signed_at: 'Signed at',
    confirm_sign_contract: 'Mark this contract as signed?',
    confirm_terminate_contract: 'Terminate this contract? This cannot be undone.',
    confirm_delete_contract: 'Delete this draft contract?',
    // contract e-signature & templates
    esign_employer: 'Employer signature',
    esign_employee: 'Employee signature',
    esign_sign_here: 'Sign here',
    esign_clear: 'Clear',
    esign_submit: 'Submit signature',
    esign_name_placeholder: 'Full name',
    esign_signed_by: 'Signed by',
    esign_signed_on: 'Signed on',
    esign_awaiting: 'Awaiting signature',
    esign_saved: 'Signature saved.',
    contract_templates: 'Contract Templates',
    new_template: 'New Template',
    load_template: 'Load Template',
    template_name: 'Template name',
    template_name_ar: 'Template name (Arabic)',
    select_template: 'Select a template…',
    it_employment_template: 'IT Employment (Part-time)',
    linked_employee: 'Linked employee',
    select_employee: 'Select an employee',
    cpr: 'CPR / National ID',
    employee_contracts: 'Contracts',
    no_contracts: 'No contracts yet',
    pdf_preview: 'Preview PDF',

    // === Wave 3 Group C ===
    // Settings
    settings_company: 'Company',
    settings_tax: 'Tax',
    settings_branding: 'Branding',
    settings_subtitle: 'Company profile, tax defaults, and document branding.',
    company_name: 'Company name',
    company_name_ar: 'Company name (Arabic)',
    default_currency: 'Default currency',
    language: 'Language',
    default_vat_rate: 'Default VAT rate (%)',
    fy_start_month: 'Fiscal year starts',
    invoice_terms: 'Invoice terms & conditions',
    bank_iban: 'Bank IBAN',
    payment_details: 'Payment details',
    document_bank_account: 'Bank account on documents',
    document_bank_account_custom: 'Custom',
    document_bank_account_hint: 'This is the account customers are asked to pay into on their invoices.',
    document_bank_account_empty: 'No bank accounts yet — add one first in',
    settings_saved: 'Settings saved.',
    month_jan: 'January',
    month_feb: 'February',
    month_mar: 'March',
    month_apr: 'April',
    month_may: 'May',
    month_jun: 'June',
    month_jul: 'July',
    month_aug: 'August',
    month_sep: 'September',
    month_oct: 'October',
    month_nov: 'November',
    month_dec: 'December',

    // Users
    users_subtitle: 'Manage who can sign in and what they can do.',
    invite_heading: 'Join {org} on Railit',
    invite_body: 'You were invited as {role}. This link belongs to {email}.',
    invite_existing_account: 'You already have a Railit account. Accepting adds this company to it, and you switch between them from the top of the screen.',
    invite_accept: 'Accept and open the workspace',
    invite_invalid_title: 'This invitation is no longer valid',
    invite_invalid_body: 'It may have expired, been used already, or been withdrawn. Ask whoever invited you to send a new one.',
    wa_country: 'Country',
    wa_country_code: 'Country code',
    notes_empty: 'No notes on this record yet.',
    notes_placeholder: 'Something a colleague reading this in six months would want to know.',
    note_add: 'Add note',
    note_by_ai: 'AI',
    note_asked_for_by: 'asked for by',
    note_written_by: 'by',
    wa_other_country: 'Other country',
    wa_whose_phone: 'Whose phone',
    wa_my_own_phone: 'My own phone',
    wa_number_local: 'Phone number',
    wa_will_link: 'Will link',
    wa_my_numbers: 'My numbers',
    wa_my_numbers_lede: 'Add your own phone and message the assistant from WhatsApp. Several numbers are free. It answers about this company, and asks which one if you keep more than one set of books.',
    wa_assistant_number: 'Message the assistant on',
    wa_add_number: 'Add my number',
    wa_add_number_hint: 'Send it a message from that phone afterwards. The first message is what proves the number is yours.',
    wa_number_added: 'Number added. Message the assistant from it to finish.',
    wa_verified: 'Verified',
    wa_awaiting_first_message: 'Waiting for your first message',
    wa_no_numbers: 'No numbers yet.',
    wa_no_usage_yet: 'Nothing spent from this number yet.',
    wa_credits_30d: 'credits, 30 days',
    wa_turns_30d: 'conversations',
    wa_last_used: 'last used',
    add_user: 'Add user',
    send_invitation: 'Send invitation',
    invite_link_hint: 'The invitation is on its way. If the email does not arrive, send them this link instead:',
    new_user: 'New user',
    edit_user: 'Edit user',
    reset_password: 'Reset password',
    disable_user: 'Disable user',
    confirm_disable_user: 'Disable this user? They will no longer be able to sign in.',
    user_disabled: 'User disabled.',
    password_reset_ok: 'Password updated.',
    user_created: 'User created.',
    user_updated: 'User updated.',
    role_owner: 'Owner',
    role_admin: 'Admin',
    role_finance: 'Finance',
    role_engineer: 'Engineer',
    role_sales: 'Sales',
    role_designer: 'Designer',
    role_viewer: 'Viewer',
    mfa: 'MFA',
    mfa_enabled: 'MFA on',
    mfa_disabled: 'MFA off',
    last_login_at: 'Last login',
    new_password: 'New password',
    confirm_password: 'Confirm password',
    password_rule: 'At least 8 characters, with letters and numbers.',
    password_mismatch: 'The two passwords do not match.',
    back_to_login: 'Back to sign in',
    reset_title: 'Set a new password',
    reset_lede: 'Choose a password for your Railit account.',
    reset_submit: 'Save the new password',
    reset_no_token: 'This page needs the link from your email. Ask for a new one from the sign-in page.',
    reset_expired: 'This link has expired. Links last one hour.',
    reset_used: 'This link has already been used. Ask for a new one.',
    reset_invalid: 'This link is not valid.',
    reset_ask_again: 'Send me a new link',
    reset_done_title: 'Your password is set',
    reset_done_body: 'You can sign in with it now. Any other device that was signed in has been signed out.',
    password_min_8: 'At least 8 characters, including a letter and a number.',
    never_signed_in: 'Never',

    // Dashboard (extra)
    revenue_mtd: 'Revenue MTD',
    profit_mtd: 'Profit MTD',
    monthly_burn: 'Monthly burn',
    cash_on_hand: 'Cash on hand',
    recent_activity: 'Recent activity',
    upcoming: 'Upcoming',
    overdue_invoices: 'Overdue invoices',
    subs_due_7d: 'Subscriptions due in 7 days',
    no_recent_activity: 'No recent activity',
    no_overdue_invoices: 'No overdue invoices',
    no_upcoming_subs: 'No subscriptions due this week',
    next_charge: 'Next charge',
    today: 'Today',
    custom: 'Custom',
    income_and_expense: 'Income & Expense',
    top_expenses: 'Top Expenses',
    all_expenses: 'All Expenses',
    total_receivables: 'Total Receivables',
    total_payables: 'Total Payables',
    current: 'Current',
    cash_flow: 'Cash Flow',
    incoming: 'Incoming',
    outgoing: 'Outgoing',
    net_cash: 'Net Cash',
    profit: 'Profit',

    // Reports
    reports_subtitle: 'Financial statements, AR ageing, and quick PDF exports.',
    report_pnl: 'Profit & Loss',
    report_balance_sheet: 'Balance Sheet',
    report_cashflow: 'Cash flow',
    report_ar_aging: 'AR Aging',
    report_pnl_sub: 'Revenue, expenses and net profit for the selected period.',
    report_balance_sheet_sub: 'Assets, liabilities and equity at a point in time.',
    report_cashflow_sub: 'Cash inflows and outflows over the selected period.',
    report_ar_aging_sub: 'Outstanding receivables grouped by age.',
    quick_pdfs: 'Quick PDFs',
    weekly_pdf: 'Weekly report (PDF)',
    daily_pdf: 'Daily report (PDF)',
    weekly_pdf_sub: 'Generate this week’s management summary.',
    daily_pdf_sub: 'Generate today’s daily snapshot.',
    download: 'Download',
    pdf_downloaded: 'PDF downloaded.',
    week_number: 'Week number',
    year: 'Year',
    prepared_by: 'Prepared by',
    pnl_revenue: 'Revenue',
    pnl_expenses: 'Expenses',
    pnl_net: 'Net profit',
    bs_assets: 'Assets',
    bs_liabilities: 'Liabilities',
    bs_equity: 'Equity',
    cf_opening: 'Opening balance',
    cf_inflows: 'Inflows',
    cf_outflows: 'Outflows',
    cf_closing: 'Closing balance',
    cf_total_in: 'Total inflows',
    cf_total_out: 'Total outflows',
    aging_as_of: 'As of',
    no_invoices_in_bucket: 'No invoices in this bucket',
    as_of: 'As of',

    // Settings: MCP
    settings_mcp: 'MCP',
    mcp_headline: 'Ask Claude about {org}',
    mcp_lead: 'Connect the assistant you already talk to and it answers from the real books: what is overdue, what a customer owes, what last month cost. It can draft a document and file it for you too.',
    mcp_state_connected: 'Connected',
    mcp_state_not_connected: 'Not connected',
    mcp_connect_claude: 'Connect Claude',
    mcp_connect_how: 'This copies the link and opens Claude’s connectors settings in a new tab. Choose Add custom connector, paste the link there, and sign in to Railit to finish.',
    mcp_link_label: 'The MCP link for {org}',
    mcp_link_short: 'Link',
    mcp_copy: 'Copy',
    mcp_copied: '{what} copied',
    mcp_no_endpoint: 'This workspace has no MCP address yet.',
    mcp_scope_title: 'One company, and it is this one',
    mcp_scope_body: 'Every company you belong to gets its own link and its own grant. This one names {org}, and keeps naming it for the life of the connection. Nothing in your other companies is reachable through it.',
    mcp_acts_title: 'It acts as you',
    mcp_acts_body: 'A connected client has exactly the permissions you have in {org} and none beyond them, and every action it takes is written to the activity log under your name.',
    mcp_connected_title: 'Connected now',
    mcp_client_unnamed: 'AI client',
    mcp_granted_on: 'Connected {date}',
    mcp_last_used: 'Last used {when}',
    mcp_never_used: 'Never used',
    mcp_disconnect: 'Disconnect',
    mcp_other_title: 'Other apps',
    mcp_other_body: 'Claude Desktop and Claude on the phone take the same link by the same steps. ChatGPT reaches an MCP server on the web only, and only once developer mode is switched on in its own settings, so it is the same link but not the same three clicks. Anything else that asks for an MCP server URL takes the link above unchanged.',
    mcp_tools_title: 'What it can reach ({n} tools)',
    mcp_tokens_title: 'Access tokens',
    mcp_tokens_body: 'For a client that cannot open a sign-in page. A token carries your permissions in {org} until you revoke it, so keep it like a password.',
    mcp_token_name: 'What is it for',
    mcp_token_create: 'Create token',
    mcp_token_default_name: 'Access token',
    mcp_token_short: 'Token',
    mcp_copy_once: 'Copy it now. It is never shown again.',
    mcp_no_tokens: 'No tokens.',
    mcp_revoke: 'Revoke',
    mcp_token_revoked: 'Revoked {date}',
    mcp_forbidden_title: 'Only an owner or an admin can set this up',
    mcp_forbidden_body: 'Connecting an AI app hands it the whole of this company’s books, so the page is kept to the people who can already see all of them. Ask an owner to connect it.',
    mcp_unavailable_title: 'The MCP settings could not be loaded',
    net_income_trend: 'Net income trend',
    access_denied: 'Access denied',
    recent_subscriptions: 'Recent subscriptions',
    no_subscriptions: 'No subscriptions',
    enable_user: 'Enable user',
    user_enabled: 'User enabled.',
    subs_mtd: 'MTD',
    live_cost_from: 'Live from',
    live_cost_updated: 'updated',
    live_cost_integration: 'integration',

    // ===== Railit SaaS =====
    crm: 'Customers & sales',
    org_settings: 'Organization settings',
    settings_organization: 'Organization',
    settings_billing: 'Billing',
    settings_whatsapp: 'WhatsApp',

    // auth
    create_account: 'Create your account',
    signup_lede: 'Your workspace is ready in under a minute. 10 days free, no card needed.',
    signup_failed: 'Could not create the account. Try again.',
    company_name_required: 'Enter your company name',
    your_name: 'Your name',
    name_required: 'Enter your name',
    work_email: 'Work email',
    email_invalid: 'Enter a valid email',
    password_min: 'At least 8 characters',
    start_free_trial: 'Start free trial',
    have_account: 'Already have an account?',
    no_account: "Don't have an account?",
    trial: 'Trial',
    trial_days_n: '{n} days free',
    no_card_needed: 'No card needed',
    from_price: 'From',
    per_month: 'month',
    price_extra_user: '+ $4 per extra user',

    // modules
    mod_finance: 'Finance',
    mod_finance_sub: 'Invoices, bills, VAT, ledger',
    mod_crm: 'CRM',
    mod_crm_sub: 'Customers, quotes, contracts',
    mod_hr: 'HR',
    mod_hr_sub: 'Employees, payroll, payslips',
    mod_inventory: 'Inventory',
    mod_inventory_sub: 'Items, stock, movements',
    mod_pos: 'POS',
    mod_pos_sub: 'Counter sales into the ledger',
    mod_ai: 'AI agents',
    mod_ai_sub: 'Chat, MCP, WhatsApp',

    // billing
    billing_base: 'Plan',
    billing_extra_users: 'Extra users ({n})',
    billing_monthly_total: 'Monthly total',
    billing_renews_on: 'Renews on {date}',
    billing_manage: 'Manage billing',
    billing_subscribe: 'Subscribe',
    billing_addons: 'Add-ons',
    billing_seats: 'Seats',
    billing_seats_detail: '{used} paid user(s). The first {included} is included in the plan; each extra one is billed monthly.',
    billing_seats_short: '{used} paid user(s) · {price} / month',
    agent_session_cost: 'Credits used in this conversation',
    agent_turn_cost: '{n} credits',
    agent_attach: 'Attach',
    agent_voice: 'Voice',
    agent_stop: 'Stop',
    agent: 'Agent',
    agent_new: 'New chat',
    agent_history: 'History',
    agent_wide: 'Widen',
    agent_narrow: 'Narrow',
    agent_send: 'Send',
    agent_greeting: 'How can I help?',
    agent_scope: 'Acting in {org}',
    agent_placeholder: 'Ask anything…',
    agent_not_wired: 'The agent is not connected yet.',
    agent_failed: 'Something went wrong. Try again.',
    agent_no_credits: 'This workspace is out of AI credits. Top up in Billing.',
    agent_stopped_budget: 'Stopped — that task was longer than one request allows.',
    agent_stopped_credits: 'Stopped early to leave you some credits.',
    agent_files: 'Files',
    agent_resize: 'Resize the panel',
    agent_expand_full: 'Open in Agent mode',
    agent_snap_release: 'Release to open Agent mode',
    agent_snap_confirm: 'Press Enter to open Agent mode',
    agent_doc_loading: 'Rendering…',
    agent_doc_download: 'Download',
    agent_doc_failed: 'Could not open this document',
    agent_file_unread: 'not read',
    agent_file_too_big: '{name} is larger than 10 MB.',
    agent_remove_file: 'Remove',
    agent_reading: 'Reading your files…',
    agent_thinking: 'Working…',
    agent_proposed: 'Proposed change',
    agent_apply: 'Apply',
    agent_apply_anyway: 'Post it anyway',
    agent_discard: 'Discard',
    agent_applied: 'Applied',
    agent_discarded: 'Discarded',
    agent_no_longer_waiting: 'This proposal is no longer waiting.',
    agent_apply_failed: 'Could not apply that. It may have expired.',
    agent_not_applied: 'Nothing has been saved yet.',
    agent_steps: '{n} steps',
    agent_steps_one: '1 step',
    agent_steps_failed: 'one failed',
    agent_no_history: 'No conversations yet.',
    agent_untitled: 'Untitled',
    agent_when_today: 'Today',
    agent_when_yesterday: 'Yesterday',
    agent_when_week: 'Last 7 days',
    agent_when_older: 'Older',
    vs_last_month: 'vs. same period last month',
    recurring_commitments: 'Recurring commitments',
    across_accounts: 'Across all accounts',
    pct_overdue: '{n}% of this is already overdue',
    view_all: 'View all',
    pdf_failed: 'Could not build the PDF. Try again.',
    agent_threads: 'Conversations',
    agent_greeting_hint: 'Ask about your ledger, or attach a file to work from.',
    agent_mode: 'Agent mode',
    agent_mode_on: 'Agent',
    agent_mode_off: 'Manual',
    agent_mode_hint: 'Turn the whole workspace into a conversation.',
    model_auto: 'Auto',
    model_auto_hint: 'Picks the right level for each question — cheap on a lookup, careful on a reconciliation.',
    model_pick_hint: 'How carefully should the agent work?',
    model_per_month: '~{n} questions a month',
    model_credits_each: '{n} credits each',
    model_thinking: 'Think',
    model_thinking_hint: 'Reason step by step before answering. Slower, and about twice the credits.',
    model_thinking_cost: 'About {n} credits a question with thinking on.',
    thinking_live: 'Thinking…',
    thinking_done: 'Thought about this',
    memory: 'Memory',
    memory_title: 'What the agent remembers',
    memory_subtitle: 'Facts it keeps between conversations, so you do not have to repeat them. Delete anything that is wrong or out of date.',
    memory_company: 'About your company',
    memory_company_hint: 'everyone in the workspace sees these',
    memory_personal: 'About you',
    memory_personal_hint: 'only you see these',
    memory_by_you: 'you wrote this',
    memory_by_agent: 'the agent noted this',
    memory_used: 'used in {n} conversations',
    memory_unused: 'not used yet',
    memory_active: 'Active',
    memory_export: 'Export as Markdown',
    memory_empty: 'Nothing remembered yet',
    memory_empty_hint: 'Tell the agent something like "we always invoice on 45-day terms" and it will keep it.',
    memory_deleted: 'Forgotten.',
    memory_failed: 'Could not update that memory.',
    memory_upload: 'Upload .md',
    memory_uploaded: 'Added {n} files.',
    memory_kind_fact: 'fact',
    memory_kind_note: 'note',
    memory_kind_skill: 'how-to',
    memory_kind_fact_hint: 'Short enough that the agent carries it in every conversation.',
    memory_kind_note_hint: 'A document. The agent sees only the description below, and opens it when that looks relevant.',
    memory_kind_skill_hint: 'A procedure. The agent reads it when it is about to do this thing.',
    memory_total_cost: 'All of this adds {tok} tokens to every question — about {cr} credits a turn.',
    memory_carried: 'carried in every conversation · {tok} tokens',
    memory_opened: 'opened {n} times',
    memory_never_opened: 'never opened — has cost {cr} credits',
    memory_cost_hint: 'Adds {tok} tokens to every question. {cr} credits so far.',
    memory_when_used: 'The agent opens this when',
    memory_file: 'The file',
    memory_words: '{n} words — opened only when the description above matches what is being asked',
    billing_credits_left: 'credits left',
    billing_credits_top_up: 'Running low — top up',
    billing_credits_trial: 'left in your trial',
    billing_credits_from_topup: 'left, from your top-up',
    account_avatar_hint: 'Click or drop a PNG, JPEG or WebP. Up to 2 MB.',
    billing_team: 'Team size',
    billing_team_lede: 'Buy seats ahead of filling them — a seat you already paid for costs nothing to fill.',
    billing_seats_using: '{n} in the workspace now',
    billing_seats_included_then: '{n} included, then {price} each',
    billing_seats_ahead: 'Buying {n} seat(s) ahead.',
    billing_seats_fewer: 'Fewer seats',
    billing_seats_more: 'More seats',
    billing_then_from_second: '{pct}% off. From the second month it is {price}.',
    purchases: 'Purchases',
    money: 'Money',
    workspace: 'Workspace',
    doc_accent_from_logo: 'Pick from logo',
    doc_accent_pick_failed: 'Could not read a colour from that logo.',
    per_month_short: ' /mo',
    billing_billed_yearly: '{total} billed yearly',
    billing_billed_yearly_total: '{total} charged once a year',
    billing_delta_month: 'a month',
    billing_switch_to_yearly: 'Switching to yearly billing',
    billing_switch_to_monthly: 'Switching to monthly billing',
    done: 'Done',
    error: 'Something went wrong.',
    billing: 'Billing',
    billing_configure_lede: 'Build your subscription on the left. The bill on the right is exactly what you will be charged.',
    billing_plan: 'Plan',
    billing_plan_lede: 'Everything else is priced against this.',
    billing_popular: 'Most chosen',
    billing_months_free: '{n} months free',
    billing_credits_monthly: '{n} AI credits a month',
    billing_first_month_is: 'first month {price}',
    billing_ai: 'AI credits',
    billing_ai_monthly_lede: 'Your monthly allowance. It resets every period and does not carry over.',
    billing_included: 'Included',
    billing_included_in_plan: 'Comes with the plan',
    billing_this_month: 'Used this period',
    billing_ai_model_note: 'Runs on {model}',
    billing_purchased_left: '{n} bought credits left',
    billing_addons_lede: 'Products you can switch on and off.',
    billing_addon_brings: '{messages} messages a month. AI runs on your plan allowance.',
    billing_addon_needs_plan: 'Available on {plan} and above',
    billing_topup: 'Credit packs',
    billing_topup_lede: 'For the month the allowance runs out before the month does.',
    billing_topup_separate: 'Bought once, never expires, and spent only after the monthly allowance is gone. Not part of the subscription below.',
    billing_approx_exchanges: 'about {n} exchanges',
    billing_percent_off: '{n}% off',
    billing_your_bill: 'Your bill',
    billing_bill_lede_month: 'Charged every month. Change or cancel any time.',
    billing_bill_lede_year: 'Charged once a year. Ten months billed, two free.',
    billing_seats_note: '{used} users, {included} included in the plan',
    billing_total_month: 'Per month',
    billing_total_year: 'Per year',
    billing_first_month: 'Your first month',
    billing_then_renews: '{pct}% off. Renews at {price} after that.',
    billing_checkout: 'Continue to checkout',
    billing_update: 'Update subscription',
    billing_no_changes: 'No changes to apply',
    billing_updated: 'Your subscription was updated.',
    billing_owner_only: 'Only the Owner can change billing.',
    billing_trial_free: 'Free for {days} days. No card yet.',
    billing_trial_running: 'Your trial is free until {date}. A card is only needed to continue after that.',
    billing_renews_plain: 'Renews on {date}',
    billing_confirm_change: 'Confirm the change',
    billing_confirm_change_lede: 'Stripe prorates the difference against what you have already paid for this period.',
    billing_due_now: 'Charged today',
    billing_due_now_unknown: 'Calculated at checkout',
    billing_next_invoice: 'Next invoice',
    billing_confirm: 'Confirm',
    billing_confirming: 'Confirming with Stripe…',
    billing_confirming_lede: 'This takes a second. Your payment went through.',
    billing_thanks: 'Thank you',
    billing_thanks_named: 'Thank you, {name}',
    billing_thanks_lede: 'Your workspace is on {plan}. Here is what was agreed.',
    billing_thanks_pending: 'Your payment is with Stripe. The subscription will appear here within a minute.',
    billing_billing_starts: 'Billing starts',
    billing_topup_done: 'Credits added',
    billing_topup_done_lede: '{n} credits are on your account. They never expire.',
    company_settings: 'Company settings',
    account_settings: 'Your account',
    account_settings_lede:
      'Your name, your password and the companies you belong to. These follow you everywhere — company settings do not.',
    account_profile: 'Profile',
    account_profile_lede: 'How you appear to every team you work with.',
    account_title: 'Title',
    account_email_fixed: 'Your sign-in address. Contact support to change it.',
    account_companies: 'Your companies',
    account_companies_lede: 'One account, as many companies as you need. Your role can differ in each.',
    account_current: 'Open',
    account_open: 'Switch to',
    account_security: 'Security',
    account_security_lede: 'Your password protects every company you belong to.',
    account_current_password: 'Current password',
    account_new_password: 'New password',
    account_change_password: 'Change password',
    account_password_scope:
      'At least 8 characters. Changing it signs you out everywhere else.',
    account_password_changed: 'Password changed.',
    account_password_changed_sessions: 'Password changed. {n} other session(s) signed out.',
    account_google_only:
      'This account signs in with Google, so there is no Railit password to change. Manage it in your Google account.',
    plan_trialing_on: 'Trialing',
    plan_subscribe_to: 'Subscribe',
    billing_leaving_checkout: 'Taking you to secure checkout…',
    billing_leaving_topup: 'Taking you to secure checkout…',
    billing_leaving_portal: 'Opening your billing portal…',
    seat_limit_trial:
      'Your plan includes {included} user(s). Subscribe to add more — every extra user is {price} a month.',
    seat_limit_billing: 'Activate the subscription before adding users.',
    seat_next_costs: 'Next user: {price} / month',
    view_plans: 'View plans',
    billing_stripe_off: 'Billing is not configured on this deployment.',
    billing_action_needed: 'Billing needs attention',
    topup_title: 'Add AI credits',
    topup_subtitle: 'One payment. The credits never expire.',
    topup_credits_word: 'credits',
    topup_addition:
      'These {pack} are added to the {included} your plan already includes, so this month you have {total}.',
    topup_saving: '{percent}% cheaper per credit than the smallest pack.',
    topup_cta: 'Pay {price}',
    topup_leaving: 'Opening checkout…',
    topup_note: 'Card details are entered on Stripe. We never see them.',
    credits_low_badge: 'Credits running low',
    credits_out_badge: 'Out of AI credits',
    credits_out_chat_title: 'You are out of AI credits',
    credits_out_chat_body:
      'Add credits to keep the conversation going. Everything else in the workspace still works.',
    credits_out_chat_cta: 'Add credits',
    /*
      NOT ONE OF THESE MAY NAME A NUMBER OF FREE DAYS OR A PERCENTAGE.

      They used to. The badge read "Activate now: 12 extra days free" and the
      band read "12 more days free … then 55% off your first month", and both
      were painted for every trialing tenant on every page. Those two offers are
      no longer given automatically (see AUTOMATIC_OFFERS in
      backend/src/lib/pricing.ts), so the strings were promising something
      nothing downstream would honour: checkout applies no coupon, the invoice
      arrives at full price, and the customer is right and we are wrong.

      What a code gives IS still stated, in the promo keys further down, but only
      after the server has said what that particular code is worth. A figure that
      lives in a language file cannot be switched off with the offer it describes.
    */
    trial_offer_badge: 'Activate your subscription',
    trial_offer_short: 'Choose a plan',
    trial_last_day: 'Last day of your trial',
    trial_ending_title: 'Your trial ends in {n} days',
    trial_ending_body: 'Choose a plan before it ends and the workspace carries on without a break. Nothing stops working today.',
    trial_ending_cta: 'Choose a plan',
    /*
      THE DISCOUNT CODE BOX, at sign-up and at checkout.

      Stripe's own promotion-code field is switched off deliberately: a code
      typed there is applied by Stripe alone, so it would slip past our
      redemption cap, leave no row in our ledger, and never reach the partner
      whose link brought the company in. This box is the only way in.

      `promo_refused` says one thing for a code that does not exist, one that
      lapsed and one that is used up, because the server deliberately does not
      distinguish them (that would be an oracle for guessing which codes exist)
      and because the difference does not help the person typing.
    */
    promo_have_code: 'Have a code?',
    promo_label: 'Discount code',
    promo_apply: 'Apply',
    promo_checking: 'Checking…',
    promo_refused: 'That code will not work. Check it with whoever gave it to you, and you can carry on without it.',
    promo_remove: 'Remove',
    promo_gives_days: '{days} extra days free',
    promo_gives_percent: '{percent}% off your first month',
    promo_gives_percent_months: '{percent}% off your first {months} months',
    promo_gives_both: '{days} extra days free, and {percent}% off your first month',
    promo_gives_both_months: '{days} extra days free, and {percent}% off your first {months} months',
    hold_title: 'This workspace is on hold',
    hold_body:
      'It has been dormant since the subscription lapsed. Nothing was deleted. Choose a plan to bring it back, plus a one-off {fee} to restore and reopen the company.',
    hold_cta: 'Restore this company',
    hold_note: 'Your books, documents and history are all still here, exactly as they were.',
    hold_warning:
      'If this stays unpaid, the workspace goes on hold on {date}. It is not deleted: reopening it later costs a plan plus a one-off restoration fee.',
    blocked_badge: 'Read-only',
    blocked_payment_title: 'Your workspace has stopped accepting entries',
    blocked_payment_body:
      'The subscription payment did not go through, and the retry window has passed. Updating the card turns writing back on immediately.',
    blocked_payment_cta: 'Update payment method',
    blocked_trial_title: 'Your trial has ended',
    blocked_trial_body:
      'Choose a plan to start creating again. Everything you entered during the trial is still here, exactly as you left it.',
    blocked_trial_cta: 'Choose a plan',
    blocked_readonly_note:
      'Nothing has been deleted. You can still sign in, read every document and export your books.',
    blocked_keep_reading: 'Keep reading my books',
    grace_banner_one: 'Payment failed. Writing stops tomorrow.',
    grace_banner_days: 'Payment failed. {days} days left to update your card.',
    grace_banner_fix: 'Fix it',
    billing_history: 'Invoices',
    billing_credit_balance: 'You are in credit:',
    billing_next_charge: 'Next charge {amount} on {date}.',
    invoice_reason_start: 'subscription started',
    invoice_reason_renewal: 'renewal',
    invoice_reason_change: 'plan change',
    invoice_reason_usage: 'usage',
    invoice_reason_manual: 'one off',
    invoice_status_paid: 'Paid',
    invoice_status_open: 'Unpaid',
    invoice_status_draft: 'Draft',
    invoice_status_void: 'Void',
    invoice_status_uncollectible: 'Written off',
    billing_reason_trial_expired: 'Your trial has ended. Subscribe to keep making changes.',
    billing_reason_payment_overdue: 'A payment failed and the grace period is over.',
    billing_reason_payment_retrying: 'A payment failed — we are retrying it.',
    billing_reason_subscription_inactive: 'This subscription is not active.',
    billing_reason_organization_suspended: 'This organization is suspended.',
    sub_status_trialing: 'Trial',
    sub_status_active: 'Active',
    sub_status_past_due: 'Past due',
    sub_status_unpaid: 'Unpaid',
    sub_status_canceled: 'Cancelled',
    sub_status_incomplete: 'Incomplete',
    sub_status_paused: 'Paused',
    trial_days_left: '{n} days left on trial',
    trial_billing_starts: 'Billing starts {date}',

    // The plan worked out from a trial's real usage — services/planRecommendation.ts.
    // Nobody picked a tier at sign-up, so this is where the question gets
    // answered, and it has to read as arithmetic rather than as an offer.
    rec_title: 'The plan your usage points to',
    rec_lede: 'Worked out from what this workspace actually did — not from a guess on the day you signed up.',
    rec_days_left: '{n} day(s) left',
    rec_measured: 'Measured over {days} day(s)',
    rec_measured_seats: '{n} user(s) in the workspace',
    rec_measured_credits: '{used} AI credits spent — about {projected} a month at that rate',
    rec_measured_credits_zero: 'No AI credits spent',
    rec_answer: '{plan} with {seats} user(s)',
    rec_apply: 'Use {plan}',
    rec_applied: 'Set to {plan}. Check the bill, then continue when you are ready.',
    rec_choose_other:
      'Pick any other plan below and it costs you nothing to change your mind — this is a suggestion, not a gate.',
    rec_reason_used_ai: 'You used the AI, so Starter is out: it has no AI credits at all.',
    rec_reason_too_early:
      'No AI used yet, but {days} day(s) is too short to call that a preference — this keeps it switched on.',
    rec_reason_no_ai: 'No AI credits spent in {days} day(s).',
    rec_starter_tradeoff:
      'Starter drops the assistant, the WhatsApp agent and anything else that spends credits. Your books, your documents and the MCP server all carry on.',
    rec_reason_used_whatsapp: 'WhatsApp is connected, so the add-on is on this quote.',
    rec_reason_credit_burn: 'At about {projected} credits a month, the tier below would not last the month.',
    rec_reason_credits_topped_up:
      'The plan alone gives {included} credits a month, so a monthly credit tier is included here to reach {total}.',
    rec_reason_credits_short:
      'Nothing we sell covers {projected} credits a month. This is the largest allowance at {total} — expect to buy a credit pack in a heavy month.',
    rec_reason_extra_seats: '{n} users: {included} in the plan, {extra} more at {price} each.',
    rec_reason_seats_included: 'All {n} of your users are included in the plan.',
    rec_expired_title: 'Your trial has ended',
    rec_expired_lede:
      'Nothing was deleted. Everything you entered is still here to read and export. New entries and the AI stay paused until you choose a plan.',

    // appearance
    theme: 'Theme',
    theme_light: 'Light',
    theme_dark: 'Dark',
    theme_system: 'System',
    addon_credits: '{n} messages / month',
    addon_usage: '{used} of {quota} messages used this month',
    enable: 'Enable',
    disable: 'Disable',
    on: 'On',
    off: 'Off',

    // organization
    org_products: 'Products',
    org_products_hint: 'Everything is included in the plan. Switch off what you do not need and it disappears from the sidebar.',
    org_members: 'People',
    org_people_moved: 'People and roles are managed in Users.',
    org_people_moved_action: 'Open Users',
    org_seat_pricing_hint: 'Viewers are free. Every other role costs $4 (BHD 1.5) per month.',
    invite: 'Invite',
    invite_sent: 'Invitation sent',
    pending_invitations: 'Pending invitations',
    no_pending_invitations: 'No pending invitations',
    revoke: 'Revoke',
    invite_person: 'Invite someone',
    expires: 'Expires',
    seat: 'Seat',
    billable: 'Paid',
    free: 'Free',

    // whatsapp
    whatsapp_upsell: 'The WhatsApp assistant answers questions about your books, sends payment reminders and pushes a daily report — $11 (BHD 4) per month.',
    whatsapp_enable_addon: 'Enable the add-on',
    whatsapp_number: 'Business number',
    whatsapp_connect_hint: 'Paste the credentials from your Meta WhatsApp Business account.',
    whatsapp_connected: 'WhatsApp connected',
    whatsapp_connected_as: 'Connected as {phone}',
    whatsapp_phone: 'Phone number (E.164)',
    whatsapp_phone_id: 'Phone number ID',
    whatsapp_token: 'Access token',
    whatsapp_contacts: 'Allowed contacts',
    whatsapp_contacts_hint: 'A number can only read company data once it is linked to a Railit user.',
    whatsapp_no_contacts: 'No one has messaged the number yet.',
    whatsapp_daily_report: 'Daily report',
    whatsapp_daily_report_hint: 'A short digest of what was invoiced, collected, spent and what is overdue.',
    not_linked: 'Not linked',
    connect: 'Connect',
    recipients: 'Recipients',
    time: 'Time',
    last_sent: 'Last sent',

    // ===== Billing cards =====
    ai_credits: 'AI credits this month',
    ai_credits_hint: 'Priced from real {model} usage — about {n} assistant exchanges.',
    feat_ai_credits: '{n} AI credits a month',
    billing_synced: 'Subscription synced from Stripe',
    sub_active: 'Subscribed',
    sub_confirmed: 'Your subscription is active',
    sub_trial_then: 'Free until {date}, then billed monthly. Cancel any time.',
    sub_card_on_file: 'Billed monthly. Cancel any time from the billing portal.',
    billing_ends_on: 'Ends on {date}',
    billing_already_paid: 'Already paid? Sync from Stripe',
    set_group_company: 'Company',
    set_group_workspace: 'Workspace',
    set_group_billing: 'Billing',
    set_group_integrations: 'Integrations',
    set_desc_company: 'Legal name, CR, VAT number, address',
    set_desc_tax: 'Default VAT rate, currency, fiscal year',
    set_desc_branding: 'Logo and colours on your documents',
    set_desc_org: 'Company profile and the products you use',
    set_desc_users: 'People, roles, invitations and seats',
    set_desc_activity: 'Every change, who made it and how',
    filters: 'Filters',
    clear_all: 'Clear all',
    show_closed_accounts: 'Show closed',
    hide_closed_accounts: 'Hide closed',
    set_desc_memory: 'What the assistant remembers about this company',
    set_desc_billing: 'Plan, seats, add-ons and AI credits',
    set_desc_mcp: 'Let Claude answer from this company’s books',
    set_desc_whatsapp: 'Your business number and daily reports',
    // ===== Connectors =====
    settings_connectors: 'Connectors',
    set_desc_connectors: 'Bring your books over from another system',
    conn_zoho_blurb: 'Bring customers, suppliers, products, invoices, bills, expenses and payments over from Zoho Books, and optionally keep both sides in step.',
    conn_zoho_invoice_blurb:
      'Bring customers, products, invoices, estimates, credit notes, expenses and payments over from Zoho Invoice. Recurring invoices and price lists do not come across: your history is safe, but scheduled billing stops on the day you move.',
    conn_what_happens:
      'You choose what Railit may do before you are sent to Zoho, and you choose which set of books to bring over before anything is imported. Read-only is the default; two-way sync is the option that ever writes anything back.',
    conn_not_configured: 'This server has no Zoho credentials configured yet, so connecting is switched off.',
    conn_connect: 'Connect Zoho Books',
    conn_disconnect: 'Disconnect',
    conn_disconnect_confirm:
      'Disconnect Zoho Books? Access is revoked at Zoho straight away. What has already been imported stays, and so does the record of it, so reconnecting later will not import anything twice.',
    conn_disconnected: 'Zoho Books disconnected',
    conn_needs_admin: 'Only an owner or an admin can connect an account or start an import.',
    conn_authorised: 'Zoho authorised. Choose which organization to import from.',
    conn_failed: 'The connection could not be completed',
    conn_organization: 'Organization',
    conn_not_chosen: 'Not chosen yet',
    conn_data_centre: 'API host',
    conn_connected_at: 'Connected',
    conn_last_run: 'Last import',
    conn_mode_title: 'What should Railit be allowed to do?',
    conn_mode_migration: 'Move my books across once',
    conn_mode_migration_d:
      'A one-time migration. Railit reads your Zoho books, brings them here, tells you what arrived and stops. It can never change anything in Zoho.',
    conn_mode_read_only: 'Keep reading Zoho',
    conn_mode_read_only_d:
      'Zoho stays your system of record and Railit keeps a copy you can refresh whenever you want the latest across. Railit can read your books and will never change them.',
    conn_mode_read_write: 'Keep both sides in step',
    conn_mode_read_write_d:
      'Two-way sync. Railit reads what changed in Zoho, then sends back what changed here: customers, suppliers and products both ways, and documents you raised in Railit. A document that came from Zoho is never edited, and if both sides change the same record nothing is written until you decide which is right.',
    conn_mode_unavailable: 'not available yet',
    conn_mode_locked:
      'What this connection may do was fixed when you approved it at Zoho, so it cannot be switched here. To change it, disconnect and connect again and choose the new option.',
    // ===== The fast path =====
    conn_updates: 'Updates',
    conn_webhook_live: 'Zoho tells us as it happens, and we check every six hours anyway',
    conn_webhook_off: 'Checked every six hours',
    conn_webhook_failed: 'Checked every six hours. Zoho would not set up instant updates',
    conn_webhook_needs_consent: 'Checked every six hours',
    conn_webhook_needs_consent_d:
      'Instant updates need a permission that did not exist when this connection was made. Connect again if you want them; nothing is lost either way, because the six-hourly check is what keeps the two sides in step.',
    conn_pushed: 'Sent to Zoho',
    conn_conflicts_raised: 'Differences found',
    // ===== Conflicts =====
    conn_conflicts: 'Waiting for you',
    conn_conflicts_why:
      'These records changed on both sides, or were changed here after arriving from Zoho. Nothing has been written to either system and this record has stopped syncing until you say which version is right.',
    conn_conflict_fields: 'Different in:',
    conn_conflict_settled: 'Settled',
    conn_conflict_no_send:
      'This document came from Zoho, so Railit will not change their copy whatever you choose. Keeping the Railit version means the two copies stay different from now on.',
    conn_value_empty: '(empty)',
    conn_side_ours: 'In Railit',
    conn_side_theirs: 'In Zoho',
    conn_keep_ours: 'Keep the Railit version and send it',
    conn_keep_ours_local: 'Keep the Railit version here',
    conn_keep_theirs: "Restore Zoho's version",
    conn_entity_customer: 'Customer',
    conn_entity_vendor: 'Supplier',
    conn_entity_item: 'Product',
    conn_entity_invoice: 'Invoice',
    conn_entity_bill: 'Bill',
    conn_entity_expense: 'Expense',
    conn_entity_payment: 'Payment',
    conn_field_name: 'Name',
    conn_field_email: 'Email',
    conn_field_phone: 'Phone',
    conn_field_vat_number: 'VAT number',
    conn_field_address: 'Address',
    conn_field_active: 'Active',
    conn_field_sku: 'SKU',
    conn_field_description: 'Description',
    conn_field_kind: 'Type',
    conn_field_unit: 'Unit',
    conn_field_sale_price: 'Sale price',
    conn_field_purchase_price: 'Purchase price',
    conn_field_number: 'Number',
    conn_field_issue_date: 'Date',
    conn_field_due_date: 'Due',
    conn_field_currency: 'Currency',
    conn_field_subtotal: 'Subtotal',
    conn_field_discount: 'Discount',
    conn_field_vat_amount: 'VAT',
    conn_field_total: 'Total',
    conn_field_state: 'Status',
    conn_field_notes: 'Notes',
    conn_field_reference: 'Reference',
    conn_field_date: 'Date',
    conn_field_amount: 'Amount',
    conn_field_category: 'Category',
    conn_field_method: 'Method',
    conn_field_applied_to: 'Applied to',
    conn_mode_label: 'This connection may',
    conn_mode_change_note:
      'This decides exactly what Zoho is asked to allow, so it is chosen now rather than later. Changing it means connecting again.',
    conn_refresh_from_zoho: 'Refresh from Zoho',
    conn_status_migrated: 'Migrated',
    conn_migrated_done: 'Your books were migrated on',
    conn_migrated_safe_to_remove:
      'Nothing more will be read from Zoho. You can remove this connection now: everything that arrived stays here, and Railit remembers what it brought over so reconnecting later would not import anything twice.',
    conn_status_pending_org: 'Choose a company',
    conn_status_connected: 'Connected',
    conn_status_error: 'Needs attention',
    conn_status_disconnected: 'Disconnected',
    conn_pick_org: 'Which set of books?',
    conn_pick_org_why: 'One Zoho login can own several organizations. Pick the one this workspace should import.',
    conn_org_default: 'Default',
    conn_use_org: 'Import from this one',
    conn_org_chosen: 'Organization chosen. You can start the import now.',
    conn_import: 'Start import',
    conn_importing: 'Importing…',
    conn_import_queued: 'Import queued. It carries on in the background, so you can close this page.',
    conn_progress: 'Import',
    conn_progress_live: 'What has arrived so far.',
    conn_progress_last: 'What the last import brought over.',
    conn_run_queued: 'Queued',
    conn_run_running: 'Running',
    conn_run_paused: 'Waiting for quota',
    conn_run_done: 'Finished',
    conn_run_failed: 'Failed',
    conn_stage_accounts: 'Accounts',
    conn_stage_contacts: 'Contacts',
    conn_stage_items: 'Products',
    conn_stage_invoices: 'Invoices',
    conn_stage_bills: 'Bills',
    conn_stage_expenses: 'Expenses',
    conn_stage_payments: 'Payments',
    conn_stage_estimates: 'Quotations',
    conn_stage_credit_notes: 'Credit notes',
    conn_stage_journals: 'Journal entries',
    /* ── The import bar. ────────────────────────────────────────────────────
       It counts STAGES, never records and never a percent, because Zoho does
       not say how many invoices exist until the last page has been read. Every
       string below is written so that it stays true of a run that pauses for a
       day and comes back. See pages/settings/connectors/ImportProgress.tsx. */
    conn_progress_label: 'Import progress',
    conn_now_reading: 'Now reading',
    conn_now_sending: 'Sending your changes to Zoho',
    conn_waiting_at: 'Waiting at',
    conn_stopped_at: 'Stopped at',
    conn_all_stages_done: 'Every stage finished.',
    conn_so_far: '{n} so far',
    conn_step_of: 'Step {n} of {total}',
    conn_queued_nothing: 'Queued. Nothing has come across yet.',
    conn_queued_hint: 'It starts by itself, usually within a minute, and carries on in the background.',
    conn_why_no_total:
      'Zoho does not say how many records there are until they have all been read, so this bar counts stages, not records.',
    conn_stage_not_reached: 'Not reached yet',
    /* THREE FACTS, NOT TWO. A tile shows a dash for "not reached yet", a number
       for "walked, and this is what was there", and this for "your Zoho product
       has no such record". The third one used to be shown as the second, and a
       zero beside Bills is a sentence about somebody's company that was not
       true. */
    conn_stage_absent: 'Not in this product',
    conn_stages_absent: '{product} does not have these, so nothing was looked for:',
    conn_stage_absent_hint: '{product} has no such records, so this stage never runs.',
    conn_zoho_books: 'Zoho Books',
    conn_zoho_invoice: 'Zoho Invoice',
    conn_product_title: 'Which Zoho product are you on?',
    conn_product_zoho_books_d:
      'Full accounting. You log in at books.zoho.com and your menu has a Banking or Accountant section.',
    conn_product_zoho_invoice_d:
      'Billing and expenses. You log in at invoice.zoho.com and there is no Banking or Accountant section.',
    conn_product_hint:
      'This decides which permissions Zoho asks you to approve, so it cannot be changed afterwards without connecting again.',
    conn_product_absent: '{product} has no {stages}, so those stages will not run.',
    conn_already_there: 'Already here',
    conn_api_calls: 'Zoho API calls used',
    conn_started: 'Started',
    conn_finished: 'Finished',
    conn_paused_quota:
      'Zoho limits how many requests your plan allows in a day, and this import has used them. It carries on by itself from where it stopped, after',
    /* Two notes, because there are now two behaviours and one sentence for both
       would be wrong for one of them. See the migration branch of
       services/zohoLedger.ts. */
    conn_ledger_note_migration:
      'Your books are being moved, so imported documents bring their journal entries with them and the reports here will read like the ones you are leaving. This happens only while the ledger is empty: a workspace that has been trading here keeps whatever your accountant has already reported.',
    conn_no_ledger_note:
      'This connection mirrors Zoho and does not write to your ledger. Documents arrive, journal entries do not, so your opening balances stay a decision you make once with your accountant.',
    // ===== Multi-company =====
    addon_mcp: 'AI & MCP',
    addon_mcp_d: 'Connect Claude or ChatGPT to this workspace.',
    your_companies: 'Your companies',
    new_company: 'New company',
    create_company: 'Create company',
    new_company_hint: 'A separate workspace with its own data, team and subscription. You will be its Owner.',
    new_company_trial: 'Starts on a 14-day free trial. No card needed.',
    org_created: '{name} is ready',
    country: 'Country',
    country_bh: 'Bahrain',
    country_sa: 'Saudi Arabia',
    country_ae: 'United Arab Emirates',
    country_kw: 'Kuwait',
    country_qa: 'Qatar',
    country_om: 'Oman',
    seats_line_note: '{n} paid user(s) · {price} each past the first',
    module_always_on: 'Always on — the rest is built on it',
    current_plan: 'Current',
    addon_email: 'Email',
    plan_sub: 'Everything Railit does, for the whole company.',
    plan_includes_seats: 'Includes {n} user',
    per_user_month: '/ user / month',
    seats_sub: 'What each person past the first costs.',
    seats_in_use: '{n} paid user(s) right now',
    seats_included: 'Included in the plan',
    seats_extra: 'Extra users',
    seats_cost: 'Added to the bill',
    manage_people: 'Manage people',
    owner_only: 'Only the Owner can change billing.',
    included_in_plan: 'Included in the plan',
    trial_no_card: 'no card on file yet',
    email_unlimited: 'No sending quota',
    addon_used: 'Used this month',
    addon_remaining: 'Remaining',
    addon_sub_whatsapp: 'Your own WhatsApp number, answering from the ledger.',
    addon_sub_email: 'Invoices, quotes, reminders and reports, sent from Railit.',
    feat_all_products: 'ERP, Finance, CRM, HR, Inventory, POS, AI, Forms',
    feat_viewers_free: 'Viewers are free — read-only costs nothing',
    feat_mcp: 'MCP server for Claude and ChatGPT',
    feat_email: 'Unlimited email sending',
    feat_seat_prorated: 'Added and removed mid-month, prorated',
    feat_wa_digest: 'Daily or weekly digest at a time you pick',
    feat_wa_reminders: 'Payment reminders that follow the invoice',
    feat_wa_arabic: 'Answers in Arabic or English',
    feat_email_docs: 'Attached to the document it belongs to',
    feat_email_log: 'Every message logged and retryable',

    /* ───────────────────────────────────────────────────────────────────────
       Phase 2B · People & partners — customers, vendors, employees, payroll,
       subscriptions, contracts. Append-only block; keep en/ar in sync.
       ─────────────────────────────────────────────────────────────────────── */
    customers_subtitle: 'The companies and people you invoice.',
    vendors_subtitle: 'The suppliers you buy from and pay.',
    employees_subtitle: 'Your team, their pay and their contracts.',
    /* ───────────────────────────────────────────────────────────────────────
       HR · Leave. Append-only block; keep en/ar in sync.
       ─────────────────────────────────────────────────────────────────────── */
    leave: 'Leave',
    my_leave: 'My leave',
    leave_subtitle: 'Requests waiting on you, and everything already agreed.',
    leave_balances: 'Balances',
    leave_balances_subtitle: 'What everybody is owed, and the terms behind it.',
    my_leave_subtitle: 'What you have left, and how to ask for time off.',
    leave_tab_requests: 'Requests',
    leave_tab_balances: 'Balances',
    leave_tab_week: 'This week',
    leave_new_request: 'Book time off',
    leave_submit_request: 'Save',
    my_leave_request: 'Request time off',
    leave_kind: 'Kind of leave',
    leave_from: 'First day',
    leave_to: 'Last day',
    leave_half_day: 'Half a day',
    leave_which_half: 'Which half',
    leave_morning: 'Morning',
    leave_afternoon: 'Afternoon',
    leave_reason: 'Reason',
    leave_reason_required: 'This kind of leave needs a note saying why.',
    leave_reason_required_hint: 'Say briefly why, this kind of leave asks for it.',
    leave_days_count: 'day(s)',
    leave_days_left: 'days left',
    leave_would_leave: 'leaves',
    leave_not_enough_days: 'more than the balance',
    leave_range_inverted: 'The last day cannot be before the first.',
    leave_approve_now: 'Already agreed',
    leave_approve_now_hint: 'Records it as approved. Untick to send it for somebody else to answer.',
    leave_pick_employee: 'Choose somebody',
    leave_everyone: 'Everybody',
    leave_awaiting_you: 'waiting on you',
    leave_awaiting: 'awaiting approval',
    leave_approve: 'Approve',
    leave_decline: 'Decline',
    leave_cancel_request: 'Cancel',
    leave_withdraw: 'Withdraw',
    leave_withdraw_title: 'Withdraw this request?',
    leave_decline_title: 'Decline this request?',
    leave_cancel_title: 'Cancel this leave?',
    leave_nothing_waiting: 'Nothing waiting for you',
    leave_nothing_waiting_hint: 'Every request has been answered. Switch the filter to see the rest.',
    leave_show_all: 'Show everything',
    leave_status_pending: 'Waiting',
    leave_status_approved: 'Approved',
    leave_status_rejected: 'Declined',
    leave_status_cancelled: 'Cancelled',
    leave_bucket_annual: 'Annual',
    leave_bucket_sick: 'Sick',
    leave_bucket_unpaid: 'Unpaid',
    leave_bucket_other: 'Other',
    leave_other_tooltip: 'Counted and shown, and deducted from no allowance.',
    leave_unlimited: 'Unlimited',
    leave_custom: 'Custom',
    leave_carried_until: 'carried over, until',
    leave_year: 'Leave year',
    leave_your_year: 'Your leave year runs',
    leave_to_date: 'to',
    leave_left: 'left',
    leave_adjust: 'Terms',
    leave_terms_for: 'Leave terms for',
    leave_policy: 'Policy',
    leave_policy_default: 'The default policy',
    leave_just_for_them: 'Just for this person',
    leave_override_hint: 'Leave a field empty and it follows the policy.',
    leave_inherit: 'Same as the policy',
    leave_year_turns_over: 'The leave year turns over',
    leave_reset_anniversary: 'On their hire date',
    leave_reset_calendar: 'On 1 January',
    leave_reset_manual: 'By hand',
    leave_current_year: 'This leave year',
    leave_reset_now: 'Roll forward to today',
    leave_undo_reset: 'Undo',
    leave_reset_only_manual: 'The leave year turns over on its own, so there is nothing to roll forward.',
    leave_save_first: 'Save your changes first.',
    leave_last_reset: 'Last rolled forward',
    leave_undo_window: 'can be undone for 24 hours',
    leave_this_week: 'This week',
    leave_this_week_short: 'This week',
    leave_this_week_subtitle: 'Who is in, and who is away.',
    leave_previous_week: 'Previous week',
    leave_next_week: 'Next week',
    leave_cell_leave: 'Leave',
    leave_cell_half_leave: '½ leave',
    leave_cell_present: 'In',
    leave_cell_absent: 'Absent',
    leave_cell_half_day: '½ day',
    leave_cell_remote: 'Remote',
    leave_cell_off: 'Off',
    my_leave_history: 'Your requests',
    my_leave_none: 'You have not asked for any time off yet',
    my_leave_none_hint: 'Book a day and it will show up here with its answer.',
    my_leave_no_record: 'You are not on the payroll here',
    my_leave_no_record_hint: 'Your login is not linked to an employee record, so there is no leave to show. Ask whoever runs HR to link it.',
    settings_leave: 'Leave',
    set_desc_leave: 'Kinds of leave, entitlements and who gets what',
    leave_types: 'Kinds of leave',
    leave_types_hint: 'Rename any of these freely. Which allowance a kind comes out of is fixed once it has been used.',
    leave_type_unpaid: 'Unpaid',
    leave_type_needs_note: 'Needs a note',
    leave_switch_off: 'Switch off',
    leave_switch_on: 'Switch on',
    leave_policies: 'Policies',
    leave_policies_hint: 'A set of rules several people share. One person\u2019s own terms sit on top, on the Balances screen.',
    leave_new_policy: 'New policy',
    leave_edit_policy: 'Edit policy',
    leave_policy_is_default: 'Default',
    leave_on_this_policy: 'on this policy',
    leave_days_a_year: 'days a year',
    leave_days_a_month: 'days a month',
    leave_carries: 'carries over',
    leave_annual_mode: 'Annual leave',
    leave_mode_fixed: 'A fixed number each year',
    leave_mode_accrual: 'Earned month by month',
    leave_annual_days: 'Days a year',
    leave_days_per_month: 'Days a month',
    leave_carryover_cap: 'Carry over up to',
    leave_carryover_expiry: 'Carried days expire after (months)',
    leave_carryover_expiry_hint: 'Zero means they never expire.',
    leave_sick_mode: 'Sick leave',
    leave_sick_days: 'Sick days a year',
    leave_prorate: 'Pro-rate the first year',
    leave_prorate_hint: 'Somebody who starts in July gets half a year\u2019s days, not a whole year\u2019s.',
    leave_assign: 'Assign',
    leave_assign_to: 'Put people on',
    payroll_subtitle: 'Monthly runs, payslips and payouts.',
    subscriptions_subtitle: 'Recurring services and what they cost you.',
    contracts_subtitle: 'Agreements, signatures and renewal dates.',
    all_kinds: 'All kinds',
    all_years: 'All years',
    clear_filters: 'Clear filters',
    no_results: 'Nothing matches those filters',
    no_results_hint: 'Try a different search, or clear the filters.',
    no_customers_yet: 'No customers yet',
    no_customers_hint: 'Add the first company you invoice and it will show up here.',
    no_vendors_yet: 'No vendors yet',
    no_vendors_hint: 'Add a supplier to start recording bills and expenses against it.',
    no_employees_yet: 'No employees yet',
    no_employees_hint: 'Add your first team member to run payroll and issue contracts.',
    no_payroll_runs_yet: 'No payroll runs yet',
    no_payroll_runs_hint: 'Create a run to generate payslips for the period you choose.',
    no_subscriptions_yet: 'No subscriptions yet',
    no_subscriptions_hint: 'Track a recurring service to see its cost and next charge date.',
    no_contracts_yet: 'No contracts yet',
    no_contracts_hint: 'Draft a contract, sign it electronically and keep the PDF here.',
    basic_info: 'Basics',
    contact_details: 'Contact',
    tax_registration: 'Tax & registration',
    job_details: 'Job',
    compensation: 'Compensation',
    billing_schedule: 'Billing schedule',
    posting_accounts: 'Posting',
    commercial_terms: 'Value & term',
    contract_body: 'Contract body',
    esign_title: 'Electronic signatures',
    esign_subtitle: 'Both parties sign here; the PDF carries the signatures.',
    template_hint: 'Start from a saved template, then edit the text below.',
    /* `posted` is already taken by the toast copy ("Posted."); a run's *state*
       needs a bare noun for chips and the lifecycle strip. */
    run_status_posted: 'Posted',
    run_stage_draft: 'Amounts can still be edited.',
    run_stage_posted: 'Posted to the ledger, waiting for payout.',
    run_stage_paid: 'Paid out from the bank account.',
    pending_edits_hint: 'Finish editing deductions before posting.',
    employee_count: 'Employees',
    back: 'Back',

    // ===== Phase 2A · document pages (invoices, quotations, bills, expenses, payments) =====
    line_items: 'Line items',
    more_actions: 'More actions',
    filter_search: 'Search',
    invoices_sub: 'What you have billed, and what is still owed to you.',
    credit_notes: 'Credit notes',
    credit_notes_sub: 'What you have given back, and what a customer still holds unused.',
    credit_note: 'Credit note',
    credit_note_details: 'Credit note details',
    credit_note_summary: 'Summary',
    new_credit_note: 'New credit note',
    edit_credit_note: 'Edit credit note',
    credit_reason: 'Reason',
    credit_reason_hint: 'Printed on the document. A VAT credit note has to say why.',
    credit_unused: 'Unused credit',
    credit_applied: 'Applied',
    credit_applied_to: 'Applied to',
    credit_applied_none: 'Not applied to any invoice yet. The customer holds it as credit.',
    credit_applied_ok: 'Credit applied.',
    credit_taken_back: 'Credit taken back off that invoice.',
    take_credit_back: 'Take the credit back off',
    total_credited: 'Total credited',
    apply_to_invoice: 'Apply to an invoice',
    select_invoice: 'Select an invoice',
    issue_: 'Issue',
    credit_note_issued: 'Credit note issued.',
    status_closed: 'Closed',
    confirm_delete_credit_note: 'Delete this draft credit note? It has posted nothing, so nothing is reversed.',
    confirm_void_credit_note: 'Void this credit note? Its journal is reversed and every invoice it was applied to goes back to owing that amount.',
    credit_exceeds_invoice_balance: 'That is more than the invoice still owes.',
    credit_exceeds_remaining: 'That is more than is left on this credit note.',
    credit_customer_mismatch: 'A credit note can only be applied to the same customer’s invoices.',
    credit_currency_mismatch: 'The credit note and the invoice are in different currencies.',
    invoice_not_creditable: 'That invoice cannot take a credit in its current state.',
    invoice_has_credit: 'A credit note has been applied to this invoice. Take the credit off first.',
    credit_note_not_applicable: 'This credit note has nothing left to apply.',
    quotations_sub: 'Offers out with customers, and what became of them.',
    bills_sub: 'What suppliers have billed you, and what is still due.',
    expenses_sub: 'Out-of-pocket and card spend, from claim to posting.',
    payments_sub: 'Every payment in and out, and the document it settled.',
    direction: 'Direction',
    inbound: 'Inbound',
    outbound: 'Outbound',
    all_directions: 'All directions',
    inbound_payment: 'Inbound payment',
    outbound_payment: 'Outbound payment',
    linked_to: 'Linked to',
    no_linked_doc: 'No linked document',
    reference: 'Reference',
    payslip: 'Payslip',
    created_at: 'Created',
    no_payments_sub: 'Pay an invoice, a bill or a payroll run and it will show up here.',
    total_inbound: 'Total in',
    total_outbound: 'Total out',
    net_flow: 'Net',
    /* Correcting a receipt. The word is CORRECT and never DELETE, in both
       languages: a payment is posted by construction, keeps its number and
       stays in the list whatever is done to it. See CorrectionDialog. */
    correct_payment: 'Correct this payment',
    correct_payment_lede:
      'Nothing is deleted. The receipt keeps its number and its place in the sequence, and says what happened to it. Choose what actually happened.',
    correction_reverse: 'It was never real',
    correction_reverse_help:
      'A duplicate, the wrong invoice, or a transfer that never arrived. The entry is reversed in its own period and the document goes back to what it said before. No money moves.',
    correction_refund: 'We sent the money back',
    correction_refund_help:
      'Money leaves the bank and reaches the customer, so it appears on the bank statement. Use this only if it really was paid back.',
    correction_void: 'Void the document',
    correction_void_help:
      'The receipt is already off it, and the document itself should not exist. It keeps its number; its journal entry is reversed.',
    correction_reason: 'Why',
    correction_reason_ph: 'Recorded twice: the same transfer was entered on Sunday and again on Monday.',
    correction_reason_short:
      'At least {n} characters. It is stored on the receipt and read by whoever checks the books.',
    correction_confirm: 'I have checked this, and I understand it is recorded in the books.',
    correction_submit: 'Prepare this correction',
    correction_none: 'Nothing can be corrected from here',
    correction_staged: 'Prepared. Nothing has changed yet: read it below and apply it.',
    correction_pending: 'Correction waiting to be applied',
    correction_pending_sub: 'Nothing has been written yet. Read it, then apply it or discard it.',
    correction_apply: 'Apply the correction',
    correction_discard: 'Discard',
    correction_applied: 'Applied.',
    correction_discarded: 'Discarded. Nothing was changed.',
    correction_expires: 'Expires {time}',
    correction_not_yours: 'Only the person who asked for this can apply it.',
    /* ───────────────────────────────────────────────────────────────────────
       Phase 2C · reports, accounting & the shared widgets. Append-only block;
       keep en/ar in sync.
       ─────────────────────────────────────────────────────────────────────── */
    stmt_total_revenue: 'Total revenue',
    stmt_total_expenses: 'Total expenses',
    stmt_total_assets: 'Total assets',
    stmt_total_liabilities: 'Total liabilities',
    stmt_total_equity: 'Total equity',
    stmt_liabilities_and_equity: 'Liabilities and equity',
    stmt_net_margin: 'net margin',
    stmt_net_movement: 'Net movement',
    stmt_no_revenue: 'No revenue in this period',
    stmt_no_expenses: 'No expenses in this period',
    stmt_no_movement: 'No movement in this period',
    stmt_no_balances: 'No accounts with a balance',
    stmt_invoice_count: 'invoices',
    aging_by_bucket: 'Outstanding by age',
    aging_total_outstanding: 'Total outstanding',
    aging_nothing_outstanding: 'Nothing outstanding',
    aging_all_settled: 'Every invoice due on or before this date has been settled.',
    days_overdue: 'days overdue',
    coa_no_accounts: 'No accounts of this type yet',
    coa_expand: 'Expand',
    coa_collapse: 'Collapse',
    journal_sub: 'Every posted entry, and what created it.',
    bank_accounts_sub: 'Where the cash sits, and what each account holds.',
    total_in_base: 'Total, BHD accounts',
    /* Named rather than merely counted: "and 2 other currencies" makes a reader
       go looking, and the figure they are looking for is right here. */
    total_excludes: 'Not included, no rate is applied here:',
    upload: 'Upload',
    preview: 'Preview',
    preview_unavailable: 'Preview is not available for this file type.',
    close: 'Close',
    aws_live_cost: 'AWS month-to-date',
    aws_not_configured: 'AWS billing keys not configured',
    edit_signature: 'Edit signature',

    /* ===== APPENDED — dashboard bands + post-checkout confirmation ===== */
    /* Band headings on the dashboard home. */
    outstanding: 'Outstanding',
    performance: 'Performance',
    activity: 'Activity',
    /* Coming back from Stripe Checkout. Nothing here is hardcoded: the plan,
       the price, the interval and the dates all come off the live payload. */
    checkout_confirming: 'Confirming your subscription…',
    checkout_confirming_sub: 'Stripe is sending the details across. It only takes a moment.',
    checkout_done_title: "You're on {plan}",
    checkout_trial_line: 'Free until {date} — then {price} per {interval}.',
    checkout_renew_line: '{price} per {interval}. Next charge {date}.',
    checkout_charged_line: '{price} per {interval}, starting today.',
    checkout_first_month: 'Your first month is {first}, then {price} per month.',
    checkout_included: '{seats} users included · {credits} AI credits a month',
    checkout_cancel_note:
      'Cancel any time from this page — you keep everything until the end of the period you have paid for.',
    checkout_ack: 'Got it',
    checkout_pending_title: 'Payment received',
    checkout_pending_body:
      'Stripe has your payment. Your plan will switch over within a minute — reload this page if it has not.',
    interval_month: 'month',
    interval_year: 'year',

    /* ===== APPENDED — document design (Settings → Branding) =====
       Everything a tenant can change about how its PDFs look. Append-only
       block; keep it byte-for-byte parallel with the Arabic one below. */
    doc_design: 'Document design',
    doc_design_sub: 'How the PDFs your customers receive look. Changes apply to every document from now on.',
    doc_brand: 'Brand',
    doc_logo: 'Logo',
    doc_logo_hint: 'PNG, JPEG, WebP or SVG. Up to 2 MB. Printed at the height you choose — never stretched.',
    doc_logo_upload: 'Upload logo',
    doc_logo_replace: 'Replace',
    doc_logo_remove: 'Remove',
    doc_logo_none: 'No logo yet — documents use your company name as a wordmark.',
    doc_logo_size: 'Printed height',
    doc_logo_uploaded: 'Logo updated',
    doc_logo_removed: 'Logo removed',
    doc_accent: 'Accent colour',
    doc_accent_hint: 'Used for the document number, totals and rules.',
    doc_accent_inherit: 'Use workspace brand colour',
    doc_header_style: 'Header treatment',
    doc_header_rule: 'Accent rule',
    doc_header_fill: 'Filled band',
    doc_header_fill_hint: 'A filled header needs a logo that reads on colour.',
    doc_layout: 'Layout',
    doc_layout_classic: 'Classic',
    doc_layout_classic_desc: 'Hairline rules, bordered panels, dark footer.',
    doc_layout_modern: 'Modern',
    doc_layout_modern_desc: 'The accent carries the page. Soft panels, no borders.',
    doc_layout_minimal: 'Minimal',
    doc_layout_minimal_desc: 'No fills. Hairlines, small type, plenty of air.',
    doc_page: 'Page',
    doc_language: 'Document language',
    doc_language_en: 'English',
    doc_language_ar: 'Arabic',
    doc_language_both: 'Both, side by side',
    doc_language_hint: 'Bilingual documents keep every figure left-to-right, so the money columns never move.',
    doc_paper: 'Paper size',
    doc_paper_a4: 'A4',
    doc_paper_letter: 'Letter',
    doc_margin: 'Margins',
    doc_margin_hint: 'Millimetres of white space around the page.',
    doc_contents: 'What appears on the document',
    doc_show_cr: 'CR number',
    doc_show_vat: 'VAT number',
    doc_show_address: 'Address',
    doc_show_phone: 'Phone',
    doc_show_email: 'Email',
    doc_show_website: 'Website',
    doc_website: 'Website address',
    doc_show_iban: 'Bank / IBAN block',
    doc_show_payment_terms: 'Terms & notes block',
    doc_show_signature: 'Signature line',
    doc_show_watermark: 'DRAFT / PAID watermark',
    doc_show_watermark_hint: 'Stamps unfinalized and settled documents diagonally across every page.',
    doc_text: 'Wording',
    doc_terms_en: 'Terms (English)',
    doc_terms_ar: 'Terms (Arabic)',
    doc_terms_hint: 'Printed in the sign-off block. Leave empty for the default wording.',
    doc_footer_note_en: 'Footer note (English)',
    doc_footer_note_ar: 'Footer note (Arabic)',
    doc_footer_note_hint: 'One short line, repeated at the bottom of every page.',
    doc_preview: 'Live preview',
    doc_preview_sub: 'The real template, with sample data.',
    doc_preview_doc: 'Preview document',
    doc_preview_failed: 'Could not render the preview.',
    doc_preview_retry: 'Try again',
    doc_preview_open_pdf: 'Open as PDF',
    doc_reset: 'Reset to defaults',
    doc_reset_confirm: 'Reset the document design?',
    doc_reset_body: 'Every setting on this page goes back to the Railit defaults, and the uploaded logo is deleted. Documents already sent are unaffected.',
    doc_reset_done: 'Document design reset',
    doc_unsaved: 'Unsaved changes',
    doc_invoice: 'Invoice',
    doc_quote: 'Quotation',
    doc_bill: 'Vendor bill',
    doc_payslip: 'Payslip',

    /* ===== APPENDED — billing rebuild: tiers, add-ons, AI credits ===== */
    plans_choose: 'Choose your plan',
    plans_sub: 'Change tier whenever you like — Stripe prorates the difference.',
    plan_current: 'Current plan',
    plan_switch_to: 'Switch to this',
    plan_upgrade_to: 'Upgrade',
    plan_start_trial: 'Start free trial',
    interval_monthly: 'Monthly',
    interval_yearly: 'Yearly',
    interval_yearly_note: '2 months free',
    plan_billed_yearly: '{price} billed once a year',
    plan_seats_included: '{n} users included',
    plan_seat_extra: 'then {price} per extra user',
    plan_ai_credits_month: '{n} AI credits a month',
    plan_whatsapp_included: 'WhatsApp included',
    plan_no_ai: 'No AI credits — MCP only',
    plan_feat_mcp: 'MCP server for Claude and ChatGPT',
    plan_feat_whatsapp: 'WhatsApp assistant can be added',
    plan_feat_priority: 'Priority support',
    plan_feat_api: 'API tokens for your own scripts',
    offer_headline: '{percent}% off your first month',
    offer_sub: 'Month one is {price} instead of {full}. Card taken at checkout; it renews at {full} after that.',
    offer_trial_first: 'Or try it free for {days} days first — no card, nothing charged.',
    offer_first_month: 'First month {price}',
    offer_badge: 'Save {percent}%',
    addons_title: 'Add-ons',
    addons_sub: 'Extras you can switch on and off at any time. Billed with the subscription.',
    addon_brings: 'Brings {messages} messages and {credits} AI credits a month.',
    addon_locked_on_plan: 'Not sold on {plan}',
    addon_upgrade_to_add: 'Available from {plan} up',
    addon_on: 'Active',
    credits_title: 'AI credits',
    credits_allowance: 'Monthly allowance',
    credits_left: '{left} left of {quota}',
    credits_spent_this_period: '{n} spent this month',
    credits_bought_balance: '{n} bought — never expires',
    credits_exchanges: '≈ {n} assistant exchanges',
    credits_burn: 'At this month\u2019s rate that is about {n} a day.',
    credits_days_cover: 'Enough for roughly {n} more days.',
    credits_runs_out: 'On this month\u2019s rate the allowance runs out before the month does.',
    credits_tier_title: 'Raise the monthly allowance',
    credits_tier_sub: 'A recurring add-on: renews with the subscription and resets every month.',
    credits_tier_included: 'Included with {plan}',
    credits_tier_total: '{n} a month',
    credits_tier_choose: 'Choose',
    credits_tier_chosen: 'Selected',
    credits_topup: 'Buy a one-off top-up',
    credits_topup_sub: 'Paid once. Lands immediately, never expires, and is only spent after the monthly allowance is gone.',
    credits_pack_credits: '{n} credits',
    credits_buy: 'Buy',
    credits_no_ai_plan: 'This plan has no AI budget — the assistant, WhatsApp and the agents are off.',
    credits_no_ai_cta: 'Upgrade to {plan}',
    topup_done_title: 'Credits added',
    topup_done_body: '{n} AI credits are on your balance. They never expire, and they are spent only once the monthly allowance is gone.',
    topup_pending_body: 'Stripe has your payment. The credits land within a minute — reload this page if they have not.',
    plan_change_title: 'Switch to {plan}',
    plan_change_new_price: 'New price',
    plan_change_due_now: 'Due today',
    plan_change_prorated: 'Stripe bills the difference for the rest of this period, pro rata.',
    plan_change_from_next: 'From the next invoice you pay {price} per {interval}.',
    plan_change_drops_addon: '{addon} is not sold on this plan and will be switched off.',
    plan_change_confirm: 'Confirm switch',
    plan_change_done: 'You are now on {plan}',
    billing_cancels_on: 'Cancels on {date}',
    billing_what_you_pay: 'What you pay',

    // ===== inventory: navigation and shells =====
    inventory: 'Inventory',
    products: 'Products',
    products_sub: 'Everything you sell, goods and services.',
    stock_levels: 'Stock levels',
    stock_levels_sub: 'What you have, and where.',
    stock_movements: 'Stock movements',
    stock_movements_sub: 'Every in and out, in order. This log cannot be edited.',
    stock_adjustments: 'Stock adjustments',
    stock_adjustments_sub: 'How stock is corrected by hand. Every one leaves a record.',
    warehouses: 'Warehouses',
    warehouses_sub: 'Where the stock physically is.',
    warehouse: 'Warehouse',
    all_warehouses: 'All warehouses',
    new_warehouse: 'New warehouse',
    edit_warehouse: 'Edit warehouse',
    warehouse_code: 'Code',
    warehouse_address: 'Address',
    close_warehouse: 'Close warehouse',
    reopen_warehouse: 'Reopen',

    // ===== product =====
    product: 'Product',
    new_product: 'New product',
    edit_product: 'Edit product',
    product_details: 'Product details',
    basics: 'Basics',
    pricing: 'Pricing',
    kind_good: 'Good',
    kind_service: 'Service',
    kind_goods: 'Goods',
    kind_services: 'Services',
    kind_good_hint: 'Counted and valued in stock.',
    kind_service_hint: 'Sold by time or by job. Nothing to count.',
    sku: 'SKU',
    sku_hint: 'Leave blank to generate one.',
    unit: 'Unit',
    sale_price: 'Sale price',
    cost_hint: 'Used to value stock and post cost of sale.',
    cost_locked_hint: 'Set from receipts now. The average below is what stock is worth.',
    average_cost: 'Average cost',
    margin: 'Margin',
    base_currency_hint: 'Prices are in your base currency.',
    track_stock: 'Track stock for this product',
    track_stock_hint: 'Off for something you sell but never count, like a printed brochure.',
    track_stock_needs_module: 'Switch the Inventory product on to count this.',
    opening_stock: 'Opening stock',
    opening_stock_hint: 'Creates one stock adjustment dated today.',
    opening_unit_cost: 'Opening cost per unit',
    income_account: 'Income account',
    cogs_account: 'Cost of goods sold',
    inventory_account: 'Inventory account',
    sold_last_90: 'Sold in the last 90 days',
    not_sold_yet: 'Not sold yet',
    revenue_90: 'Revenue',
    cost_90: 'Cost',
    duplicate_product: 'Duplicate',
    archive: 'Archive',
    unarchive: 'Unarchive',
    archived: 'Archived',
    show_archived: 'Show archived',
    export_csv: 'Export CSV',
    change_category: 'Change category',
    manage_categories: 'Categories',
    new_category: 'New category',
    no_categories: 'No categories yet',
    n_selected: 'selected',
    select_row: 'Select row',
    select_all_page: 'Select all on this page',
    no_products: 'No products yet',
    no_products_sub:
      'Add the goods and services you sell. You can price them now and count the stock later.',
    no_products_match: 'No products match',
    no_products_match_sub: 'Try a different search, or clear the filters.',
    product_search_ph: 'Name or SKU',
    confirm_archive_products: 'Archive the selected products? They stay on every document they already appear on.',
    confirm_delete_product: 'Delete this product? Only possible while nothing references it.',

    // ===== product image =====
    image: 'Image',
    add_image: 'Add image',
    change_image: 'Change',
    remove_image: 'Remove',
    image_drop_hint: 'Drop, paste or click',
    image_removed: 'Image removed',
    image_pending: 'Uploaded when you save.',
    undo: 'Undo',
    image_too_big: 'Images only, up to 5 MB.',

    // ===== stock =====
    stock: 'Stock',
    on_hand: 'On hand',
    committed: 'Committed',
    available: 'Available',
    inventory_value: 'Inventory value',
    stock_value: 'Stock value',
    units_on_hand: 'Units on hand',
    products_tracked: 'Products tracked',
    across_all_units: 'across all units',
    moving_average: 'moving average',
    stock_by_warehouse: 'Stock by warehouse',
    negative_rows: 'Negative rows',
    negative_stock_hint: 'sold below what was received',
    no_stock_yet: 'No stock recorded yet',
    no_stock_counted: 'No stock counted yet',
    no_stock_counted_sub:
      'Count your stock once and every movement after it is tracked automatically.',
    no_warehouses: 'No warehouses yet',
    no_warehouses_sub: 'Add a warehouse before counting stock.',
    add_warehouse: 'Add warehouse',
    stock_in_stock: 'In stock',
    stock_low: 'Low',
    stock_out: 'Out of stock',
    stock_not_tracked: 'Not tracked',
    below_reorder_only: 'Below reorder only',

    // ===== movements =====
    movement_change: 'Change',
    movement_balance: 'Balance',
    movement_details: 'Movement',
    recent_movements: 'Recent movements',
    no_movements: 'No movements yet',
    no_movements_period: 'No movements in this period',
    no_movements_period_sub: 'Try a wider date range.',
    no_movements_ever: 'No stock movements yet',
    no_movements_ever_sub: 'Movements appear here as you sell, receive and count stock.',
    filtered_to_product: 'One product only',
    clear_filter: 'Clear',
    mv_purchase: 'Purchase',
    mv_sale: 'Sale',
    mv_adjustment: 'Count',
    mv_write_off: 'Write off',
    mv_opening: 'Opening',
    mv_transfer_in: 'Transfer in',
    mv_transfer_out: 'Transfer out',
    mv_reversal: 'Reversal',

    // ===== adjustments, the count flow =====
    adjustments: 'Adjustments',
    stock_adjustment: 'Stock adjustment',
    count_stock: 'Count stock',
    new_adjustment: 'New adjustment',
    edit_adjustment: 'Edit adjustment',
    adjustment_number: 'Adjustment #',
    adjustment_date: 'Adjustment date',
    counted_by: 'Recorded by',
    warehouse_locked_hint: 'Locked once you start counting.',
    system_qty: 'System',
    counted_qty: 'Counted',
    unit_cost: 'Unit cost',
    line_value: 'Value',
    not_counted: 'Not counted',
    n_counted: 'counted',
    filter_the_sheet: 'Filter the sheet…',
    review_counts: 'Review',
    post_count: 'Post',
    post_count_title: 'Post stock adjustment',
    post_count_warning: 'You are about to change stock. This cannot be undone.',
    products_change: 'products change',
    products_no_change: 'products matched and will not move.',
    net_value_change: 'Net',
    value_change: 'Value change',
    products_counted: 'Products counted',
    products_changed: 'Products changed',
    count_reason: 'Reason',
    reason_count: 'Counting correction',
    reason_write_off: 'Damaged, lost or expired',
    reason_opening: 'Opening balance',
    reason_count_hint: 'Type what you counted. We work out the difference.',
    reason_write_off_hint: 'Type the loss as a negative number. A write-off never adds stock.',
    reason_opening_hint: 'Stock that existed before Railit did. It posts as owner capital, not income.',
    count_attest: 'I counted this stock myself.',
    status_posted: 'Posted',
    adjust_stock: 'Adjust stock',
    nothing_to_count: 'Nothing to count in this warehouse',
    nothing_to_count_sub: 'Add products first, or pick another warehouse.',
    no_adjustments: 'No stock adjustments yet',
    no_adjustments_sub:
      'An adjustment is how stock is corrected by hand. Every one leaves a record.',
    no_adjustment_lines: 'Nothing counted yet',
    confirm_post_adjustment:
      'Post this adjustment? It writes the stock movements and the journal entry, and cannot be undone.',
    confirm_void_adjustment: 'Void this adjustment? Both the movements and the journal are reversed.',
    confirm_delete_adjustment: 'Delete this draft? Nothing has been posted yet.',
    pick_warehouse_first: 'Pick a warehouse to load the sheet.',

    // ===== low stock =====
    low_stock: 'Low stock',
    out_of_stock: 'Out of stock',
    below_reorder: 'Below reorder',
    reorder_point: 'Reorder point',
    reorder_point_hint: 'We flag it below this.',
    reorder_qty: 'Reorder quantity',
    suggested_order: 'Suggested order',
    low_stock_band: 'products are at or below their reorder point.',
    out_of_stock_band: 'products are out of stock.',
    show_them: 'Show them',
    dismiss: 'Dismiss',

    // ===== who sells us this =====
    suppliers: 'Suppliers',
    supplier: 'Supplier',
    add_supplier: 'Add supplier',
    select_supplier: 'Choose a supplier',
    no_suppliers_yet: 'Nobody is on file as selling this yet.',
    supplier_sku: 'Their code',
    last_price: 'Last price',
    never_purchased: 'Never bought from them',
    lead_time_days: 'Lead time (days)',
    preferred_supplier: 'Preferred',
    make_preferred: 'Make preferred',
    supplier_price_hint:
      'The last price updates itself from every supplier bill you post, so it stays true without anybody maintaining it. The preferred supplier is the one an order goes to.',

    // ===== units, the closed list from lib/units.ts =====
    unit_unit: 'Unit',
    unit_piece: 'Piece',
    unit_kg: 'Kilogram',
    unit_g: 'Gram',
    unit_litre: 'Litre',
    unit_ml: 'Millilitre',
    unit_metre: 'Metre',
    unit_cm: 'Centimetre',
    unit_box: 'Box',
    unit_pack: 'Pack',
    unit_carton: 'Carton',
    unit_hour: 'Hour',
    unit_day: 'Day',

    // ===== server error codes =====
    item_sku_taken: 'That SKU is already used.',
    item_type_required: 'Say whether this is a good or a service.',
    item_in_use: 'This product appears on documents and cannot be deleted. Archive it instead.',
    item_has_stock: 'This product still has stock.',
    insufficient_stock: 'There is not enough stock for that.',
    module_off: 'The Inventory product is switched off for this company.',
    category_in_use: 'Products still use this category.',

    // ===== activity log and undo =====
    files: 'Files',
    onboarding: 'Onboarding',
    onboarding_offer: 'Bring your data across, or start with the assistant',

    // ── Closing the company down ─────────────────────────────────────────────
    org_delete_title: 'Delete this company',
    org_delete_lede:
      'Everything in this workspace goes: the ledger, every document, every staff record and everybody\u2019s access to it. You get {n} days to change your mind, and after that it cannot be brought back.',
    org_delete_cta: 'Delete this company',
    org_delete_owner_only: 'Only the owner of this company can delete it.',
    org_delete_continue: 'I understand, continue',
    org_delete_step1_title: 'This is what would be deleted',
    org_delete_step1_lede: 'Everything below is in this workspace right now. All of it goes.',
    org_delete_step2_title: 'What happens next',
    org_delete_step2_a:
      'The company keeps working normally for {n} days. Nobody loses access and nothing stops.',
    org_delete_step2_b:
      'You can undo it at any point in those days, from this same screen, and everything carries on as if you never asked.',
    org_delete_step2_c:
      'After that it is destroyed. We cannot bring it back for you, and neither can anybody else.',
    org_delete_step3_title: 'Type the company name',
    org_delete_step3_lede: 'To confirm, type {name} below.',
    org_delete_final: 'Schedule the deletion',
    org_delete_name_wrong: 'That is not this company name.',
    org_delete_scheduled: 'Scheduled. This company will be deleted in {n} days.',
    org_delete_cancelled: 'Cancelled. This company is staying.',
    org_delete_keep: 'Keep this company',
    org_delete_pending: 'This company is scheduled for deletion in {n} days, on {date}.',
    org_delete_pending_note:
      'Everything still works until then. Press the button below and it carries on as if you never asked.',
    org_delete_banner: 'This company will be deleted in {n} days. Everything works normally until then.',
    org_delete_banner_undo: 'Keep it',

    // ── Nobody has a company ─────────────────────────────────────────────────
    no_company_title: 'You are not in any company yet',
    no_company_lede:
      'A company is the workspace your books live in. Create one and you are the owner of it, or wait for a colleague to invite you into theirs.',
    no_company_cta: 'Create a company',
    no_company_invited: 'Waiting for an invitation? It arrives by email and brings you straight in.',

    // ── The company is closed while it waits ─────────────────────────────────
    closed_title: 'This company is closed',
    closed_lede:
      'You asked for it to be deleted, so nobody can work in it any more. Nothing has been destroyed yet: everything is exactly where it was, and it stays that way for {n} more days, until {date}.',
    closed_restore_lede: 'To bring it back, type {name} below.',
    closed_restore_cta: 'Bring this company back',
    closed_restore_note:
      'Everything returns untouched except the subscription. It was cancelled when the company closed and cannot be un-cancelled, so you would need to subscribe again.',
    closed_ask_owner:
      'The owner of this company asked for it to be deleted. Only they can bring it back, and only before the date above.',
    closed_other_companies: 'You also belong to:',
    org_switch_deleting: 'Deleting in {n}d',
    files_lede: 'Every document the company has, filed by the month it belongs to.',
    new_folder: 'New folder',
    all_files: 'All files',
    not_filed_yet: 'Not filed yet',
    folders: 'Folders',
    files_stored: 'files stored',
    search_files: 'Search files',
    no_file_matches: 'No file matches that',
    nothing_filed_here: 'Nothing filed here',
    files_empty_lede: 'Every receipt, invoice and photograph attached to a record shows up here.',
    try_a_shorter_word: 'Try a shorter word, or clear the search.',
    unlink: 'Unlink',
    not_linked_title: 'Not linked to anything',
    not_linked_lede: 'This file is not attached to any record yet.',
    added: 'Added',
    activity_log: 'Activity log',
    activity_log_sub: 'Every change, who made it, and how.',
    filter_person: 'Person',
    filter_channel: 'Channel',
    filter_how: 'How',
    filter_entity: 'Record type',
    filter_action: 'Action',
    all_people: 'Everyone',
    all_channels: 'All channels',
    all_ways: 'Any way',
    all_entities: 'All records',
    all_actions: 'All actions',
    only_assistant: 'Only the assistant',
    only_undone: 'Only undone',
    channel_web: 'Web panel',
    channel_whatsapp: 'WhatsApp',
    channel_telegram: 'Telegram',
    channel_api: 'Connected app',
    channel_system: 'System',
    by_person: 'Typed by hand',
    by_ai: 'Proposed by the assistant, approved by a person',
    by_ai_short: 'Assistant',
    by_system: 'Automatic',
    actor_system: 'System',
    field: 'Field',
    what_changed: 'What changed',
    where_from: 'Where it came from',
    before_: 'Before',
    after_: 'After',
    their_words: 'What they said',
    approval_preview: 'What was approved',
    also_recorded: 'Also recorded',
    lines_unchanged: '{count} lines, unchanged',
    lines_changed: '{count} lines, {n} changed',
    and_more: 'and {n} more',
    undo_review: 'Review this undo',
    undo_effects: 'This will do',
    undo_creates: 'This will create',
    undo_keeps: 'This will keep',
    undo_warnings: 'Be aware',
    undo_reason: 'Reason',
    undo_reason_ph: 'entered in error',
    undo_reason_required: 'A reason is required.',
    undone: 'Undone',
    undone_by: 'Undone by {name} on {date}',
    undone_ok: 'Undone.',
    undo_not_possible: 'This cannot be undone',
    undo_no_ALREADY_UNDONE: 'This has already been undone.',
    undo_no_SUPERSEDED: 'A later change replaced this one. Undo that one first.',
    undo_no_HAS_PAYMENTS: 'Payments have been recorded against this. Reverse them first.',
    undo_no_NOT_UNDOABLE: 'There is no safe way to reverse this.',
    undo_no_UNDO_OF_UNDO: 'This action is itself an undo.',
    undo_no_TOO_OLD: 'This is more than 90 days old. Edit the record instead.',
    undo_no_NOT_PERMITTED: 'Only the person who did this, an Owner or an Admin may undo it.',
    undo_no_STALE: 'The record changed while you were reading this.',
    undo_no_RECORD_GONE: 'The record no longer exists.',
    undo_no_DOWNSTREAM_DOCUMENT: 'This entry belongs to a document. Undo the document.',
    undo_no_PLAN_NOT_ENTITLED: 'Your current plan does not include that module.',
    undo_no_PERIOD_CLOSED: 'The accounting period is closed.',
    undo_no_sent: 'The message has already gone out. Nothing here can recall it.',
    undo_no_deleted: 'The record was permanently deleted.',
    undo_no_file: 'The file was permanently deleted from storage.',
    no_activity: 'Nothing recorded yet',
    no_activity_sub: 'Every change made in this workspace appears here.',
    no_activity_match: 'Nothing matches those filters',
    view_record: 'Open the record',
    act_create: 'Created',
    act_update: 'Changed',
    act_delete: 'Deleted',
    act_post: 'Posted',
    act_pay: 'Paid',
    act_send: 'Sent',
    act_sign: 'Signed',
    act_void: 'Voided',
    act_reverse: 'Reversed',
    act_submit: 'Submitted',
    act_approve: 'Approved',
    act_reject: 'Rejected',
    act_terminate: 'Terminated',
    act_deactivate: 'Deactivated',
    act_restore: 'Restored',
    act_reinstate: 'Reinstated',
    act_enable: 'Switched on',
    act_disable: 'Switched off',
    act_grant: 'Granted',
    act_revoke: 'Revoked',
    act_login: 'Signed in',
    act_logout: 'Signed out',
    act_password_reset: 'Password reset',
    act_reset: 'Leave year reset',
    act_undo: 'Undone',
    ent_Invoice: 'Invoice',
    ent_Bill: 'Supplier bill',
    ent_Expense: 'Expense',
    ent_Payment: 'Payment',
    ent_JournalEntry: 'Journal entry',
    ent_Quotation: 'Quotation',
    ent_Contract: 'Contract',
    ent_ContractTemplate: 'Contract template',
    ent_Customer: 'Customer',
    ent_Vendor: 'Supplier',
    ent_Employee: 'Employee',
    ent_PayrollRun: 'Payroll run',
    ent_Payslip: 'Payslip',
    ent_Item: 'Product or service',
    ent_ItemCategory: 'Category',
    ent_ItemVendor: 'Product supplier',
    ent_StockAdjustment: 'Stock adjustment',
    ent_Warehouse: 'Warehouse',
    ent_Account: 'Ledger account',
    ent_BankAccount: 'Bank account',
    ent_Organization: 'Workspace',
    ent_Membership: 'Membership',
    ent_Invitation: 'Invitation',
    ent_Setting: 'Settings',
    ent_User: 'User',
    ent_ApiToken: 'Connected app key',
    ent_Attachment: 'Attachment',
    ent_RecordComment: 'Note',
    ent_Subscription: 'Recurring subscription',
    ent_OrgSubscription: 'Plan and billing',
    ent_WhatsappAccount: 'WhatsApp connection',
    ent_WhatsappContact: 'WhatsApp contact',
    ent_Memory: 'Assistant memory',
    ent_FxRate: 'Exchange rate',
    ent_Email: 'Email',
    ent_OAuthClient: 'Connected application',
    f_modules: 'Modules',
    f_locale: 'Language',
    f_timezone: 'Time zone',
    f_base_salary: 'Salary',
    f_track_stock: 'Track stock',
    f_is_default: 'Default',
    f_is_active: 'Active',

    // ── Bringing a company in ────────────────────────────────────────────────
    // The first screen a new workspace sees. Three doors, then whichever one
    // they opened. Every string is here in both languages rather than local to
    // the page, because this is the screen a Bahraini customer meets first and
    // an English word left behind in an Arabic sentence is the whole first
    // impression.
    start_title: 'Bring your company in',
    start_lede: 'Nothing here is empty for long. Pick whichever of these you already have.',
    start_skip: 'Start with an empty workspace',
    start_zoho_title: 'Bring it from Zoho',
    start_zoho_lede:
      'Connect your Zoho Books account and your customers, suppliers, items and documents come across, and keep coming across.',
    start_zoho_needs: 'You will need to sign in to Zoho',
    start_files_title: 'Drop your files in',
    start_files_lede:
      'Spreadsheets, PDFs, photographs, a zip of the whole folder. Every file is read or kept, and you are told exactly what happened to each one.',
    start_files_needs: 'Anything you have. Nothing has to be tidy first',
    start_agent_title: 'Start from nothing, with the assistant',
    start_agent_lede:
      'No files yet? The assistant asks what the company is and sets it up with you, a couple of questions at a time.',
    start_agent_needs: 'About five minutes',
    start_drop_anywhere: 'Drop to start reading',

    // ── The drop box ─────────────────────────────────────────────────────────
    intake_title: 'Drop your files in',
    intake_lede:
      'Anything you have. Spreadsheets are read row by row, everything else is kept as a document, and nothing is written to your books until you say so.',
    intake_drop: 'Drop files here',
    intake_drop_hint: 'or choose them',
    intake_drop_kinds: 'Excel, CSV, PDF, images, or a .zip of the lot',
    intake_choose: 'Choose files',
    intake_start: 'Read these files',
    intake_reading: 'Reading',
    intake_sheet_of: 'sheet {i} of {n}',
    intake_rows: '{n} rows',
    intake_waiting: 'Waiting',
    intake_of_files: '{done} of {total} files',

    // Per-file outcomes. The words on the row and the words in the summary are
    // the same words on purpose: a person should not have to work out that
    // "read" and "imported" are the same thing.
    intake_outcome_imported: 'Read',
    intake_outcome_stored: 'Kept',
    intake_outcome_refused: 'Not read',
    intake_outcome_pending: 'Reading',
    intake_files_read: 'read',
    intake_files_kept: 'kept as documents',
    intake_files_refused: 'not understood',
    intake_all_accounted: 'Every file is accounted for.',

    // ── What happened to one file, in the reader's own language ──────────────
    // The server records the English wording for its log and a CODE for this.
    // A report a Bahraini customer cannot read is half a report, and it is the
    // whole product of this screen. See IntakeFile.detail_code.
    io_PATH_ESCAPE: 'This entry is named "{name}", which points outside the archive, so it was not opened.',
    io_ENCRYPTED: 'This file is password protected. Remove the password and send it again.',
    io_UNSUPPORTED_METHOD: 'This file is compressed in a way the system cannot read. Zip it again with the standard settings.',
    io_ENTRY_TOO_LARGE: 'This file unpacks to more than the {limit} limit for one file.',
    io_ARCHIVE_TOO_LARGE: 'The archive unpacks to more than {limit}, so it was stopped here rather than filling the disk.',
    io_TOO_MANY_FILES: 'This archive holds more than {limit} files. Send it in parts.',
    io_TOO_DEEP: 'This archive is nested {depth} levels deep, past the limit of {max}. It was kept as a file rather than unpacked.',
    io_CORRUPT: 'This could not be opened. It may be damaged or only partly uploaded.',
    io_EMPTY: 'This file is empty (zero bytes), so there was nothing to read.',
    io_NO_STORAGE: 'Document storage is not set up on this server, so this file could not be kept.',
    io_STORE_FAILED: 'This file could not be stored: {why}',
    io_IMPORTED: 'Read as {sheets} sheets, {ready} ready to import.',
    io_STORED_DOCUMENT: 'Kept as a document.',
    io_STORED_UNKNOWN: 'The system does not read this file type, so it was kept as a file.',
    io_STORED_UNREADABLE_SHEET: 'This could not be read as a spreadsheet, so it was kept as a file.',
    io_STORED_NO_ROWS: 'This opened as a spreadsheet but has no rows in any sheet, so it was kept as a file.',
    io_STORED_NO_IMPORTABLE_SHEET:
      'Read as {sheets} sheets, none of which is a list this system can import. Kept as a file.',

    // The name and the bytes disagreeing, appended to whichever line above.
    io_note: 'named .{ext}, and it is really {real}',
    io_note_noext: 'no extension, and it is really {real}',
    io_real_pdf: 'a PDF',
    io_real_png: 'a PNG image',
    io_real_jpeg: 'a JPEG image',
    io_real_gif: 'a GIF image',
    io_real_tiff: 'a TIFF scan',
    io_real_heic: 'a HEIC photograph',
    io_real_zip: 'a zip archive',
    io_real_not_a_workbook: 'not a workbook',

    // Why one sheet did not stage. Same code-not-a-sentence rule as above.
    is_SHEET_NOT_A_LIST:
      'This sheet is not a list of customers, suppliers, staff or products, so there is nothing here to import it as.',
    is_SHEET_BYTES_GONE: 'The file this sheet came from is no longer available. Drop it in again.',
    is_SHEET_NO_COLUMNS: 'No columns are mapped, so there is nothing to read from this sheet.',
    is_SHEET_ALL_EMPTY: 'Every row in this sheet was empty once the mapped columns were read.',
    is_SHEET_NONE_VALID: 'No row in this sheet passed validation. The column mapping is probably wrong.',
    is_SHEET_FAILED: 'This sheet could not be read: {why}',

    // ── The mapping review ───────────────────────────────────────────────────
    intake_review_title: 'Check what the columns mean',
    intake_review_lede:
      'This is the one thing worth your eyes. Everything below is read by the system, not retyped, so a wrong column here is the only way a number can end up in the wrong place.',
    intake_review_none: 'Nothing here needs importing.',
    intake_column: 'Column in your file',
    intake_field: 'Goes to',
    intake_ignore: 'Leave out',
    intake_constant: 'No column in your file',
    intake_entity: 'This sheet is a list of',
    intake_entity_customer: 'Customers',
    intake_entity_vendor: 'Suppliers',
    intake_entity_employee: 'Staff',
    intake_entity_item: 'Products',
    intake_entity_supplier_bill: 'Supplier invoices',
    intake_entity_customer_invoice: 'Invoices you issued',
    intake_entity_document_lines: 'Line items of other invoices',
    intake_entity_none: 'Not a list to import',
    /*
      THE MONEY WARNING.

      Deliberately blunt, and deliberately about a column rather than a row:
      the failure it exists for was five columns of money dropped at once,
      behind a screen that said the import had worked.
    */
    intake_money_like: 'These columns hold amounts and nothing is reading them: {columns}',
    intake_unmapped: 'Not being read: {columns}',
    intake_lines_for: 'These are the line items of',
    intake_lines_for_none: 'They belong to no other sheet',
    intake_lines_key: 'matched on',
    intake_lines_coverage: '{n}% of them found their document',
    intake_lines_by_person: 'you chose this',
    intake_by_model: 'Matched automatically',
    intake_by_person: 'You changed this',
    intake_skip_sheet: 'Skip this sheet',
    intake_sample: 'First rows',
    intake_import: 'Import {n} rows',
    intake_importing: 'Reading every row…',

    // ── The receipt ──────────────────────────────────────────────────────────
    intake_done_title: 'Ready to go into your books',
    intake_done_lede:
      'Every row is read and checked. Nothing has been written yet: press the button below and it goes in.',
    intake_staged: '{n} rows ready',
    intake_rejected: '{n} rows could not be read',
    intake_apply: 'Add {n} rows to my books',
    intake_applying: 'Writing them in…',
    intake_applied_title: 'It is in your books',
    intake_applied_lede: '{n} rows were added. You can open your workspace and see them.',
    intake_written: '{n} rows added',
    intake_open_dashboard: 'Open my workspace',
    intake_receipt: 'What came in',
    intake_nothing_staged: 'Nothing was staged from these files.',
    intake_failed: 'This import stopped',
    intake_retry: 'Try again',
    intake_reconnecting: 'Reconnecting…',
    intake_finished: 'Finished',
    intake_open_files: 'Open Documents',
    intake_go_dashboard: 'Go to the dashboard',

    // ── The list-beside-detail screen ────────────────────────────────────────
    clear_selection: 'Clear',
    resize_list: 'Resize the list',
    back_to_list: 'Back to the list',
    close_record: 'Close this record',
    more: 'More',
    more_new: 'Something else',
    attach_files: 'Attach files',
    comments_history: 'Notes and history',
    date_range: 'Date range',
    /* An invoice can be raised against a walk-in with no contact record, and
       the placeholder used to be a bare dash, which reads as missing data
       rather than as a deliberate absence. */
    no_customer: 'No customer',

    /*
      HOW LATE SOMETHING IS. See lib/documentAge.ts.

      English has two forms and picks between them with `Intl.PluralRules`, so
      four of these six are the same sentence. They are written out anyway
      rather than collapsed behind a fallback: the whole point of the table is
      that a translator can see every case the code can ask for, and a missing
      key that silently resolves to another one is the bug this shape prevents.
    */
    doc_age_overdue_zero: 'Overdue by {n} days',
    doc_age_overdue_one: 'Overdue by 1 day',
    doc_age_overdue_two: 'Overdue by {n} days',
    doc_age_overdue_few: 'Overdue by {n} days',
    doc_age_overdue_many: 'Overdue by {n} days',
    doc_age_overdue_other: 'Overdue by {n} days',
    doc_age_due_in_zero: 'Due in {n} days',
    doc_age_due_in_one: 'Due in 1 day',
    doc_age_due_in_two: 'Due in {n} days',
    doc_age_due_in_few: 'Due in {n} days',
    doc_age_due_in_many: 'Due in {n} days',
    doc_age_due_in_other: 'Due in {n} days',
    doc_age_due_today: 'Due today',
    doc_age_due_tomorrow: 'Due tomorrow',

    // ── Saved views ──────────────────────────────────────────────────────────
    view_all_invoices: 'All invoices',
    view_overdue_invoices: 'Overdue invoices',
    view_draft_invoices: 'Drafts',
    view_all_customers: 'All customers',
    view_active_customers: 'Active customers',
    view_inactive_customers: 'Inactive customers',

    // ── What to do next ──────────────────────────────────────────────────────
    next_invoice_draft: 'Nobody has seen this invoice yet. {action}.',
    next_invoice_draft_action: 'Send it to {name}',
    next_invoice_due: '{amount} of this invoice is still outstanding. {action}.',
    next_invoice_overdue:
      'This invoice is past its due date and {amount} is still outstanding. {action}.',
    next_invoice_pay_action: 'Record a payment',
    next_customer_email:
      'There is no email address on file, so nothing can be sent to {name}. {action}.',
    next_customer_email_action: 'Add one',
    next_customer_invoice: '{name} has never been invoiced. {action}.',
    next_customer_invoice_action: 'Create the first invoice',

    // ── People and catalogue: the split-view lists ───────────────────────────
    /* Views on the catalogue. Kind and the archive are presets of one axis,
       what is in the list at all, so they are views; stock is not, because
       /items?stock=low is a link the low-stock band already sends. */
    view_all_products: 'All products',
    view_goods: 'Goods',
    view_services: 'Services',
    view_archived_products: 'Archived products',
    view_all_employees: 'All employees',
    view_active_employees: 'Active employees',
    view_paused_employees: 'Paused',
    view_terminated_employees: 'Terminated',

    next_item_price:
      'There is no sale price on {name}, so it cannot go on an invoice. {action}.',
    next_item_price_action: 'Set one',
    next_item_low: 'Stock of {name} has fallen below its reorder point. {action}.',
    next_item_out: 'There is none of {name} left on the shelf. {action}.',
    next_item_stock_action: 'Record an adjustment',
    next_employee_contract: 'Nothing is signed on file for {name}. {action}.',
    next_employee_contract_action: 'Write the first contract',

    // ── Purchases: the split-view lists ──────────────────────────────────────
    /* The empty slot on a row, named rather than left as a dash: a bill always
       has a vendor and a payment usually has an account, so when one is missing
       the row should say so in words. */
    no_vendor: 'No vendor',
    no_bank_account: 'No account',
    /* A payment that settles no document has nobody on the other side of it,
       which is a real thing (an advance, a bank movement) and not a gap in the
       data. It is said, rather than left blank or filled with a guess. */
    unlinked_payment: 'No counterparty',

    view_all_bills: 'All bills',
    view_overdue_bills: 'Overdue bills',
    view_draft_bills: 'Drafts',
    view_all_expenses: 'All expenses',
    view_awaiting_expenses: 'Waiting on approval',
    view_draft_expenses: 'Drafts',
    view_all_vendors: 'All vendors',
    view_active_vendors: 'Active vendors',
    view_inactive_vendors: 'Inactive vendors',
    view_all_payments: 'All payments',
    view_inbound_payments: 'Money in',
    view_outbound_payments: 'Money out',

    next_bill_draft: 'This bill is not in the books yet. {action}.',
    next_bill_draft_action: 'Post it',
    next_bill_due: '{amount} of this bill is still owed. {action}.',
    next_bill_overdue:
      'This bill is past its due date and {amount} is still owed. {action}.',
    next_bill_pay_action: 'Record a payment',
    next_expense_draft: 'Nobody has been asked to approve this yet. {action}.',
    next_expense_rejected: 'This claim came back rejected. Put right what was wrong, then {action}.',
    next_expense_submit_action: 'send it for approval',
    next_expense_submitted: 'This claim is waiting on a decision. {action}.',
    next_expense_approve_action: 'Approve it',
    next_expense_approved: 'This expense is approved but not in the books yet. {action}.',
    next_expense_post_action: 'Post it',
    next_vendor_email:
      'There is no email address on file, so nothing can be sent to {name}. {action}.',
    next_vendor_email_action: 'Add one',

    // ── The sales split: views ───────────────────────────────────────────────
    view_all_quotations: 'All quotations',
    view_draft_quotations: 'Drafts',
    view_all_credit_notes: 'All credit notes',
    view_draft_credit_notes: 'Drafts',
    view_all_contracts: 'All contracts',
    view_all_subscriptions: 'All subscriptions',

    // ── The sales split: what to do next ─────────────────────────────────────
    next_quotation_draft: 'Nobody has seen this quotation yet. {action}.',
    next_quotation_draft_action: 'Send it to {name}',
    next_quotation_sent: '{name} has not answered this quotation yet. {action}.',
    next_quotation_sent_action: 'Mark it signed',
    next_quotation_signed: 'This quotation was accepted and is not an invoice yet. {action}.',
    next_quotation_signed_action: 'Convert it',
    next_credit_note_draft: 'This credit note is a draft, so it is not credit yet. {action}.',
    next_credit_note_draft_action: 'Issue it to {name}',
    next_credit_note_unused: '{amount} of this credit note has not been used. {action}.',
    next_credit_note_unused_action: 'Put it against an invoice',
    next_contract_unsigned: 'Nothing binds {name} until this contract is signed. {action}.',
    next_contract_unsigned_action: 'Sign it',
    next_subscription_due: '{name} was due to be charged on {date} and was not. {action}.',
    next_subscription_due_action: 'Charge it now',
    next_subscription_paused: '{name} is paused, so nothing is being charged. {action}.',
    next_subscription_paused_action: 'Start it again',

    // ── The customer page: receivables, income, transactions, statement ──────
    /* Three tabs, not five: there is no comment thread and no per-customer mail
       log in this product, so neither is offered. See pages/customers/Detail. */
    cx_tab_overview: 'Overview',
    cx_tab_transactions: 'Transactions',
    cx_tab_statement: 'Statement',

    cx_receivables: 'Receivables',
    cx_unused_credits: 'Unused credits',
    cx_open_invoices: 'Open invoices',
    /* Said out loud because a column of permanent zeros with no explanation
       reads as a broken feature rather than a property of the product. */
    cx_credits_note:
      'A receipt is always entered against a document and can never exceed what that document still owes, so nothing is ever left over to sit as a credit.',
    cx_never_invoiced: '{name} has never been invoiced, so there is nothing owing.',

    cx_lifetime: 'Lifetime',
    cx_invoiced: 'Invoiced',
    cx_received: 'Received',
    cx_first_invoice: 'First invoice',
    cx_last_invoice: 'Last invoice',
    cx_last_payment: 'Last payment',
    cx_none_yet: 'None yet',

    cx_income: 'Income',
    cx_basis: 'Basis',
    cx_basis_accrual: 'Accrual',
    cx_basis_cash: 'Cash',
    cx_basis_accrual_note:
      'The month each invoice was raised, less the VAT on it, whether or not it has been paid.',
    cx_basis_cash_note:
      'The month each receipt arrived, less the VAT share of the invoice it settled.',
    cx_last_n_months: 'Last {n} months',
    cx_income_none: '{name} has never been invoiced, so there is nothing to chart.',
    cx_income_total: 'Total, {from} to {to}',

    cx_show: 'Show',
    cx_everything: 'Everything',
    cx_show_all: 'All {n}',
    /* The invoice a receipt settled, and the invoice a quotation became. */
    cx_against: 'Against',
    cx_became: 'Became',
    cx_ends: 'Ends',
    cx_next_charge: 'Next charge',

    cx_statement_summary: 'Statement of accounts',
    cx_statement_lines: 'Transactions',
    cx_statement_empty: 'Nothing was invoiced or received in this period.',
    cx_transaction: 'Transaction',
    cx_balance: 'Balance',
    cx_balance_due: 'Balance due',
    cx_stmt_invoice: 'Invoice',
    cx_stmt_due_on: 'Due {date}',
    cx_stmt_settles: 'Against {number}',
    cx_stmt_payment: 'Payment received',

    /* ---------------------------------------------------------------- drafts
       The Drafts page. Note what is NOT here: any sentence describing a
       change. Those come from the `preview` column the formatters in
       agent/approvals.ts wrote, in the language the conversation was held in,
       and translating them here would mean a second description of the same
       row that could disagree with the card in the chat. What is translated is
       the furniture around them. */
    drafts: 'Drafts',
    drafts_lede: 'Every change staged and waiting, wherever it was asked for. Nothing on this page has touched the ledger yet.',
    drafts_waiting: '{n} waiting',
    drafts_search_ph: 'Search the wording, an amount, a name\u2026',
    drafts_none: 'Nothing is waiting',
    drafts_none_sub: 'When the assistant, a WhatsApp thread, an import or a payment correction stages a change, it waits here until somebody applies it.',
    drafts_none_but_decided:
      'Nothing is waiting. {n} change(s) have already been decided, under the other tabs above.',
    drafts_none_match: 'No draft matches these filters',
    draft_all_statuses: 'Any status',
    draft_status_pending: 'Waiting',
    draft_status_expired: 'Expired',
    draft_status_approved: 'Applied',
    draft_status_rejected: 'Rejected',
    draft_status_superseded: 'Replaced',
    draft_kind: 'Kind',
    draft_all_kinds: 'Any kind',
    draft_sort: 'Sort',
    draft_sort_newest: 'Newest first',
    draft_sort_oldest: 'Oldest first',
    draft_sort_expiring: 'Closing soonest',
    draft_sort_largest: 'Largest amount',
    draft_only_mine: 'Only mine',
    draft_apply: 'Apply',
    draft_reject: 'Reject',
    draft_reject_lede: 'The change stays on this list, marked rejected, with your reason beside it. Nothing is deleted.',
    draft_reject_reason: 'Reason',
    draft_reject_reason_ph: 'Why this should not be recorded',
    draft_reject_reason_short: 'At least {n} characters, in your own words.',
    draft_applied: 'Applied.',
    draft_rejected: 'Rejected, and kept on the list.',
    draft_rejected_because: 'Rejected:',
    draft_blocked_expired: 'The window closed before anybody applied it. Ask for the change again to get a fresh one.',
    draft_blocked_decided: 'Already decided.',
    draft_blocked_not_yours: 'Only {who} can apply this one.',
    draft_blocked_role: 'Your role can read drafts but not apply them. Owner, Admin and Finance decide.',
    draft_window_closed: 'Window closed',
    draft_minutes_left: '{n} min left',
    draft_hours_left: '{n} h left',
    draft_attachments: 'Attachments',
    draft_origin_assistant: 'Assistant',
    draft_origin_payment: 'Payment correction',
    draft_origin_intake: 'Import',
    draft_origin_telegram: 'Telegram',
    draft_staged_from: 'Staged from',
    draft_expires: 'Expires',
    draft_payload: 'Exactly what will be written',
    intent_create_expense: 'New expense',
    intent_create_invoice: 'New invoice',
    intent_create_quotation: 'New quotation',
    intent_record_invoice_payment: 'Record a payment',
    intent_bulk_create: 'Several records at once',
    intent_update_employee: 'Update an employee',
    intent_record_adjustment: 'Manual journal',
    intent_record_customer_refund: 'Customer refund',
    intent_reverse_payment: 'Take back a receipt',
    intent_void_document: 'Void a document',
    intent_reverse_expense: 'Reverse an expense',
    intent_update_party: 'Update a customer or supplier',
    intent_update_company_profile: 'Update company details',
    intent_create_bank_account: 'New bank account',
    intent_close_bank_account: 'Close a bank account',
    intent_create_item: 'New product',
    intent_update_item: 'Update a product',
    intent_adjust_stock: 'Stock adjustment',
    intent_create_purchase: 'New purchase',
    intent_book_leave: 'Book leave',
    intent_decide_leave: 'Decide a leave request',
    intent_reverse_journal: 'Reverse a journal entry',

    /* ───────────────────────────────────────────────────────────────────────
       Reversing a journal entry, from the entry itself. Append-only block;
       keep English and Arabic in step. */
    reverse_entry: 'Reverse this entry',
    reverse_entry_lede:
      'Nothing is deleted. A second entry is posted that flips every line of this one, on this entry\'s own date, so a period you have already reported does not move. Both stay on the account and each points at the other.',
    reverse_entry_reason: 'Why',
    reverse_entry_reason_ph:
      'Posted twice: the same bank charge was entered from one message on Sunday and again on Monday.',
    reverse_entry_reason_short:
      'At least {n} characters. It goes on the reversing entry and is read by whoever checks the books.',
    reverse_entry_confirm: 'I have checked this, and I understand it is recorded in the books.',
    reverse_entry_submit: 'Prepare this reversal',
    reverse_entry_staged: 'Prepared. Nothing has changed yet: read it below and apply it.',
    reverse_entry_pending: 'Reversal waiting to be applied',
    reverse_entry_pending_sub: 'Nothing has been written yet. Read it, then apply it or discard it.',
    reverse_entry_apply: 'Apply the reversal',
    reverse_entry_discard: 'Discard',
    reverse_entry_applied: 'Applied.',
    reverse_entry_discarded: 'Discarded. Nothing was changed.',
    reverse_entry_not_yours: 'Only the person who asked for this can apply it.',
    /* The two directions of the link, drawn on whichever entry is open. */
    reversed_by_entry: 'Reversed by {number}',
    reverses_entry: 'This reverses {number}',
    /* Said BEFORE the button, not after: the audit already suspects this entry
       and the person about to press Apply is the one who can confirm it. */
    reverse_entry_duplicate_note:
      'The ledger audit reports this entry as saying the same thing as {number}. If that is a mistake, this is how to undo it.',
  },
  ar: {
    dashboard: 'الرئيسية',
    accounting: 'المحاسبة',
    subscriptions: 'الاشتراكات',
    expenses: 'المصروفات',
    payroll: 'الرواتب',
    employees: 'الموظفون',
    contracts: 'العقود',
    invoices: 'الفواتير',
    quotations: 'عروض الأسعار',
    bills: 'الفواتير الواردة',
    payments: 'المدفوعات',
    bank: 'البنك',
    customers: 'العملاء',
    vendors: 'الموردون',
    chart_of_accounts: 'دليل الحسابات',
    journal: 'دفتر اليومية',
    reports: 'التقارير',
    users: 'المستخدمون والصلاحيات',
    settings: 'الإعدادات',
    finance: 'الماليّة',
    people: 'الموارد البشرية',
    sales: 'المبيعات',
    system: 'النظام',
    recurring: 'المتكررة',
    welcome: 'مرحبًا بعودتك',
    search: 'ابحث عن أي شيء…',
    new_: 'جديد',
    export_: 'تصدير',
    print: 'طباعة',
    refresh: 'تحديث',
    revenue: 'الإيرادات',
    net_profit: 'صافي الربح',
    burn: 'الإنفاق الشهري',
    cash: 'الرصيد النقدي',
    name: 'الاسم',
    email: 'البريد',
    role: 'الدور',
    status: 'الحالة',
    amount: 'المبلغ',
    date: 'التاريخ',
    actions: 'إجراءات',
    add: 'إضافة',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    view: 'عرض',
    active: 'فعّال',
    paused: 'متوقف',
    overdue: 'متأخر',
    draft: 'مسودة',
    sent: 'مُرسل',
    signed: 'موقّع',
    paid: 'مدفوع',
    pending: 'قيد الانتظار',
    this_week: 'أسبوع',
    this_month: 'شهر',
    this_year: 'سنة',
    revenue_vs_expenses: 'الإيرادات مقابل المصروفات',
    vs_last: 'مقارنة بالشهر الماضي',
    quick_actions: 'إجراءات سريعة',
    recent: 'النشاط الأخير',
    bhd: 'BHD',
    usd: 'USD',
    collapse_sidebar: 'تصغير',
    expand_sidebar: 'توسيع',
    nav_new: 'جديد',
    sign_in: 'تسجيل الدخول',
    sign_in_lede: 'استخدم بيانات الشركة. لم يتم دعوتك بعد؟ تواصل مع المدير.',
    password: 'كلمة المرور',
    keep_signed_in: 'ابقني مسجلاً',
    forgot: 'نسيت؟',
    continue_: 'متابعة',
    invalid_credentials: 'البريد أو كلمة المرور غير صحيحة.',
    internal_system: 'نظام داخلي — الوصول مقتصر على موظفي جوين فيوجر.',
    coming_soon: 'قريبًا',
    coming_soon_sub: 'هذه الوحدة ضمن مرحلة بناء لاحقة وليست متاحة بعد.',
    back_to_dashboard: 'العودة إلى الرئيسية',
    not_found: 'الصفحة غير موجودة',
    not_found_sub: 'المسار الذي حاولت فتحه غير موجود.',
    forbidden: 'غير مصرّح',
    forbidden_sub: 'دورك الحالي لا يملك صلاحية الوصول لهذه الصفحة.',
    logout: 'تسجيل الخروج',
    notifications: 'الإشعارات',
    loading: 'جاري التحميل',
    no_data: 'لا توجد بيانات بعد',
    dashboard_unavailable: 'بيانات لوحة التحكم غير متاحة حاليًا.',

    // shared / table
    search_placeholder: 'بحث…',
    show_inactive: 'عرض غير النشطين',
    only_active: 'النشطون فقط',
    all: 'الكل',
    yes: 'نعم',
    no: 'لا',
    optional: 'اختياري',
    confirm: 'تأكيد',
    saving: 'جارٍ الحفظ…',
    creating: 'جارٍ الإنشاء…',
    deleting: 'جارٍ الحذف…',
    inactive: 'غير نشط',
    restore: 'استعادة',
    set_default: 'تعيين كافتراضي',
    default_: 'افتراضي',
    none: 'لا يوجد',
    error_generic: 'حدث خطأ ما.',
    saved: 'تم الحفظ.',
    created: 'تم الإنشاء.',
    deleted: 'تم الحذف.',
    restored: 'تمت الاستعادة.',
    posted: 'تم الترحيل.',
    uploaded: 'تم الرفع.',
    uploading: 'جارٍ الرفع…',
    preview_pdf: 'معاينة PDF',
    document_preview_of: 'معاينة المستند {number}',
    document_preview_preparing: 'جارٍ تجهيز هذا المستند',
    document_preview_failed: 'تعذّر تجهيز هذا المستند.',
    open_document_full: 'فتح بالحجم الكامل',
    open_in_new_tab: 'فتح في تبويب جديد',
    try_again: 'إعادة المحاولة',
    attachments: 'المرفقات',
    no_attachments: 'لا توجد مرفقات بعد',
    confirm_delete_attachment: 'حذف هذا الملف؟',

    // customers / vendors
    new_customer: 'عميل جديد',
    edit_customer: 'تعديل العميل',
    new_vendor: 'مورّد جديد',
    edit_vendor: 'تعديل المورّد',
    customer_details: 'تفاصيل العميل',
    vendor_details: 'تفاصيل المورّد',
    name_ar: 'الاسم بالعربية',
    phone: 'الهاتف',
    vat_number: 'الرقم الضريبي',
    cr_number: 'السجل التجاري',
    address: 'العنوان',
    notes: 'ملاحظات',
    recent_invoices: 'أحدث الفواتير',
    recent_quotations: 'أحدث عروض الأسعار',
    recent_bills: 'أحدث فواتير المورد',
    recent_expenses: 'أحدث المصاريف',
    no_invoices: 'لا توجد فواتير بعد',
    no_bills: 'لا توجد فواتير مورد بعد',
    no_expenses: 'لا توجد مصاريف بعد',
    no_quotations: 'لا توجد عروض أسعار بعد',
    confirm_delete_customer: 'تعطيل هذا العميل؟',
    confirm_delete_vendor: 'تعطيل هذا المورّد؟',
    confirm_delete_account: 'حذف هذا الحساب؟ لا يمكن التراجع.',

    // accounts
    chart_of_accounts_sub: 'كل حسابات الأستاذ مجمّعة حسب النوع.',
    new_account: 'حساب جديد',
    edit_account: 'تعديل الحساب',
    code: 'الرمز',
    type: 'النوع',
    normal_balance: 'الرصيد الطبيعي',
    parent: 'الحساب الأب',
    debit: 'مدين',
    credit: 'دائن',
    asset: 'الأصول',
    liability: 'الخصوم',
    equity: 'حقوق الملكية',
    expense: 'المصروفات',
    balance: 'الرصيد',
    system_account: 'نظامي',

    // bank accounts
    bank_accounts: 'الحسابات البنكية',
    new_bank_account: 'حساب بنكي جديد',
    edit_bank_account: 'تعديل الحساب البنكي',
    opening_balance: 'الرصيد الافتتاحي',
    opening_balance_hint: 'اختياري. يسجّل رأس مال المالك في الدفاتر مقابل حقوق المالك.',
    bank_name: 'اسم البنك',
    iban: 'الآيبان',
    currency: 'العملة',

    // journal
    journal_viewer: 'سجل اليومية',
    post_manual_entry: 'ترحيل قيد يدوي',
    journal_entry: 'قيد محاسبي',
    entry_number: 'رقم القيد',
    entry_date: 'تاريخ القيد',
    memo: 'بيان',
    source: 'المصدر',
    total: 'الإجمالي',
    from_date: 'من',
    to_date: 'إلى',
    account: 'الحساب',
    add_line: 'إضافة سطر',
    remove: 'حذف',
    debits_total: 'المدين',
    credits_total: 'الدائن',
    balanced: 'متوازن',
    out_of_balance: 'غير متوازن',
    difference: 'الفرق',
    reversed: 'معكوس',
    manual: 'يدوي',
    invoice: 'فاتورة',
    bill: 'فاتورة موردين',
    payment: 'سداد',
    payroll_src: 'رواتب',
    subscription: 'اشتراك',

    // === Wave 3 Group A ===
    new_invoice: 'فاتورة جديدة',
    edit_invoice: 'تعديل الفاتورة',
    invoice_details: 'تفاصيل الفاتورة',
    customer: 'العميل',
    issue_date: 'تاريخ الإصدار',
    due_date: 'تاريخ الاستحقاق',
    bill_date: 'تاريخ الفاتورة',
    expires_at: 'تاريخ الانتهاء',
    expense_date: 'تاريخ المصروف',
    vendor: 'المورّد',
    category: 'الفئة',
    method: 'طريقة الدفع',
    bank_account: 'الحساب البنكي',
    bank_account_in: 'الحساب البنكي الذي سيستلم الدفعة',
    bank_account_out: 'الحساب البنكي الذي ستُصرف منه الدفعة',
    expense_account: 'حساب المصروف',
    description: 'الوصف',
    qty: 'الكمية',
    rate: 'السعر',
    line_amount: 'القيمة',
    subtotal: 'الإجمالي قبل الضريبة',
    vat: 'الضريبة',
    vat_rate: 'نسبة الضريبة',
    vat_amount: 'الضريبة',
    discount: 'الخصم',
    grand_total: 'الإجمالي',
    amount_paid: 'المدفوع',
    amount_due: 'المتبقي',
    total_due: 'المستحق',
    add_line_item: 'إضافة سطر',
    no_lines: 'لا توجد بنود بعد',
    select_customer: 'اختر العميل',
    select_vendor: 'اختر المورّد',
    select_account: 'اختر الحساب',
    select_bank_account: 'اختر الحساب البنكي',

    advance_section: 'الدفعة المقدمة',
    advance_type: 'فوترة دفعة مقدمة',
    advance_none: 'بدون — فوترة المبلغ كاملًا',
    advance_type_percent: 'نسبة من الصافي',
    advance_type_amount: 'مبلغ ثابت',
    advance_percent_value: 'نسبة الدفعة المقدمة %',
    advance_amount_value: 'قيمة الدفعة المقدمة',
    advance_percent_hint: '0–100 (مثال: 50 تعني 50%)',
    advance_amount_hint: 'بحد أقصى المجموع الفرعي ناقص الخصم',
    advance_custom_label_en: 'تسمية السطر (إنجليزي)',
    advance_custom_label_ar: 'تسمية السطر (عربي)',
    advance_default_label: 'دفعة مقدمة',
    advance_balance_later: 'الرصيد المتبقي للفوترة لاحقًا',
    advance_badge: 'دفعة مقدمة',
    advance_value_required: 'أدخل قيمة الدفعة المقدمة',
    advance_invalid: 'قيمة الدفعة المقدمة تقارب إلى صفر — ارفعها أو افوتر المبلغ كاملًا',
    advance_percent_invalid: 'لا يمكن أن تتجاوز الدفعة المقدمة 100%',
    advance_exceeds_net: 'لا يمكن أن تتجاوز الدفعة المقدمة المجموع الفرعي ناقص الخصم',
    advance_type_required: 'اختر نوع الدفعة المقدمة',

    new_quotation: 'عرض سعر جديد',
    edit_quotation: 'تعديل عرض السعر',
    quotation_details: 'تفاصيل عرض السعر',

    new_bill: 'فاتورة موردين جديدة',
    edit_bill: 'تعديل الفاتورة',
    bill_details: 'تفاصيل فاتورة المورد',

    new_expense: 'مصروف جديد',
    edit_expense: 'تعديل المصروف',
    expense_details: 'تفاصيل المصروف',

    send_: 'إرسال',
    sign_: 'توقيع',
    convert_to_invoice: 'تحويل إلى فاتورة',
    record_payment: 'تسجيل دفعة',
    void_: 'إلغاء',
    download_pdf: 'تحميل PDF',
    submit_: 'تقديم',
    approve_: 'اعتماد',
    reject_: 'رفض',
    post_: 'ترحيل',
    sent_ok: 'تم الإرسال.',
    signed_ok: 'تم التوقيع.',
    rejected_ok: 'تم الرفض.',
    voided_ok: 'تم الإلغاء.',
    payment_recorded: 'تم تسجيل الدفعة.',
    submitted_ok: 'تم التقديم.',
    approved_ok: 'تم الاعتماد.',
    converted_ok: 'تم التحويل إلى فاتورة.',

    status_draft: 'مسودة',
    status_sent: 'مُرسل',
    status_signed: 'موقّع',
    status_expired: 'منتهي',
    status_rejected: 'مرفوض',
    status_partial: 'مدفوع جزئيًا',
    status_paid: 'مدفوع',
    status_overdue: 'متأخر',
    status_void: 'ملغى',
    status_open: 'مفتوح',
    status_submitted: 'مُقدّم',
    status_approved: 'معتمد',

    filter_status: 'الحالة',
    all_statuses: 'كل الحالات',

    prev: 'السابق',
    next: 'التالي',
    page: 'صفحة',
    of: 'من',

    discount_exceeds_subtotal: 'لا يمكن أن يتجاوز الخصم المجموع الفرعي',
    invalid_url: 'أدخل رابطًا صحيحًا (https://…)',

    confirm_void_invoice: 'إلغاء هذه الفاتورة؟ سيتم عكس قيدها المحاسبي.',
    confirm_void_bill: 'إلغاء فاتورة المورد؟ سيتم عكس قيدها المحاسبي.',
    confirm_delete_invoice: 'حذف هذه المسودة؟',
    confirm_delete_quotation: 'حذف عرض السعر هذا؟',
    confirm_delete_bill: 'حذف مسودة الفاتورة؟',
    confirm_delete_expense: 'حذف هذا المصروف؟',
    confirm_reject_quotation: 'رفض عرض السعر؟',
    confirm_reject_expense: 'رفض هذا المصروف؟',

    payment_amount: 'المبلغ',
    paid_at: 'تاريخ السداد',
    payment_method: 'طريقة الدفع',
    payment_reference: 'المرجع',
    payment_memo: 'ملاحظات',
    method_bank: 'بنك',
    method_cash: 'نقدي',
    method_card: 'بطاقة',
    method_wire: 'حوالة',
    payments_history: 'الدفعات',
    no_payments: 'لا توجد دفعات بعد',
    journal_summary: 'القيد المحاسبي',
    no_journal_entry: 'لم يتم ترحيل قيد بعد',

    converted_invoice: 'الفاتورة المُولّدة',
    convert: 'تحويل',
    notes_optional: 'ملاحظات (اختياري)',
    line_subtotal: 'الإجمالي قبل الضريبة',
    line_total_due: 'المتبقي',
    invoice_summary: 'الملخص',
    quotation_summary: 'الملخص',
    bill_summary: 'الملخص',
    expense_summary: 'الملخص',
    submitted_by: 'قدّمها',
    approved_by: 'اعتمدها',
    receipt_url: 'رابط الإيصال',
    save_draft: 'حفظ كمسودة',

    // === Wave 3 Group B ===
    // employees
    new_employee: 'موظف جديد',
    edit_employee: 'تعديل الموظف',
    employee_details: 'تفاصيل الموظف',
    employee_number: 'رقم الموظف',
    department: 'القسم',
    contract_type: 'نوع العقد',
    base_salary: 'الراتب الأساسي',
    joined_at: 'تاريخ الانضمام',
    terminated_at: 'تاريخ إنهاء الخدمة',
    full_time: 'دوام كامل',
    part_time: 'دوام جزئي',
    contractor: 'متعاقد',
    terminated: 'منتهٍ',
    terminate: 'إنهاء الخدمة',
    confirm_terminate_employee:
      'إنهاء خدمة هذا الموظف؟ سيتم تعطيل أي حساب مستخدم مرتبط وإنهاء الجلسات. لا يمكن التراجع.',
    recent_payslips: 'أحدث كشوف الرواتب',
    no_payslips: 'لا توجد كشوف رواتب بعد',
    period: 'الفترة',
    gross: 'الإجمالي',
    deductions: 'الاستقطاعات',
    net_amount: 'الصافي',
    linked_user: 'المستخدم المرتبط',
    no_linked_user: 'لا يوجد مستخدم مرتبط',
    // payroll
    payroll_runs: 'دورات الرواتب',
    new_payroll_run: 'دورة رواتب جديدة',
    payroll_run: 'دورة رواتب',
    payroll_run_details: 'تفاصيل دورة الرواتب',
    period_start: 'بداية الفترة',
    period_end: 'نهاية الفترة',
    pay_date: 'تاريخ الصرف',
    total_gross: 'إجمالي الراتب',
    total_deductions: 'إجمالي الاستقطاعات',
    total_net: 'إجمالي الصافي',
    payslips: 'كشوف الرواتب',
    employee: 'الموظف',
    payment_status: 'حالة الدفع',
    post_run: 'ترحيل الدورة',
    pay_run: 'صرف الدورة',
    delete_run: 'حذف الدورة',
    confirm_delete_payroll_run: 'حذف هذه المسودة لدورة الرواتب؟',
    confirm_post_payroll_run:
      'ترحيل هذه الدورة؟ سيتم ترحيل كل كشف راتب للأستاذ ولن يمكن حذفه بعد ذلك.',
    pick_bank_account: 'اختر حسابًا بنكيًا',
    pay_payroll_title: 'صرف دورة الرواتب',
    optional_employees: 'موظفون محددون (اختياري)',
    optional_employees_hint: 'اتركه فارغًا لتضمين كل الموظفين النشطين.',
    no_payslips_in_run: 'لا توجد كشوف رواتب في هذه الدورة',
    // subscriptions
    new_subscription: 'اشتراك جديد',
    edit_subscription: 'تعديل الاشتراك',
    subscription_details: 'تفاصيل الاشتراك',
    service: 'الخدمة',
    cost: 'التكلفة',
    cycle: 'الدورة',
    monthly: 'شهري',
    quarterly: 'ربع سنوي',
    yearly: 'سنوي',
    auto_charge: 'فوترة تلقائية',
    auto: 'تلقائي',
    manual_charge: 'يدوي',
    pick_vendor: 'اختر موردًا',
    pick_expense_account: 'اختر حساب مصروف',
    owner: 'المسؤول',
    pick_owner_employee: 'اختر موظفًا مسؤولًا',
    pause: 'إيقاف مؤقت',
    resume: 'استئناف',
    charge_now: 'فوترة الآن',
    recent_charges: 'أحدث الفواتير',
    no_charges: 'لا توجد فواتير بعد',
    charged_at: 'تاريخ الفوترة',
    cancelled: 'ملغى',
    confirm_cancel_subscription: 'إلغاء هذا الاشتراك؟ ستتوقف الفواتير الجديدة.',
    confirm_charge_now:
      'إنشاء فاتورة موردين لهذا الاشتراك الآن؟ سيتم تقديم تاريخ الفوترة التالية.',
    confirm_delete_subscription: 'حذف هذا الاشتراك؟',
    // contracts
    new_contract: 'عقد جديد',
    edit_contract: 'تعديل العقد',
    contract_details: 'تفاصيل العقد',
    party: 'الطرف',
    party_name: 'اسم الطرف',
    kind: 'النوع',
    contract_value: 'القيمة',
    ends_at: 'تاريخ الانتهاء',
    body: 'النص',
    body_md: 'النص (إنجليزي - Markdown)',
    body_md_ar: 'النص (عربي - Markdown)',
    service_agreement: 'اتفاقية خدمات',
    saas_subscription: 'اشتراك SaaS',
    employment: 'توظيف',
    nda: 'اتفاقية سرية',
    mou: 'مذكرة تفاهم',
    other: 'أخرى',
    expired: 'منتهي',
    sign: 'توقيع',
    signed_at: 'تاريخ التوقيع',
    confirm_sign_contract: 'وضع هذا العقد كموقّع؟',
    confirm_terminate_contract: 'إنهاء هذا العقد؟ لا يمكن التراجع.',
    confirm_delete_contract: 'حذف هذه المسودة من العقد؟',
    // contract e-signature & templates
    esign_employer: 'توقيع صاحب العمل',
    esign_employee: 'توقيع الموظف',
    esign_sign_here: 'وقّع هنا',
    esign_clear: 'مسح',
    esign_submit: 'إرسال التوقيع',
    esign_name_placeholder: 'الاسم الكامل',
    esign_signed_by: 'وقّع بواسطة',
    esign_signed_on: 'تاريخ التوقيع',
    esign_awaiting: 'بانتظار التوقيع',
    esign_saved: 'تم حفظ التوقيع.',
    contract_templates: 'قوالب العقود',
    new_template: 'قالب جديد',
    load_template: 'تحميل قالب',
    template_name: 'اسم القالب',
    template_name_ar: 'اسم القالب بالعربية',
    select_template: 'اختر قالبًا…',
    it_employment_template: 'توظيف تقني (جزئي)',
    linked_employee: 'الموظف المرتبط',
    select_employee: 'اختر موظفًا',
    cpr: 'الرقم الشخصي (CPR)',
    employee_contracts: 'العقود',
    no_contracts: 'لا توجد عقود بعد',
    pdf_preview: 'معاينة PDF',

    // === Wave 3 Group C ===
    // Settings
    settings_company: 'الشركة',
    settings_tax: 'الضرائب',
    settings_branding: 'الهوية',
    settings_subtitle: 'بيانات الشركة، إعدادات الضريبة الافتراضية، وهوية الفواتير.',
    company_name: 'اسم الشركة',
    company_name_ar: 'اسم الشركة بالعربية',
    default_currency: 'العملة الافتراضية',
    language: 'اللغة',
    default_vat_rate: 'نسبة الضريبة الافتراضية (%)',
    fy_start_month: 'بداية السنة المالية',
    invoice_terms: 'شروط وأحكام الفواتير',
    bank_iban: 'الآيبان البنكي',
    payment_details: 'بيانات الدفع',
    document_bank_account: 'الحساب البنكي في المستندات',
    document_bank_account_custom: 'يدوي',
    document_bank_account_hint: 'هذا هو الحساب الذي يُطلب من العملاء التحويل إليه في فواتيرهم.',
    document_bank_account_empty: 'لا توجد حسابات بنكية بعد — أضف حسابًا أولًا من',
    settings_saved: 'تم حفظ الإعدادات.',
    month_jan: 'يناير',
    month_feb: 'فبراير',
    month_mar: 'مارس',
    month_apr: 'أبريل',
    month_may: 'مايو',
    month_jun: 'يونيو',
    month_jul: 'يوليو',
    month_aug: 'أغسطس',
    month_sep: 'سبتمبر',
    month_oct: 'أكتوبر',
    month_nov: 'نوفمبر',
    month_dec: 'ديسمبر',

    // Users
    users_subtitle: 'إدارة من يستطيع الدخول وما يمكنه فعله.',
    invite_heading: 'انضم إلى {org} على Railit',
    invite_body: 'دُعيت بصلاحية {role}. هذا الرابط يخص {email}.',
    invite_existing_account: 'لديك حساب Railit بالفعل. القبول يضيف هذه الشركة إليه، وتنتقل بينها وبين شركاتك من أعلى الشاشة.',
    invite_accept: 'اقبل وافتح المساحة',
    invite_invalid_title: 'هذه الدعوة لم تعد صالحة',
    invite_invalid_body: 'ربما انتهت صلاحيتها أو استُخدمت أو سُحبت. اطلب ممن دعاك إرسال دعوة جديدة.',
    wa_country: 'الدولة',
    wa_country_code: 'مفتاح الدولة',
    notes_empty: 'لا توجد ملاحظات على هذا السجل.',
    notes_placeholder: 'شيء يحتاج معرفته من يفتح هذا السجل بعد ستة أشهر.',
    note_add: 'أضف ملاحظة',
    note_by_ai: 'الذكاء الاصطناعي',
    note_asked_for_by: 'بطلب من',
    note_written_by: 'بقلم',
    wa_other_country: 'دولة أخرى',
    wa_whose_phone: 'هاتف من',
    wa_my_own_phone: 'هاتفي أنا',
    wa_number_local: 'رقم الهاتف',
    wa_will_link: 'سيُربط الرقم',
    wa_my_numbers: 'أرقامي',
    wa_my_numbers_lede: 'أضف رقمك وتحدّث مع المساعد من واتساب. الأرقام الإضافية مجانية. يجيبك عن هذه الشركة، ويسألك أيّها تقصد إن كنت تدير أكثر من دفتر.',
    wa_assistant_number: 'راسل المساعد على',
    wa_add_number: 'أضف رقمي',
    wa_add_number_hint: 'أرسل له رسالة من ذلك الهاتف بعدها. أول رسالة هي ما يُثبت أن الرقم رقمك.',
    wa_number_added: 'أُضيف الرقم. أرسل رسالة من الهاتف لإكمال الربط.',
    wa_verified: 'موثّق',
    wa_awaiting_first_message: 'بانتظار أول رسالة منك',
    wa_no_numbers: 'لا توجد أرقام بعد.',
    wa_no_usage_yet: 'لم يُصرف من هذا الرقم شيء بعد.',
    wa_credits_30d: 'رصيد خلال ٣٠ يومًا',
    wa_turns_30d: 'محادثة',
    wa_last_used: 'آخر استخدام',
    add_user: 'أضف مستخدمًا',
    send_invitation: 'أرسل الدعوة',
    invite_link_hint: 'الدعوة في طريقها. إن لم يصل البريد، أرسل له هذا الرابط:',
    new_user: 'مستخدم جديد',
    edit_user: 'تعديل المستخدم',
    reset_password: 'إعادة تعيين كلمة المرور',
    disable_user: 'تعطيل المستخدم',
    confirm_disable_user: 'تعطيل هذا المستخدم؟ لن يتمكن من تسجيل الدخول.',
    user_disabled: 'تم تعطيل المستخدم.',
    password_reset_ok: 'تم تحديث كلمة المرور.',
    user_created: 'تم إنشاء المستخدم.',
    user_updated: 'تم تحديث المستخدم.',
    role_owner: 'مالك',
    role_admin: 'مدير',
    role_finance: 'مالية',
    role_engineer: 'مهندس',
    role_sales: 'مبيعات',
    role_designer: 'مصمم',
    role_viewer: 'مشاهد',
    mfa: 'التحقق الثنائي',
    mfa_enabled: 'مفعّل',
    mfa_disabled: 'غير مفعّل',
    last_login_at: 'آخر دخول',
    new_password: 'كلمة المرور الجديدة',
    confirm_password: 'تأكيد كلمة المرور',
    password_rule: '٨ خانات على الأقل، وفيها حروف وأرقام.',
    password_mismatch: 'كلمتا المرور غير متطابقتين.',
    back_to_login: 'العودة لتسجيل الدخول',
    reset_title: 'اختر كلمة مرور جديدة',
    reset_lede: 'اختر كلمة مرور لحسابك في Railit.',
    reset_submit: 'احفظ كلمة المرور',
    reset_no_token: 'هذه الصفحة تحتاج الرابط الموجود في بريدك. اطلب رابطًا جديدًا من صفحة الدخول.',
    reset_expired: 'انتهت صلاحية هذا الرابط. الروابط تدوم ساعة واحدة.',
    reset_used: 'استُخدم هذا الرابط من قبل. اطلب رابطًا جديدًا.',
    reset_invalid: 'هذا الرابط غير صالح.',
    reset_ask_again: 'أرسل لي رابطًا جديدًا',
    reset_done_title: 'تم ضبط كلمة المرور',
    reset_done_body: 'تستطيع الدخول بها الآن. وأي جهاز آخر كان مسجّلًا خرج من الحساب.',
    password_min_8: '٨ أحرف على الأقل، وتشمل حرفًا ورقمًا.',
    never_signed_in: 'لم يدخل بعد',

    // Dashboard (extra)
    revenue_mtd: 'إيرادات الشهر',
    profit_mtd: 'ربح الشهر',
    monthly_burn: 'الإنفاق الشهري',
    cash_on_hand: 'الرصيد النقدي',
    recent_activity: 'النشاط الأخير',
    upcoming: 'القادم',
    overdue_invoices: 'فواتير متأخرة',
    subs_due_7d: 'اشتراكات تستحق خلال 7 أيام',
    no_recent_activity: 'لا يوجد نشاط حديث',
    no_overdue_invoices: 'لا توجد فواتير متأخرة',
    no_upcoming_subs: 'لا توجد اشتراكات هذا الأسبوع',
    next_charge: 'موعد التجديد',
    today: 'اليوم',
    custom: 'مخصص',
    income_and_expense: 'الإيرادات والمصروفات',
    top_expenses: 'أعلى المصروفات',
    all_expenses: 'إجمالي المصروفات',
    total_receivables: 'إجمالي الذمم المدينة',
    total_payables: 'إجمالي الذمم الدائنة',
    current: 'جارية',
    cash_flow: 'التدفق النقدي',
    incoming: 'وارد',
    outgoing: 'صادر',
    net_cash: 'صافي النقد',
    profit: 'الربح',

    // Reports
    reports_subtitle: 'القوائم المالية، أعمار الذمم، وتصدير سريع PDF.',
    report_pnl: 'الأرباح والخسائر',
    report_balance_sheet: 'الميزانية العمومية',
    report_cashflow: 'التدفقات النقدية',
    report_ar_aging: 'أعمار الذمم المدينة',
    report_pnl_sub: 'الإيرادات والمصروفات وصافي الربح للفترة المختارة.',
    report_balance_sheet_sub: 'الأصول والخصوم وحقوق الملكية في تاريخ محدد.',
    report_cashflow_sub: 'تدفقات النقد الداخلة والخارجة خلال الفترة.',
    report_ar_aging_sub: 'الذمم المدينة المتبقية مجمّعة حسب العمر.',
    quick_pdfs: 'تقارير PDF سريعة',
    weekly_pdf: 'التقرير الأسبوعي (PDF)',
    daily_pdf: 'التقرير اليومي (PDF)',
    weekly_pdf_sub: 'إنشاء ملخص الإدارة لهذا الأسبوع.',
    daily_pdf_sub: 'إنشاء لقطة اليوم.',
    download: 'تحميل',
    pdf_downloaded: 'تم تحميل الملف.',
    week_number: 'رقم الأسبوع',
    year: 'السنة',
    prepared_by: 'إعداد',
    pnl_revenue: 'الإيرادات',
    pnl_expenses: 'المصروفات',
    pnl_net: 'صافي الربح',
    bs_assets: 'الأصول',
    bs_liabilities: 'الخصوم',
    bs_equity: 'حقوق الملكية',
    cf_opening: 'الرصيد الافتتاحي',
    cf_inflows: 'التدفقات الداخلة',
    cf_outflows: 'التدفقات الخارجة',
    cf_closing: 'الرصيد الختامي',
    cf_total_in: 'إجمالي الداخل',
    cf_total_out: 'إجمالي الخارج',
    aging_as_of: 'بتاريخ',
    no_invoices_in_bucket: 'لا توجد فواتير في هذه الفئة',
    as_of: 'بتاريخ',

    // الإعدادات: كلود / MCP
    settings_mcp: 'MCP',
    mcp_headline: 'اسأل كلود عن {org}',
    mcp_lead: 'اربط المساعد الذي تتحدّث إليه أصلاً ليجيبك من الدفاتر نفسها: ما المتأخر، وكم على العميل، وكم كلّف الشهر الماضي. ويستطيع كذلك إعداد مستند وحفظه نيابةً عنك.',
    mcp_state_connected: 'متصل',
    mcp_state_not_connected: 'غير متصل',
    mcp_connect_claude: 'اربط كلود',
    mcp_connect_how: 'ينسخ هذا الرابط ويفتح إعدادات الموصّلات في كلود في تبويب جديد. اختر «إضافة موصّل مخصّص» والصق الرابط هناك ثم سجّل الدخول إلى Railit لإتمام الربط.',
    mcp_link_label: 'رابط MCP الخاص بـ {org}',
    mcp_link_short: 'الرابط',
    mcp_copy: 'نسخ',
    mcp_copied: 'تم نسخ {what}',
    mcp_no_endpoint: 'لا يوجد عنوان MCP لهذه المساحة بعد.',
    mcp_scope_title: 'شركة واحدة، وهي هذه',
    mcp_scope_body: 'لكل شركة تنتمي إليها رابطها الخاص وصلاحيتها الخاصة. هذا الرابط يخصّ {org}، ويظل كذلك طوال عمر الاتصال. ولا يمكن الوصول من خلاله إلى أي شيء في شركاتك الأخرى.',
    mcp_acts_title: 'يتصرّف باسمك',
    mcp_acts_body: 'يملك التطبيق المتصل صلاحياتك نفسها في {org} ولا شيء خارجها، ويُسجَّل كل إجراء يقوم به في سجل النشاط باسمك.',
    mcp_connected_title: 'المتصل الآن',
    mcp_client_unnamed: 'تطبيق ذكاء اصطناعي',
    mcp_granted_on: 'اتصل في {date}',
    mcp_last_used: 'آخر استخدام {when}',
    mcp_never_used: 'لم يُستخدم بعد',
    mcp_disconnect: 'قطع الاتصال',
    mcp_other_title: 'تطبيقات أخرى',
    mcp_other_body: 'يقبل Claude Desktop وتطبيق كلود على الجوال الرابط نفسه بالخطوات نفسها. أما ChatGPT فلا يصل إلى خادم MCP إلا من الويب، وبعد تفعيل وضع المطوّر في إعداداته، فهو الرابط نفسه لكنه ليس النقرات الثلاث نفسها. وأي تطبيق آخر يطلب عنوان خادم MCP يقبل الرابط أعلاه كما هو.',
    mcp_tools_title: 'ما الذي يصل إليه ({n} أداة)',
    mcp_tokens_title: 'رموز الوصول',
    mcp_tokens_body: 'لتطبيق لا يستطيع فتح صفحة تسجيل دخول. يحمل الرمز صلاحياتك في {org} إلى أن تلغيه، فاحفظه كما تحفظ كلمة المرور.',
    mcp_token_name: 'لأي غرض',
    mcp_token_create: 'إنشاء رمز',
    mcp_token_default_name: 'رمز وصول',
    mcp_token_short: 'الرمز',
    mcp_copy_once: 'انسخه الآن. لن يظهر مرة أخرى.',
    mcp_no_tokens: 'لا توجد رموز.',
    mcp_revoke: 'إلغاء الرمز',
    mcp_token_revoked: 'أُلغي في {date}',
    mcp_forbidden_title: 'الإعداد متاح للمالك أو المدير فقط',
    mcp_forbidden_body: 'ربط تطبيق ذكاء اصطناعي يمنحه دفاتر هذه الشركة كاملة، لذلك تقتصر الصفحة على من يراها كاملة أصلاً. اطلب من المالك ربطه.',
    mcp_unavailable_title: 'تعذّر تحميل إعدادات MCP',
    net_income_trend: 'اتجاه صافي الدخل',
    access_denied: 'الوصول مرفوض',
    recent_subscriptions: 'الاشتراكات الأخيرة',
    no_subscriptions: 'لا توجد اشتراكات',
    enable_user: 'تفعيل المستخدم',
    user_enabled: 'تم تفعيل المستخدم.',
    subs_mtd: 'منذ بداية الشهر',
    live_cost_from: 'مباشر من',
    live_cost_updated: 'مُحدّث',
    live_cost_integration: 'التكامل',

    // ===== Railit SaaS =====
    crm: 'العملاء والمبيعات',
    org_settings: 'إعدادات المنشأة',
    settings_organization: 'المنشأة',
    settings_billing: 'الاشتراك',
    settings_whatsapp: 'واتساب',

    // auth
    create_account: 'أنشئ حسابك',
    signup_lede: 'مساحة عملك جاهزة خلال أقل من دقيقة. 14 يومًا مجانًا وبدون بطاقة.',
    signup_failed: 'تعذّر إنشاء الحساب. حاول مرة أخرى.',
    company_name_required: 'أدخل اسم الشركة',
    your_name: 'اسمك',
    name_required: 'أدخل اسمك',
    work_email: 'البريد الإلكتروني للعمل',
    email_invalid: 'أدخل بريدًا صحيحًا',
    password_min: '8 أحرف على الأقل',
    start_free_trial: 'ابدأ التجربة المجانية',
    have_account: 'لديك حساب؟',
    no_account: 'ليس لديك حساب؟',
    trial: 'التجربة',
    trial_days_n: '{n} أيام مجانًا',
    no_card_needed: 'بدون بطاقة',
    from_price: 'ابتداءً من',
    per_month: 'شهريًا',
    price_extra_user: '+ 4 دولار لكل مستخدم إضافي',

    // modules
    mod_finance: 'المالية',
    mod_finance_sub: 'فواتير ومطالبات وضريبة وقيود',
    mod_crm: 'إدارة العملاء',
    mod_crm_sub: 'العملاء وعروض الأسعار والعقود',
    mod_hr: 'الموارد البشرية',
    mod_hr_sub: 'الموظفون والرواتب وقسائم الراتب',
    mod_inventory: 'المخزون',
    mod_inventory_sub: 'الأصناف والكميات والحركات',
    mod_pos: 'نقاط البيع',
    mod_pos_sub: 'مبيعات الكاشير تُرحَّل للقيود',
    mod_ai: 'وكلاء الذكاء الاصطناعي',
    mod_ai_sub: 'محادثة وMCP وواتساب',

    // billing
    billing_base: 'الاشتراك',
    billing_extra_users: 'مستخدمون إضافيون ({n})',
    billing_monthly_total: 'الإجمالي الشهري',
    billing_renews_on: 'يتجدد في {date}',
    billing_manage: 'إدارة الاشتراك',
    billing_subscribe: 'اشترك الآن',
    billing_addons: 'الإضافات',
    billing_seats: 'المستخدمون',
    billing_seats_detail: '{used} مستخدم مدفوع. أول {included} مشمول في الاشتراك، وكل إضافي يُحتسب شهريًا.',
    billing_seats_short: '{used} مستخدم مدفوع · {price} شهريًا',
    agent_session_cost: 'الكريدت المستهلك في هذه المحادثة',
    agent_turn_cost: '{n} كريدت',
    agent_attach: 'إرفاق',
    agent_voice: 'صوت',
    agent_stop: 'إيقاف',
    agent: 'الوكيل',
    agent_new: 'محادثة جديدة',
    agent_history: 'السجل',
    agent_wide: 'توسيع',
    agent_narrow: 'تضييق',
    agent_send: 'إرسال',
    agent_greeting: 'كيف أساعدك؟',
    agent_scope: 'يعمل داخل {org}',
    agent_placeholder: 'اسأل عن أي شيء…',
    agent_not_wired: 'لم يُوصَل الوكيل بعد.',
    agent_failed: 'حدث خطأ. حاول مرة أخرى.',
    agent_no_credits: 'نفد كريدت الذكاء الاصطناعي في مساحة العمل. أضف المزيد من صفحة الفوترة.',
    agent_stopped_budget: 'توقّف — المهمة أطول مما يسمح به طلب واحد.',
    agent_stopped_credits: 'توقّف مبكرًا ليبقى لديك رصيد كريدت.',
    agent_files: 'الملفات',
    agent_resize: 'تغيير عرض اللوحة',
    agent_expand_full: 'افتح في وضع الوكيل',
    agent_snap_release: 'أفلت للانتقال إلى وضع الوكيل',
    agent_snap_confirm: 'اضغط Enter للانتقال إلى وضع الوكيل',
    agent_doc_loading: 'يُجهَّز…',
    agent_doc_download: 'تنزيل',
    agent_doc_failed: 'تعذّر فتح هذا المستند',
    agent_file_unread: 'لم يُقرأ',
    agent_file_too_big: 'حجم {name} أكبر من 10 ميجابايت.',
    agent_remove_file: 'إزالة',
    agent_reading: 'يقرأ ملفاتك…',
    agent_thinking: 'يعمل…',
    agent_proposed: 'تغيير مقترح',
    agent_apply: 'تنفيذ',
    agent_apply_anyway: 'نفّذه على أي حال',
    agent_discard: 'تجاهل',
    agent_applied: 'تم التنفيذ',
    agent_discarded: 'تم التجاهل',
    agent_no_longer_waiting: 'لم يعد هذا المقترح في الانتظار.',
    agent_apply_failed: 'تعذّر التنفيذ. ربما انتهت صلاحية الاقتراح.',
    agent_not_applied: 'لم يُحفظ أي شيء بعد.',
    agent_steps: '{n} خطوات',
    agent_steps_one: 'خطوة واحدة',
    agent_steps_failed: 'فشلت واحدة',
    agent_no_history: 'لا توجد محادثات بعد.',
    agent_untitled: 'بدون عنوان',
    agent_when_today: 'اليوم',
    agent_when_yesterday: 'أمس',
    agent_when_week: 'آخر 7 أيام',
    agent_when_older: 'أقدم',
    vs_last_month: 'مقارنة بنفس الفترة الشهر الماضي',
    recurring_commitments: 'التزامات متكررة',
    across_accounts: 'في جميع الحسابات',
    pct_overdue: '{n}% من هذا المبلغ متأخر بالفعل',
    view_all: 'عرض الكل',
    pdf_failed: 'تعذّر إنشاء ملف PDF. حاول مرة أخرى.',
    agent_threads: 'المحادثات',
    agent_greeting_hint: 'اسأل عن دفاترك، أو أرفق ملفًا لنعمل عليه.',
    agent_mode: 'وضع الوكيل',
    agent_mode_on: 'وكيل',
    agent_mode_off: 'يدوي',
    agent_mode_hint: 'حوّل مساحة العمل كلها إلى محادثة.',
    model_auto: 'تلقائي',
    model_auto_hint: 'يختار المستوى المناسب لكل سؤال — الأرخص للبحث، والأدق للتسويات.',
    model_pick_hint: 'بأي دقة تريد أن يعمل الوكيل؟',
    model_per_month: '~{n} سؤال شهريًا',
    model_credits_each: '{n} كريدت للسؤال',
    model_thinking: 'تفكير',
    model_thinking_hint: 'يفكّر خطوة بخطوة قبل الإجابة. أبطأ، وبضعف الكريدت تقريبًا.',
    model_thinking_cost: 'نحو {n} كريدت للسؤال مع التفكير.',
    thinking_live: 'يفكّر…',
    thinking_done: 'فكّر في هذا',
    memory: 'الذاكرة',
    memory_title: 'ما يتذكّره الوكيل',
    memory_subtitle: 'حقائق يحتفظ بها بين المحادثات حتى لا تكررها. احذف أي شيء خاطئ أو قديم.',
    memory_company: 'عن شركتك',
    memory_company_hint: 'يراها كل من في مساحة العمل',
    memory_personal: 'عنك أنت',
    memory_personal_hint: 'تراها أنت فقط',
    memory_by_you: 'أنت كتبتها',
    memory_by_agent: 'الوكيل دوّنها',
    memory_used: 'استُخدمت في {n} محادثة',
    memory_unused: 'لم تُستخدم بعد',
    memory_active: 'مفعّلة',
    memory_export: 'تصدير كـ Markdown',
    memory_empty: 'لا شيء محفوظ بعد',
    memory_empty_hint: 'أخبر الوكيل بشيء مثل «نحن دائمًا نفوتر بمهلة 45 يومًا» وسيحتفظ به.',
    memory_deleted: 'تم النسيان.',
    memory_failed: 'تعذّر تحديث هذه الذاكرة.',
    memory_upload: 'رفع ملف .md',
    memory_uploaded: 'تمت إضافة {n} ملفات.',
    memory_kind_fact: 'حقيقة',
    memory_kind_note: 'ملاحظة',
    memory_kind_skill: 'طريقة عمل',
    memory_kind_fact_hint: 'قصيرة بما يكفي ليحملها الوكيل في كل محادثة.',
    memory_kind_note_hint: 'مستند. الوكيل يرى الوصف فقط، ويفتحه عندما يبدو مناسبًا.',
    memory_kind_skill_hint: 'إجراء. يقرأه الوكيل عندما يوشك على تنفيذ هذا الشيء.',
    memory_total_cost: 'كل هذا يضيف {tok} توكن لكل سؤال — نحو {cr} كريدت للدور.',
    memory_carried: 'محمولة في كل محادثة · {tok} توكن',
    memory_opened: 'فُتحت {n} مرة',
    memory_never_opened: 'لم تُفتح قط — كلّفت {cr} كريدت',
    memory_cost_hint: 'تضيف {tok} توكن لكل سؤال. {cr} كريدت حتى الآن.',
    memory_when_used: 'يفتحه الوكيل عندما',
    memory_file: 'الملف',
    memory_words: '{n} كلمة — تُفتح فقط عندما يطابق الوصف أعلاه ما يُسأل عنه',
    billing_credits_left: 'كريدت متبقٍ',
    billing_credits_top_up: 'أوشك على النفاد — أضِف رصيدًا',
    billing_credits_trial: 'متبقٍ من تجربتك',
    billing_credits_from_topup: 'متبقٍ من رصيدك المشترى',
    account_avatar_hint: 'اضغط أو أفلِت صورة PNG أو JPEG أو WebP. حتى 2 ميجابايت.',
    billing_team: 'حجم الفريق',
    billing_team_lede: 'اشترِ مقاعد قبل شغلها — المقعد المدفوع لا يكلّفك شيئًا عند شغله.',
    billing_seats_using: '{n} في مساحة العمل حاليًا',
    billing_seats_included_then: '{n} مشمول، ثم {price} لكل مستخدم',
    billing_seats_ahead: 'شراء {n} مقعد إضافي مسبقًا.',
    billing_seats_fewer: 'مقاعد أقل',
    billing_seats_more: 'مقاعد أكثر',
    billing_then_from_second: 'خصم {pct}%. ومن الشهر الثاني يصبح {price}.',
    purchases: 'المشتريات',
    money: 'الأموال',
    workspace: 'مساحة العمل',
    doc_accent_from_logo: 'اختر من الشعار',
    doc_accent_pick_failed: 'تعذّر استخراج لون من هذا الشعار.',
    per_month_short: ' /شهر',
    billing_billed_yearly: '{total} تُدفع سنويًا',
    billing_billed_yearly_total: '{total} تُخصم مرة واحدة في السنة',
    billing_delta_month: 'شهريًا',
    billing_switch_to_yearly: 'التحويل إلى الدفع السنوي',
    billing_switch_to_monthly: 'التحويل إلى الدفع الشهري',
    done: 'تم',
    error: 'حدث خطأ ما.',
    billing: 'الاشتراك',
    billing_configure_lede: 'اضبط اشتراكك على اليسار. والفاتورة على اليمين هي المبلغ الذي ستُحاسب عليه بالضبط.',
    billing_plan: 'الباقة',
    billing_plan_lede: 'كل ما بعدها يُسعَّر بناءً عليها.',
    billing_popular: 'الأكثر اختيارًا',
    billing_months_free: '{n} شهران مجانًا',
    billing_credits_monthly: '{n} كريدت ذكاء اصطناعي شهريًا',
    billing_first_month_is: 'الشهر الأول {price}',
    billing_ai: 'رصيد الذكاء الاصطناعي',
    billing_ai_monthly_lede: 'مخصّصك الشهري. يتجدّد كل دورة ولا يُرحَّل.',
    billing_included: 'مشمول',
    billing_included_in_plan: 'يأتي مع الباقة',
    billing_this_month: 'المستهلك هذه الدورة',
    billing_ai_model_note: 'يعمل على {model}',
    billing_purchased_left: 'بقي {n} كريدت مشترى',
    billing_addons_lede: 'منتجات يمكنك تشغيلها وإيقافها.',
    billing_addon_brings: '{messages} رسالة شهريًا. والذكاء الاصطناعي يُخصم من رصيد باقتك.',
    billing_addon_needs_plan: 'متاح في باقة {plan} فما فوق',
    billing_topup: 'باقات كريدت',
    billing_topup_lede: 'لحين نفاد المخصّص قبل انتهاء الشهر.',
    billing_topup_separate: 'تُشترى مرة واحدة ولا تنتهي صلاحيتها، وتُستهلك بعد نفاد المخصّص الشهري فقط. وهي ليست جزءًا من الاشتراك.',
    billing_approx_exchanges: 'نحو {n} محادثة',
    billing_percent_off: 'خصم {n}%',
    billing_your_bill: 'فاتورتك',
    billing_bill_lede_month: 'تُحاسب شهريًا. غيّر أو ألغِ في أي وقت.',
    billing_bill_lede_year: 'تُحاسب مرة كل سنة. عشرة أشهر مدفوعة وشهران مجانًا.',
    billing_seats_note: '{used} مستخدمين، {included} مشمول في الباقة',
    billing_total_month: 'شهريًا',
    billing_total_year: 'سنويًا',
    billing_first_month: 'شهرك الأول',
    billing_then_renews: 'خصم {pct}%. ثم يتجدّد بـ {price}.',
    billing_checkout: 'المتابعة إلى الدفع',
    billing_update: 'تحديث الاشتراك',
    billing_no_changes: 'لا تغييرات لتطبيقها',
    billing_updated: 'تم تحديث اشتراكك.',
    billing_owner_only: 'المالك وحده يمكنه تغيير الاشتراك.',
    billing_trial_free: 'مجانًا لمدة {days} أيام. بدون بطاقة حتى الآن.',
    billing_trial_running: 'تجربتك مجانية حتى {date}. ولن تحتاج بطاقة إلا للاستمرار بعدها.',
    billing_renews_plain: 'يتجدّد في {date}',
    billing_confirm_change: 'تأكيد التغيير',
    billing_confirm_change_lede: 'يحتسب سترايب الفرق بالتناسب مع ما دفعته لهذه الدورة.',
    billing_due_now: 'يُخصم اليوم',
    billing_due_now_unknown: 'يُحتسب عند الدفع',
    billing_next_invoice: 'الفاتورة القادمة',
    billing_confirm: 'تأكيد',
    billing_confirming: 'جارٍ التأكيد مع سترايب…',
    billing_confirming_lede: 'ثانية واحدة. تمت عملية الدفع بنجاح.',
    billing_thanks: 'شكرًا لك',
    billing_thanks_named: 'شكرًا لك يا {name}',
    billing_thanks_lede: 'مساحة عملك الآن على باقة {plan}. وهذا ما تم الاتفاق عليه.',
    billing_thanks_pending: 'دفعتك لدى سترايب. وسيظهر الاشتراك هنا خلال دقيقة.',
    billing_billing_starts: 'تبدأ المحاسبة',
    billing_topup_done: 'أُضيف الرصيد',
    billing_topup_done_lede: '{n} كريدت في حسابك. ولا تنتهي صلاحيتها.',
    company_settings: 'إعدادات الشركة',
    account_settings: 'حسابك',
    account_settings_lede:
      'اسمك وكلمة مرورك والشركات التي تنتمي إليها. هذه تتبعك أينما ذهبت — أما إعدادات الشركة فلا.',
    account_profile: 'الملف الشخصي',
    account_profile_lede: 'كيف تظهر لكل فريق تعمل معه.',
    account_title: 'المسمّى الوظيفي',
    account_email_fixed: 'عنوان تسجيل دخولك. تواصل مع الدعم لتغييره.',
    account_companies: 'شركاتك',
    account_companies_lede: 'حساب واحد، وأي عدد تريده من الشركات. ودورك قد يختلف في كل منها.',
    account_current: 'مفتوحة',
    account_open: 'انتقل إليها',
    account_security: 'الأمان',
    account_security_lede: 'كلمة مرورك تحمي كل شركة تنتمي إليها.',
    account_current_password: 'كلمة المرور الحالية',
    account_new_password: 'كلمة المرور الجديدة',
    account_change_password: 'تغيير كلمة المرور',
    account_password_scope:
      '8 أحرف على الأقل. تغييرها يسجّل خروجك من كل الأجهزة الأخرى.',
    account_password_changed: 'تم تغيير كلمة المرور.',
    account_password_changed_sessions: 'تم تغيير كلمة المرور. وأُنهيت {n} جلسة أخرى.',
    account_google_only:
      'هذا الحساب يسجّل الدخول عبر Google، فلا توجد كلمة مرور في وي هاي لتغييرها. أدِرها من حساب Google.',
    plan_trialing_on: 'قيد التجربة',
    plan_subscribe_to: 'اشترك',
    billing_leaving_checkout: 'جارٍ نقلك إلى صفحة الدفع الآمنة…',
    billing_leaving_topup: 'جارٍ نقلك إلى صفحة الدفع الآمنة…',
    billing_leaving_portal: 'جارٍ فتح بوابة الفوترة…',
    seat_limit_trial:
      'باقتك تشمل {included} مستخدم. اشترك لإضافة المزيد — كل مستخدم إضافي {price} شهريًا.',
    seat_limit_billing: 'فعّل الاشتراك قبل إضافة مستخدمين.',
    seat_next_costs: 'المستخدم التالي: {price} شهريًا',
    view_plans: 'عرض الباقات',
    billing_stripe_off: 'الاشتراكات غير مفعّلة في هذه النسخة.',
    billing_action_needed: 'الاشتراك يحتاج انتباهك',
    topup_title: 'أضف رصيد ذكاء اصطناعي',
    topup_subtitle: 'دفعة واحدة، والرصيد لا ينتهي بانتهاء الشهر.',
    topup_credits_word: 'رصيد',
    topup_addition:
      'هذه الـ{pack} تُضاف إلى {included} التي تتضمنها خطتك، فيصبح لديك {total} هذا الشهر.',
    topup_saving: 'أرخص بنسبة {percent}٪ لكل رصيد من أصغر باقة.',
    topup_cta: 'ادفع {price}',
    topup_leaving: 'نفتح صفحة الدفع…',
    topup_note: 'بيانات البطاقة تُدخل في Stripe، ولا نراها إطلاقًا.',
    credits_low_badge: 'الرصيد يقترب من النفاد',
    credits_out_badge: 'انتهى رصيد الذكاء الاصطناعي',
    credits_out_chat_title: 'انتهى رصيدك من الذكاء الاصطناعي',
    credits_out_chat_body: 'أضف رصيدًا لتكمل المحادثة. بقية النظام يعمل كما هو.',
    credits_out_chat_cta: 'أضف رصيدًا',
    /* لا يجوز لأي من هذه المفاتيح أن يذكر عدد أيام مجانية أو نسبة خصم. انظر
       الشرح المقابل في القاموس الإنجليزي. العرضان لم يعودا يُمنحان تلقائيًا
       (AUTOMATIC_OFFERS في backend/src/lib/pricing.ts)، وما يمنحه الكود يُذكر
       في مفاتيح promo_* بعد أن يقول الخادم قيمة ذلك الكود بعينه. */
    trial_offer_badge: 'فعّل اشتراكك',
    trial_offer_short: 'اختر باقة',
    trial_last_day: 'آخر يوم في فترة التجربة',
    trial_ending_title: 'تنتهي فترة تجربتك خلال {n} أيام',
    trial_ending_body: 'اختر باقة قبل انتهائها فتستمر مساحة العمل دون انقطاع. لا شيء يتوقف اليوم.',
    trial_ending_cta: 'اختر باقة',
    /* صندوق كود الخصم، في التسجيل وفي الدفع. حقل الأكواد في Stripe مُطفأ
       عمدًا، فالكود المكتوب هناك يتجاوز سقف الاستخدام وسجلّنا ونسبة الشريك.
       و promo_refused جواب واحد لكود غير موجود ولكود منتهٍ ولكود استُنفد، لأن
       الخادم لا يفرّق بينها عمدًا. */
    promo_have_code: 'لديك كود؟',
    promo_label: 'كود الخصم',
    promo_apply: 'تطبيق',
    promo_checking: 'جارٍ التحقق…',
    promo_refused: 'هذا الكود لن يعمل. راجعه مع من أعطاك إياه، ويمكنك المتابعة بدونه.',
    promo_remove: 'إزالة',
    promo_gives_days: '{days} يومًا إضافية مجانًا',
    promo_gives_percent: 'خصم {percent}٪ على شهرك الأول',
    promo_gives_percent_months: 'خصم {percent}٪ على أول {months} أشهر',
    promo_gives_both: '{days} يومًا إضافية مجانًا، وخصم {percent}٪ على شهرك الأول',
    promo_gives_both_months: '{days} يومًا إضافية مجانًا، وخصم {percent}٪ على أول {months} أشهر',
    hold_title: 'هذه المساحة موقوفة مؤقتًا',
    hold_body:
      'توقفت منذ انتهاء الاشتراك، ولم يُحذف شيء. اختر خطة لإعادتها، مع رسم لمرة واحدة قدره {fee} لاسترجاع البيانات وإعادة فتح الشركة.',
    hold_cta: 'استرجع الشركة',
    hold_note: 'دفاترك ومستنداتك وسجلّك كلها موجودة كما هي.',
    hold_warning:
      'إن بقي الاشتراك غير مدفوع، تتوقف المساحة في {date}. لا تُحذف: إعادة فتحها لاحقًا تكلّف خطة مع رسم استرجاع لمرة واحدة.',
    blocked_badge: 'للقراءة فقط',
    blocked_payment_title: 'توقفت مساحتك عن استقبال أي إدخال',
    blocked_payment_body:
      'لم تنجح دفعة الاشتراك، وانتهت مهلة إعادة المحاولة. تحديث البطاقة يعيد الكتابة فورًا.',
    blocked_payment_cta: 'حدّث طريقة الدفع',
    blocked_trial_title: 'انتهت فترة التجربة',
    blocked_trial_body:
      'اختر خطة لتعود إلى الإنشاء. كل ما أدخلته أثناء التجربة موجود كما هو.',
    blocked_trial_cta: 'اختر خطة',
    blocked_readonly_note:
      'لم يُحذف شيء. تستطيع الدخول وقراءة كل مستند وتصدير دفاترك.',
    blocked_keep_reading: 'أريد قراءة دفاتري',
    grace_banner_one: 'لم تنجح الدفعة. الكتابة تتوقف غدًا.',
    grace_banner_days: 'لم تنجح الدفعة. أمامك {days} أيام لتحديث البطاقة.',
    grace_banner_fix: 'عالج الأمر',
    billing_history: 'الفواتير',
    billing_credit_balance: 'لديك رصيد دائن:',
    billing_next_charge: 'الخصم القادم {amount} بتاريخ {date}.',
    invoice_reason_start: 'بداية الاشتراك',
    invoice_reason_renewal: 'تجديد',
    invoice_reason_change: 'تغيير الباقة',
    invoice_reason_usage: 'استهلاك',
    invoice_reason_manual: 'فاتورة مفردة',
    invoice_status_paid: 'مدفوعة',
    invoice_status_open: 'غير مدفوعة',
    invoice_status_draft: 'مسودة',
    invoice_status_void: 'ملغاة',
    invoice_status_uncollectible: 'معدومة',
    billing_reason_trial_expired: 'انتهت التجربة المجانية. اشترك لمتابعة التعديلات.',
    billing_reason_payment_overdue: 'فشلت عملية الدفع وانتهت مهلة السماح.',
    billing_reason_payment_retrying: 'فشلت عملية الدفع — نعيد المحاولة.',
    billing_reason_subscription_inactive: 'الاشتراك غير فعّال.',
    billing_reason_organization_suspended: 'تم تعليق هذه المنشأة.',
    sub_status_trialing: 'تجربة',
    sub_status_active: 'فعّال',
    sub_status_past_due: 'متأخر السداد',
    sub_status_unpaid: 'غير مدفوع',
    sub_status_canceled: 'ملغى',
    sub_status_incomplete: 'غير مكتمل',
    sub_status_paused: 'موقوف',
    trial_days_left: 'باقي {n} يوم من التجربة',
    trial_billing_starts: 'تبدأ الفوترة في {date}',

    // الباقة المستنتجة من الاستخدام الفعلي للتجربة — services/planRecommendation.ts
    rec_title: 'الباقة التي يشير إليها استخدامك',
    rec_lede: 'محسوبة مما فعلته مساحة العمل فعلًا — لا من تخمين يوم التسجيل.',
    rec_days_left: 'باقي {n} يوم',
    rec_measured: 'مقيسة على مدى {days} يوم',
    rec_measured_seats: '{n} مستخدم في مساحة العمل',
    rec_measured_credits: 'استُهلك {used} كريدت ذكاء اصطناعي — أي نحو {projected} شهريًا بهذا المعدل',
    rec_measured_credits_zero: 'لم يُستهلك أي كريدت ذكاء اصطناعي',
    rec_answer: '{plan} مع {seats} مستخدم',
    rec_apply: 'اختر {plan}',
    rec_applied: 'تم ضبطها على {plan}. راجع الفاتورة ثم تابع متى شئت.',
    rec_choose_other:
      'اختر أي باقة أخرى بالأسفل، ولا يكلّفك تغيير رأيك شيئًا — هذه توصية لا بوابة.',
    rec_reason_used_ai: 'استخدمت الذكاء الاصطناعي، لذا باقة المبتدئ مستبعدة: لا كريدت فيها إطلاقًا.',
    rec_reason_too_early:
      'لم تستخدم الذكاء الاصطناعي بعد، لكن {days} يوم مدة أقصر من أن نعتبرها تفضيلًا — لذا أبقيناه مفعّلًا.',
    rec_reason_no_ai: 'لم يُستهلك أي كريدت ذكاء اصطناعي خلال {days} يوم.',
    rec_starter_tradeoff:
      'باقة المبتدئ تُسقِط المساعد ووكيل واتساب وكل ما يستهلك كريدت. أما حساباتك ومستنداتك وخادم MCP فتبقى كما هي.',
    rec_reason_used_whatsapp: 'واتساب متصل، لذا أُدرجت الإضافة في هذا العرض.',
    rec_reason_credit_burn: 'بمعدل نحو {projected} كريدت شهريًا، لن تكفي الباقة الأدنى إلى آخر الشهر.',
    rec_reason_credits_topped_up:
      'الباقة وحدها تمنح {included} كريدت شهريًا، لذا أُدرجت شريحة كريدت شهرية للوصول إلى {total}.',
    rec_reason_credits_short:
      'لا شيء نبيعه يغطي {projected} كريدت شهريًا. وهذا أكبر مخصّص متاح عند {total} — توقّع شراء باقة كريدت في الأشهر الكثيفة.',
    rec_reason_extra_seats: '{n} مستخدمين: {included} مشمول في الباقة، و{extra} إضافي بـ {price} لكل واحد.',
    rec_reason_seats_included: 'كل مستخدميك الـ{n} مشمولون في الباقة.',
    rec_expired_title: 'انتهت تجربتك المجانية',
    rec_expired_lede:
      'لم يُحذف شيء. كل ما أدخلته لا يزال هنا للقراءة والتصدير. أما الإدخالات الجديدة والذكاء الاصطناعي فتبقى متوقفة حتى تختار باقة.',

    // appearance
    theme: 'المظهر',
    theme_light: 'فاتح',
    theme_dark: 'داكن',
    theme_system: 'حسب النظام',
    addon_credits: '{n} رسالة شهريًا',
    addon_usage: 'استُخدمت {used} من {quota} رسالة هذا الشهر',
    enable: 'تفعيل',
    disable: 'إيقاف',
    on: 'مفعّل',
    off: 'متوقف',

    // organization
    org_products: 'المنتجات',
    org_products_hint: 'كل المنتجات مشمولة في الاشتراك. أوقف ما لا تحتاجه ليختفي من القائمة الجانبية.',
    org_members: 'الفريق',
    org_people_moved: 'تتم إدارة الفريق والأدوار من صفحة المستخدمين.',
    org_people_moved_action: 'فتح المستخدمين',
    org_seat_pricing_hint: 'المشاهدون مجانًا. كل دور آخر بـ 1٫5 د.ب شهريًا.',
    invite: 'دعوة',
    invite_sent: 'تم إرسال الدعوة',
    pending_invitations: 'الدعوات المعلّقة',
    no_pending_invitations: 'لا توجد دعوات معلّقة',
    revoke: 'إلغاء الدعوة',
    invite_person: 'دعوة شخص',
    expires: 'تنتهي في',
    seat: 'المقعد',
    billable: 'مدفوع',
    free: 'مجاني',

    // whatsapp
    whatsapp_upsell: 'مساعد واتساب يجيب عن أسئلة حساباتك، ويرسل تذكيرات الدفع وتقريرًا يوميًا — 4 د.ب شهريًا.',
    whatsapp_enable_addon: 'تفعيل الإضافة',
    whatsapp_number: 'رقم الأعمال',
    whatsapp_connect_hint: 'الصق بيانات الاعتماد من حساب واتساب للأعمال في ميتا.',
    whatsapp_connected: 'تم ربط واتساب',
    whatsapp_connected_as: 'مرتبط بالرقم {phone}',
    whatsapp_phone: 'رقم الهاتف (E.164)',
    whatsapp_phone_id: 'معرّف رقم الهاتف',
    whatsapp_token: 'رمز الوصول',
    whatsapp_contacts: 'جهات الاتصال المسموح لها',
    whatsapp_contacts_hint: 'لا يستطيع أي رقم قراءة بيانات الشركة قبل ربطه بمستخدم في وي هاي.',
    whatsapp_no_contacts: 'لم يراسل أحد الرقم بعد.',
    whatsapp_daily_report: 'التقرير اليومي',
    whatsapp_daily_report_hint: 'ملخص قصير لما تمّت فوترته وتحصيله وصرفه وما هو متأخر.',
    not_linked: 'غير مرتبط',
    connect: 'ربط',
    recipients: 'المستلمون',
    time: 'الوقت',
    last_sent: 'آخر إرسال',

    // ===== Billing cards =====
    ai_credits: 'أرصدة الذكاء الاصطناعي هذا الشهر',
    ai_credits_hint: 'تُحتسب من الاستهلاك الفعلي لنموذج {model} — نحو {n} محادثة مع المساعد.',
    feat_ai_credits: '{n} رصيد ذكاء اصطناعي شهريًا',
    billing_synced: 'تم تحديث الاشتراك من سترايب',
    sub_active: 'مُشترك',
    sub_confirmed: 'اشتراكك فعّال',
    sub_trial_then: 'مجاني حتى {date}، ثم يُحتسب شهريًا. يمكنك الإلغاء في أي وقت.',
    sub_card_on_file: 'يُحتسب شهريًا. يمكنك الإلغاء في أي وقت من بوابة الفوترة.',
    billing_ends_on: 'ينتهي في {date}',
    billing_already_paid: 'دفعت بالفعل؟ حدّث من سترايب',
    set_group_company: 'الشركة',
    set_group_workspace: 'مساحة العمل',
    set_group_billing: 'الاشتراك',
    set_group_integrations: 'التكاملات',
    set_desc_company: 'الاسم القانوني والسجل التجاري والرقم الضريبي والعنوان',
    set_desc_tax: 'نسبة الضريبة والعملة والسنة المالية',
    set_desc_branding: 'الشعار والألوان على مستنداتك',
    set_desc_org: 'ملف الشركة والمنتجات المستخدمة',
    set_desc_users: 'الفريق والأدوار والدعوات والمقاعد',
    set_desc_activity: 'كل تغيير، ومن أجراه، وكيف',
    filters: 'الفلاتر',
    clear_all: 'مسح الكل',
    show_closed_accounts: 'إظهار المغلقة',
    hide_closed_accounts: 'إخفاء المغلقة',
    set_desc_memory: 'ما يتذكره المساعد عن هذه الشركة',
    set_desc_billing: 'الاشتراك والمقاعد والإضافات وأرصدة الذكاء الاصطناعي',
    set_desc_mcp: 'دع كلود يجيب من دفاتر هذه الشركة',
    set_desc_whatsapp: 'رقم الأعمال والتقارير اليومية',
    // ===== الموصّلات =====
    settings_connectors: 'الموصّلات',
    set_desc_connectors: 'انقل دفاترك من نظام آخر',
    conn_zoho_blurb: 'انقل العملاء والموردين والمنتجات والفواتير وفواتير الموردين والمصروفات والمدفوعات من زوهو بوكس، ويمكنك إبقاء الطرفين متطابقين.',
    conn_zoho_invoice_blurb:
      'انقل العملاء والمنتجات والفواتير وعروض الأسعار وإشعارات الدائن والمصروفات والمدفوعات من زوهو إنفويس. لا تُنقل الفواتير المتكررة ولا قوائم الأسعار: سجلّك محفوظ، لكن الفوترة المجدولة تتوقف يوم الانتقال.',
    conn_what_happens:
      'تختار ما يُسمح لريلت بفعله قبل انتقالك إلى زوهو، وتختار مجموعة الدفاتر قبل استيراد أي شيء. القراءة فقط هي الوضع الافتراضي، والمزامنة باتجاهين هي الخيار الوحيد الذي يكتب شيئًا في زوهو.',
    conn_not_configured: 'لم تُضبط بيانات زوهو على هذا الخادم بعد، لذلك الربط معطّل.',
    conn_connect: 'اربط زوهو بوكس',
    conn_disconnect: 'فصل الربط',
    conn_disconnect_confirm:
      'فصل زوهو بوكس؟ يُلغى الوصول لدى زوهو فورًا. ما استُورد يبقى، ويبقى سجله أيضًا، فلن يُستورد أي شيء مرتين عند إعادة الربط لاحقًا.',
    conn_disconnected: 'تم فصل زوهو بوكس',
    conn_needs_admin: 'المالك أو المدير فقط يمكنه ربط حساب أو بدء استيراد.',
    conn_authorised: 'تم التفويض من زوهو. اختر المنشأة التي سيتم الاستيراد منها.',
    conn_failed: 'تعذّر إتمام الربط',
    conn_organization: 'المنشأة',
    conn_not_chosen: 'لم تُختر بعد',
    conn_data_centre: 'مضيف الواجهة',
    conn_connected_at: 'تاريخ الربط',
    conn_last_run: 'آخر استيراد',
    conn_mode_title: 'ما الذي يُسمح لريلت بفعله؟',
    conn_mode_migration: 'انقل دفاتري مرة واحدة',
    conn_mode_migration_d:
      'ترحيل لمرة واحدة. يقرأ ريلت دفاترك في زوهو وينقلها إلى هنا ويخبرك بما وصل ثم يتوقف. لا يمكنه تغيير أي شيء في زوهو إطلاقًا.',
    conn_mode_read_only: 'استمر بالقراءة من زوهو',
    conn_mode_read_only_d:
      'يبقى زوهو مرجعك الرسمي، ويحتفظ ريلت بنسخة يمكنك تحديثها متى أردت أحدث البيانات. يقرأ ريلت دفاترك ولن يغيّرها أبدًا.',
    conn_mode_read_write: 'أبقِ الطرفين متطابقين',
    conn_mode_read_write_d:
      'مزامنة باتجاهين. يقرأ ريلت ما تغيّر في زوهو ثم يُرسل ما تغيّر هنا: العملاء والموردون والمنتجات في الاتجاهين، والمستندات التي أنشأتها في ريلت. أما المستند القادم من زوهو فلا يُعدَّل هناك أبدًا، وإذا تغيّر السجل نفسه في الطرفين فلن يُكتب شيء حتى تقرر أنت أيّهما الصحيح.',
    conn_mode_unavailable: 'غير متاح بعد',
    conn_mode_locked:
      'ما يُسمح لهذا الربط بفعله تحدّد لحظة موافقتك لدى زوهو، فلا يمكن تبديله من هنا. لتغييره افصل الربط ثم اربط من جديد واختر الخيار الآخر.',
    // ===== المسار السريع =====
    conn_updates: 'التحديثات',
    conn_webhook_live: 'يُخبرنا زوهو فور حدوث التغيير، ونتحقق كل ست ساعات أيضًا',
    conn_webhook_off: 'يُتحقق كل ست ساعات',
    conn_webhook_failed: 'يُتحقق كل ست ساعات. لم يقبل زوهو تفعيل التحديثات الفورية',
    conn_webhook_needs_consent: 'يُتحقق كل ست ساعات',
    conn_webhook_needs_consent_d:
      'التحديثات الفورية تحتاج صلاحية لم تكن موجودة حين أُنشئ هذا الربط. اربط من جديد إن أردتها؛ ولا يضيع شيء في الحالتين، فالتحقق كل ست ساعات هو ما يُبقي الطرفين متطابقين.',
    conn_pushed: 'أُرسل إلى زوهو',
    conn_conflicts_raised: 'اختلافات وُجدت',
    // ===== الاختلافات =====
    conn_conflicts: 'بانتظارك',
    conn_conflicts_why:
      'هذه السجلات تغيّرت في الطرفين، أو عُدّلت هنا بعد وصولها من زوهو. لم يُكتب شيء في أي من النظامين، وتوقفت مزامنة هذا السجل حتى تحدد أي نسخة هي الصحيحة.',
    conn_conflict_fields: 'الاختلاف في:',
    conn_conflict_settled: 'تم الحسم',
    conn_conflict_no_send:
      'هذا المستند جاء من زوهو، فلن يغيّر ريلت نسختهم مهما اخترت. الإبقاء على نسخة ريلت يعني أن النسختين ستبقيان مختلفتين من الآن.',
    conn_value_empty: '(فارغ)',
    conn_side_ours: 'في ريلت',
    conn_side_theirs: 'في زوهو',
    conn_keep_ours: 'أبقِ نسخة ريلت وأرسلها',
    conn_keep_ours_local: 'أبقِ نسخة ريلت هنا',
    conn_keep_theirs: 'استعِد نسخة زوهو',
    conn_entity_customer: 'عميل',
    conn_entity_vendor: 'مورّد',
    conn_entity_item: 'منتج',
    conn_entity_invoice: 'فاتورة',
    conn_entity_bill: 'فاتورة مورّد',
    conn_entity_expense: 'مصروف',
    conn_entity_payment: 'دفعة',
    conn_field_name: 'الاسم',
    conn_field_email: 'البريد',
    conn_field_phone: 'الهاتف',
    conn_field_vat_number: 'الرقم الضريبي',
    conn_field_address: 'العنوان',
    conn_field_active: 'نشط',
    conn_field_sku: 'رمز الصنف',
    conn_field_description: 'الوصف',
    conn_field_kind: 'النوع',
    conn_field_unit: 'الوحدة',
    conn_field_sale_price: 'سعر البيع',
    conn_field_purchase_price: 'سعر الشراء',
    conn_field_number: 'الرقم',
    conn_field_issue_date: 'التاريخ',
    conn_field_due_date: 'الاستحقاق',
    conn_field_currency: 'العملة',
    conn_field_subtotal: 'المجموع الفرعي',
    conn_field_discount: 'الخصم',
    conn_field_vat_amount: 'الضريبة',
    conn_field_total: 'الإجمالي',
    conn_field_state: 'الحالة',
    conn_field_notes: 'ملاحظات',
    conn_field_reference: 'المرجع',
    conn_field_date: 'التاريخ',
    conn_field_amount: 'المبلغ',
    conn_field_category: 'التصنيف',
    conn_field_method: 'طريقة الدفع',
    conn_field_applied_to: 'مطبقة على',
    conn_mode_label: 'هذا الربط يستطيع',
    conn_mode_change_note:
      'هذا يحدد بالضبط ما سيُطلب من زوهو السماح به، لذلك يُختار الآن لا لاحقًا. تغييره يعني إعادة الربط من جديد.',
    conn_refresh_from_zoho: 'حدّث من زوهو',
    conn_status_migrated: 'تم الترحيل',
    conn_migrated_done: 'رُحّلت دفاترك بتاريخ',
    conn_migrated_safe_to_remove:
      'لن يُقرأ شيء آخر من زوهو. يمكنك إزالة هذا الربط الآن: كل ما وصل يبقى هنا، ويتذكر ريلت ما نقله فلن يُستورد أي شيء مرتين عند إعادة الربط لاحقًا.',
    conn_status_pending_org: 'اختر منشأة',
    conn_status_connected: 'مربوط',
    conn_status_error: 'يحتاج انتباهًا',
    conn_status_disconnected: 'مفصول',
    conn_pick_org: 'أي مجموعة دفاتر؟',
    conn_pick_org_why: 'حساب زوهو الواحد قد يملك عدة منشآت. اختر التي تخص مساحة العمل هذه.',
    conn_org_default: 'الافتراضية',
    conn_use_org: 'استورد من هذه',
    conn_org_chosen: 'تم اختيار المنشأة. يمكنك بدء الاستيراد الآن.',
    conn_import: 'ابدأ الاستيراد',
    conn_importing: 'جارٍ الاستيراد…',
    conn_import_queued: 'أُدرج الاستيراد في الطابور. يكمل في الخلفية، ويمكنك إغلاق هذه الصفحة.',
    conn_progress: 'الاستيراد',
    conn_progress_live: 'ما وصل حتى الآن.',
    conn_progress_last: 'ما جلبه آخر استيراد.',
    conn_run_queued: 'في الطابور',
    conn_run_running: 'يعمل',
    conn_run_paused: 'بانتظار الحصة',
    conn_run_done: 'اكتمل',
    conn_run_failed: 'فشل',
    conn_stage_accounts: 'الحسابات',
    conn_stage_contacts: 'جهات التعامل',
    conn_stage_items: 'المنتجات',
    conn_stage_invoices: 'الفواتير',
    conn_stage_bills: 'فواتير الموردين',
    conn_stage_expenses: 'المصروفات',
    conn_stage_payments: 'المدفوعات',
    conn_stage_estimates: 'عروض الأسعار',
    conn_stage_credit_notes: 'الإشعارات الدائنة',
    conn_stage_journals: 'قيود اليومية',
    /* ── شريط الاستيراد. يَعُدّ المراحل لا السجلات، لأن زوهو لا يذكر العدد
       الكلي قبل قراءة كل الصفحات. */
    conn_progress_label: 'تقدّم الاستيراد',
    conn_now_reading: 'يقرأ الآن',
    conn_now_sending: 'يرسل تغييراتكم إلى زوهو',
    conn_waiting_at: 'متوقّف مؤقتًا عند',
    conn_stopped_at: 'توقّف عند',
    conn_all_stages_done: 'اكتملت كل المراحل.',
    conn_so_far: '{n} حتى الآن',
    conn_step_of: 'المرحلة {n} من {total}',
    conn_queued_nothing: 'في قائمة الانتظار. لم يصل شيء بعد.',
    conn_queued_hint: 'يبدأ تلقائيًا خلال دقيقة عادةً، ويكمل في الخلفية.',
    conn_why_no_total:
      'زوهو لا يذكر عدد السجلات قبل قراءتها كاملةً، لذلك يَعُدّ هذا الشريط المراحل لا السجلات.',
    conn_stage_not_reached: 'لم يصل إليها بعد',
    conn_stage_absent: 'غير موجودة في هذا المنتج',
    conn_stages_absent: 'لا يحتوي {product} على هذه العناصر، لذلك لم يُبحث عنها:',
    conn_stage_absent_hint: 'لا يحتوي {product} على سجلات من هذا النوع، لذلك لا تعمل هذه المرحلة أبداً.',
    conn_zoho_books: 'زوهو بوكس',
    conn_zoho_invoice: 'زوهو إنفويس',
    conn_product_title: 'أي منتج من زوهو تستخدم؟',
    conn_product_zoho_books_d:
      'محاسبة كاملة. تسجّل الدخول عبر books.zoho.com وتجد في القائمة قسم البنوك أو المحاسب.',
    conn_product_zoho_invoice_d:
      'الفوترة والمصروفات. تسجّل الدخول عبر invoice.zoho.com ولا يوجد قسم للبنوك أو المحاسب.',
    conn_product_hint:
      'هذا يحدد الصلاحيات التي يطلب زوهو الموافقة عليها، ولا يمكن تغييره لاحقاً إلا بالاتصال من جديد.',
    conn_product_absent: 'لا يحتوي {product} على {stages}، لذلك لن تعمل تلك المراحل.',
    conn_already_there: 'موجود مسبقًا',
    conn_api_calls: 'طلبات واجهة زوهو المستهلكة',
    conn_started: 'البداية',
    conn_finished: 'الانتهاء',
    conn_paused_quota:
      'يحدّ زوهو عدد الطلبات المسموح بها لخطتك في اليوم، وقد استُهلكت. يكمل الاستيراد من حيث توقف تلقائيًا بعد',
    conn_ledger_note_migration:
      'دفاترك تُنقل، فتأتي المستندات المستوردة بقيودها معها وتظهر التقارير هنا كما تظهر في النظام الذي تغادره. ويحدث هذا ما دام الدفتر فارغًا فقط: أما مساحة عمل تعمل هنا منذ مدة فتحتفظ بما قيّده محاسبك.',
    conn_no_ledger_note:
      'هذا الربط يعكس زوهو ولا يكتب في دفترك. تصل المستندات ولا تصل القيود، فتبقى أرصدتك الافتتاحية قرارًا تتخذه مرة واحدة مع محاسبك.',
    // ===== تعدد الشركات =====
    addon_mcp: 'الذكاء الاصطناعي وMCP',
    addon_mcp_d: 'اربط كلود أو ChatGPT بمساحة العمل هذه.',
    your_companies: 'شركاتك',
    new_company: 'شركة جديدة',
    create_company: 'إنشاء الشركة',
    new_company_hint: 'مساحة عمل منفصلة ببياناتها وفريقها واشتراكها. ستكون مالكها.',
    new_company_trial: 'تبدأ بتجربة مجانية 14 يومًا. بدون بطاقة.',
    org_created: '{name} جاهزة',
    country: 'الدولة',
    country_bh: 'البحرين',
    country_sa: 'السعودية',
    country_ae: 'الإمارات',
    country_kw: 'الكويت',
    country_qa: 'قطر',
    country_om: 'عُمان',
    seats_line_note: '{n} مستخدم مدفوع · {price} لكل مستخدم بعد الأول',
    module_always_on: 'دائم التفعيل — بقية النظام مبني عليه',
    current_plan: 'الحالي',
    addon_email: 'البريد الإلكتروني',
    plan_sub: 'كل ما يقدّمه وي هاي، للشركة كاملة.',
    plan_includes_seats: 'يشمل {n} مستخدم',
    per_user_month: '/ مستخدم / شهريًا',
    seats_sub: 'كم يكلّف كل شخص بعد الأول.',
    seats_in_use: '{n} مستخدم مدفوع حاليًا',
    seats_included: 'مشمول في الاشتراك',
    seats_extra: 'مستخدمون إضافيون',
    seats_cost: 'يُضاف للفاتورة',
    manage_people: 'إدارة الفريق',
    owner_only: 'المالك وحده يستطيع تعديل الاشتراك.',
    included_in_plan: 'مشمول في الاشتراك',
    trial_no_card: 'لا توجد بطاقة مسجّلة بعد',
    email_unlimited: 'بدون حد للإرسال',
    addon_used: 'المستخدم هذا الشهر',
    addon_remaining: 'المتبقي',
    addon_sub_whatsapp: 'رقم واتساب خاص بك، يجيب من دفتر الحسابات.',
    addon_sub_email: 'الفواتير وعروض الأسعار والتذكيرات والتقارير، تُرسل من وي هاي.',
    feat_all_products: 'ERP والمالية والعملاء والموارد البشرية والمخزون ونقاط البيع والذكاء الاصطناعي والنماذج',
    feat_viewers_free: 'المشاهدون مجانًا — القراءة فقط بلا تكلفة',
    feat_mcp: 'خادم MCP لكلود وChatGPT',
    feat_email: 'إرسال بريد بلا حدود',
    feat_seat_prorated: 'يُضاف ويُحذف خلال الشهر بحساب تناسبي',
    feat_wa_digest: 'ملخص يومي أو أسبوعي في الوقت الذي تختاره',
    feat_wa_reminders: 'تذكيرات دفع تتابع الفاتورة',
    feat_wa_arabic: 'إجابات بالعربية أو الإنجليزية',
    feat_email_docs: 'مرتبطة بالمستند الذي تخصّه',
    feat_email_log: 'كل رسالة مسجّلة وقابلة لإعادة الإرسال',

    /* ───────────────────────────────────────────────────────────────────────
       Phase 2B · People & partners — customers, vendors, employees, payroll,
       subscriptions, contracts. Append-only block; keep en/ar in sync.
       ─────────────────────────────────────────────────────────────────────── */
    customers_subtitle: 'الشركات والأشخاص الذين تصدر لهم فواتير.',
    vendors_subtitle: 'المورّدون الذين تشتري منهم وتدفع لهم.',
    employees_subtitle: 'فريقك ورواتبهم وعقودهم.',
    /* ───────────────────────────────────────────────────────────────────────
       HR · Leave. Append-only block; keep en/ar in sync.
       ─────────────────────────────────────────────────────────────────────── */
    leave: 'الإجازات',
    my_leave: 'إجازاتي',
    leave_subtitle: 'الطلبات التي تنتظر ردّك، وكل ما تمّت الموافقة عليه.',
    leave_balances: 'الأرصدة',
    leave_balances_subtitle: 'رصيد كل موظف والشروط التي يقوم عليها.',
    my_leave_subtitle: 'رصيدك المتبقّي وكيف تطلب إجازة.',
    leave_tab_requests: 'الطلبات',
    leave_tab_balances: 'الأرصدة',
    leave_tab_week: 'هذا الأسبوع',
    leave_new_request: 'تسجيل إجازة',
    leave_submit_request: 'حفظ',
    my_leave_request: 'طلب إجازة',
    leave_kind: 'نوع الإجازة',
    leave_from: 'أول يوم',
    leave_to: 'آخر يوم',
    leave_half_day: 'نصف يوم',
    leave_which_half: 'أي نصف',
    leave_morning: 'صباحًا',
    leave_afternoon: 'مساءً',
    leave_reason: 'السبب',
    leave_reason_required: 'هذا النوع من الإجازات يحتاج سببًا مكتوبًا.',
    leave_reason_required_hint: 'اذكر السبب باختصار, هذا النوع يطلبه.',
    leave_days_count: 'يوم',
    leave_days_left: 'يوم متبقٍّ',
    leave_would_leave: 'يتبقّى',
    leave_not_enough_days: 'أكثر من الرصيد',
    leave_range_inverted: 'آخر يوم لا يمكن أن يسبق أول يوم.',
    leave_approve_now: 'متّفق عليها مسبقًا',
    leave_approve_now_hint: 'تُسجَّل كمعتمدة. أزل العلامة لإرسالها ليردّ عليها شخص آخر.',
    leave_pick_employee: 'اختر موظفًا',
    leave_everyone: 'الجميع',
    leave_awaiting_you: 'بانتظار ردّك',
    leave_awaiting: 'بانتظار الاعتماد',
    leave_approve: 'اعتماد',
    leave_decline: 'رفض',
    leave_cancel_request: 'إلغاء',
    leave_withdraw: 'سحب الطلب',
    leave_withdraw_title: 'سحب هذا الطلب؟',
    leave_decline_title: 'رفض هذا الطلب؟',
    leave_cancel_title: 'إلغاء هذه الإجازة؟',
    leave_nothing_waiting: 'لا شيء بانتظارك',
    leave_nothing_waiting_hint: 'تمّ الردّ على كل الطلبات. غيّر الفلتر لرؤية البقية.',
    leave_show_all: 'عرض الكل',
    leave_status_pending: 'بالانتظار',
    leave_status_approved: 'معتمدة',
    leave_status_rejected: 'مرفوضة',
    leave_status_cancelled: 'ملغاة',
    leave_bucket_annual: 'سنوية',
    leave_bucket_sick: 'مرضية',
    leave_bucket_unpaid: 'بدون راتب',
    leave_bucket_other: 'أخرى',
    leave_other_tooltip: 'تُحتسب وتُعرض، ولا تُخصم من أي رصيد.',
    leave_unlimited: 'غير محدودة',
    leave_custom: 'مخصّص',
    leave_carried_until: 'مرحّلة، حتى',
    leave_year: 'سنة الإجازات',
    leave_your_year: 'سنة إجازاتك تبدأ',
    leave_to_date: 'إلى',
    leave_left: 'متبقٍّ',
    leave_adjust: 'الشروط',
    leave_terms_for: 'شروط إجازات',
    leave_policy: 'اللائحة',
    leave_policy_default: 'اللائحة الافتراضية',
    leave_just_for_them: 'لهذا الموظف وحده',
    leave_override_hint: 'اترك الحقل فارغًا ليتبع اللائحة.',
    leave_inherit: 'مثل اللائحة',
    leave_year_turns_over: 'تبدأ سنة الإجازات',
    leave_reset_anniversary: 'في تاريخ التعيين',
    leave_reset_calendar: 'في أول يناير',
    leave_reset_manual: 'يدويًا',
    leave_current_year: 'سنة الإجازات الحالية',
    leave_reset_now: 'ترحيلها إلى اليوم',
    leave_undo_reset: 'تراجع',
    leave_reset_only_manual: 'سنة الإجازات تتجدّد تلقائيًا، فلا شيء لترحيله.',
    leave_save_first: 'احفظ تعديلاتك أولًا.',
    leave_last_reset: 'آخر ترحيل',
    leave_undo_window: 'يمكن التراجع خلال ٢٤ ساعة',
    leave_this_week: 'هذا الأسبوع',
    leave_this_week_short: 'هذا الأسبوع',
    leave_this_week_subtitle: 'من في الدوام ومن هو غائب.',
    leave_previous_week: 'الأسبوع السابق',
    leave_next_week: 'الأسبوع التالي',
    leave_cell_leave: 'إجازة',
    leave_cell_half_leave: 'نصف إجازة',
    leave_cell_present: 'حاضر',
    leave_cell_absent: 'غائب',
    leave_cell_half_day: 'نصف يوم',
    leave_cell_remote: 'عن بُعد',
    leave_cell_off: 'راحة',
    my_leave_history: 'طلباتك',
    my_leave_none: 'لم تطلب أي إجازة بعد',
    my_leave_none_hint: 'سجّل يومًا وسيظهر هنا مع الردّ عليه.',
    my_leave_no_record: 'لست ضمن كشف الرواتب هنا',
    my_leave_no_record_hint: 'حسابك غير مرتبط بسجل موظف، فلا توجد إجازات لعرضها. اطلب من مسؤول الموارد البشرية ربطه.',
    settings_leave: 'الإجازات',
    set_desc_leave: 'أنواع الإجازات والاستحقاقات ومن يحصل على ماذا',
    leave_types: 'أنواع الإجازات',
    leave_types_hint: 'يمكنك إعادة تسمية أي منها. أما الرصيد الذي تُخصم منه فيثبت بعد أول استخدام.',
    leave_type_unpaid: 'بدون راتب',
    leave_type_needs_note: 'تحتاج سببًا',
    leave_switch_off: 'إيقاف',
    leave_switch_on: 'تفعيل',
    leave_policies: 'اللوائح',
    leave_policies_hint: 'مجموعة قواعد يشترك فيها عدة موظفين. الشروط الخاصة بموظف واحد تُضاف فوقها من شاشة الأرصدة.',
    leave_new_policy: 'لائحة جديدة',
    leave_edit_policy: 'تعديل اللائحة',
    leave_policy_is_default: 'الافتراضية',
    leave_on_this_policy: 'على هذه اللائحة',
    leave_days_a_year: 'يوم سنويًا',
    leave_days_a_month: 'يوم شهريًا',
    leave_carries: 'ترحيل حتى',
    leave_annual_mode: 'الإجازة السنوية',
    leave_mode_fixed: 'عدد ثابت كل سنة',
    leave_mode_accrual: 'تُكتسب شهرًا بشهر',
    leave_annual_days: 'أيام في السنة',
    leave_days_per_month: 'أيام في الشهر',
    leave_carryover_cap: 'الترحيل حتى',
    leave_carryover_expiry: 'تنتهي الأيام المرحّلة بعد (أشهر)',
    leave_carryover_expiry_hint: 'صفر يعني أنها لا تنتهي أبدًا.',
    leave_sick_mode: 'الإجازة المرضية',
    leave_sick_days: 'أيام مرضية في السنة',
    leave_prorate: 'احتساب نسبي للسنة الأولى',
    leave_prorate_hint: 'من يبدأ في يوليو يحصل على نصف أيام السنة لا كلها.',
    leave_assign: 'إسناد',
    leave_assign_to: 'إسناد موظفين إلى',
    payroll_subtitle: 'دورات الرواتب وقسائمها ومدفوعاتها.',
    subscriptions_subtitle: 'الخدمات المتكررة وتكلفتها عليك.',
    contracts_subtitle: 'الاتفاقيات والتواقيع ومواعيد التجديد.',
    all_kinds: 'كل الأنواع',
    all_years: 'كل السنوات',
    clear_filters: 'مسح الفلاتر',
    no_results: 'لا شيء يطابق هذه الفلاتر',
    no_results_hint: 'جرّب بحثًا مختلفًا أو امسح الفلاتر.',
    no_customers_yet: 'لا يوجد عملاء بعد',
    no_customers_hint: 'أضف أول شركة تصدر لها فاتورة وستظهر هنا.',
    no_vendors_yet: 'لا يوجد موردون بعد',
    no_vendors_hint: 'أضف موردًا لتبدأ بتسجيل الفواتير والمصروفات عليه.',
    no_employees_yet: 'لا يوجد موظفون بعد',
    no_employees_hint: 'أضف أول عضو في الفريق لتشغيل الرواتب وإصدار العقود.',
    no_payroll_runs_yet: 'لا توجد دورات رواتب بعد',
    no_payroll_runs_hint: 'أنشئ دورة لتوليد قسائم الرواتب للفترة التي تختارها.',
    no_subscriptions_yet: 'لا توجد اشتراكات بعد',
    no_subscriptions_hint: 'تابع خدمة متكررة لترى تكلفتها وموعد الخصم القادم.',
    no_contracts_yet: 'لا توجد عقود بعد',
    no_contracts_hint: 'حرّر عقدًا ووقّعه إلكترونيًا واحتفظ بنسخة PDF هنا.',
    basic_info: 'الأساسيات',
    contact_details: 'بيانات التواصل',
    tax_registration: 'الضريبة والسجل',
    job_details: 'الوظيفة',
    compensation: 'الراتب',
    billing_schedule: 'جدول الخصم',
    posting_accounts: 'الترحيل',
    commercial_terms: 'القيمة والمدة',
    contract_body: 'نص العقد',
    esign_title: 'التوقيعات الإلكترونية',
    esign_subtitle: 'يوقّع الطرفان هنا، وتُطبع التواقيع في ملف PDF.',
    template_hint: 'ابدأ من قالب محفوظ ثم عدّل النص أدناه.',
    run_status_posted: 'مُرحّلة',
    run_stage_draft: 'لا تزال المبالغ قابلة للتعديل.',
    run_stage_posted: 'مُرحّلة إلى الدفاتر، بانتظار الصرف.',
    run_stage_paid: 'مصروفة من الحساب البنكي.',
    pending_edits_hint: 'أنهِ تعديل الاستقطاعات قبل الترحيل.',
    employee_count: 'الموظفون',
    back: 'رجوع',

    // ===== Phase 2A · صفحات المستندات (الفواتير، عروض الأسعار، فواتير الموردين، المصروفات، المدفوعات) =====
    line_items: 'البنود',
    more_actions: 'إجراءات أخرى',
    filter_search: 'بحث',
    invoices_sub: 'ما أصدرته من فواتير، وما لا يزال مستحقًا لك.',
    credit_notes: 'الإشعارات الدائنة',
    credit_notes_sub: 'ما أعدته للعملاء، وما لا يزال بحوزتهم كرصيد غير مستخدم.',
    credit_note: 'إشعار دائن',
    credit_note_details: 'تفاصيل الإشعار الدائن',
    credit_note_summary: 'الملخص',
    new_credit_note: 'إشعار دائن جديد',
    edit_credit_note: 'تعديل الإشعار الدائن',
    credit_reason: 'السبب',
    credit_reason_hint: 'يُطبع على المستند. الإشعار الدائن الضريبي يجب أن يذكر السبب.',
    credit_unused: 'الرصيد الدائن غير المستخدم',
    credit_applied: 'المطبَّق',
    credit_applied_to: 'مطبَّق على',
    credit_applied_none: 'لم يُطبّق على أي فاتورة بعد. يحتفظ به العميل كرصيد دائن.',
    credit_applied_ok: 'تم تطبيق الرصيد الدائن.',
    credit_taken_back: 'أُزيل الرصيد الدائن عن تلك الفاتورة.',
    take_credit_back: 'إزالة الرصيد الدائن',
    total_credited: 'إجمالي المبلغ الدائن',
    apply_to_invoice: 'تطبيق على فاتورة',
    select_invoice: 'اختر فاتورة',
    issue_: 'إصدار',
    credit_note_issued: 'صدر الإشعار الدائن.',
    status_closed: 'مقفلة',
    confirm_delete_credit_note: 'حذف مسودة الإشعار الدائن؟ لم يُرحّل شيء، فلا شيء يُعكس.',
    confirm_void_credit_note: 'إلغاء هذا الإشعار الدائن؟ يُعكس قيده، وتعود كل فاتورة طُبّق عليها مستحقة بذلك المبلغ.',
    credit_exceeds_invoice_balance: 'المبلغ أكبر مما تبقّى على الفاتورة.',
    credit_exceeds_remaining: 'المبلغ أكبر مما تبقّى من هذا الإشعار الدائن.',
    credit_customer_mismatch: 'لا يُطبّق الإشعار الدائن إلا على فواتير العميل نفسه.',
    credit_currency_mismatch: 'الإشعار الدائن والفاتورة بعملتين مختلفتين.',
    invoice_not_creditable: 'لا يمكن تطبيق رصيد دائن على هذه الفاتورة في حالتها الحالية.',
    invoice_has_credit: 'طُبّق إشعار دائن على هذه الفاتورة. أزل الرصيد الدائن أولًا.',
    credit_note_not_applicable: 'لم يتبقَّ في هذا الإشعار الدائن ما يمكن تطبيقه.',
    quotations_sub: 'العروض المرسلة للعملاء، وما آلت إليه.',
    bills_sub: 'ما أصدره الموردون عليك، وما لا يزال مستحقًا.',
    expenses_sub: 'المصروفات النقدية والبطاقات، من المطالبة حتى الترحيل.',
    payments_sub: 'كل حركة قبض وصرف، والمستند الذي سدّدته.',
    direction: 'الاتجاه',
    inbound: 'وارد',
    outbound: 'صادر',
    all_directions: 'كل الاتجاهات',
    inbound_payment: 'دفعة واردة',
    outbound_payment: 'دفعة صادرة',
    linked_to: 'مرتبطة بـ',
    no_linked_doc: 'لا يوجد مستند مرتبط',
    reference: 'المرجع',
    payslip: 'قسيمة راتب',
    created_at: 'أُنشئت',
    no_payments_sub: 'سدّد فاتورة أو فاتورة مورد أو دورة رواتب لتظهر هنا.',
    total_inbound: 'إجمالي الوارد',
    total_outbound: 'إجمالي الصادر',
    net_flow: 'الصافي',
    /* «تصحيح» وليس «حذف». نص التذكرة كان «حذف / عكس الدفع»، وكلمتان لزرّ واحد
       اعترافٌ بأن الأولى غير صحيحة: لا شيء يُحذف هنا. */
    correct_payment: 'تصحيح هذه الدفعة',
    correct_payment_lede:
      'لا يُحذف شيء. يبقى السند برقمه وفي مكانه من التسلسل، ويذكر ما جرى له. اختر ما حدث فعلًا.',
    correction_reverse: 'لم تحدث أصلًا',
    correction_reverse_help:
      'مكرّرة، أو على الفاتورة الخطأ، أو حوالة لم تصل. يُعكس القيد في فترته نفسها ويعود المستند إلى ما كان عليه. لا تتحرك أي أموال.',
    correction_refund: 'أعدنا المبلغ إليهم',
    correction_refund_help:
      'يخرج المبلغ من البنك ويصل إلى العميل، فيظهر في كشف الحساب البنكي. لا تستخدمها إلا إذا أُعيد المبلغ فعلًا.',
    correction_void: 'إلغاء المستند',
    correction_void_help:
      'السند لم يعد عليه، والمستند نفسه ما كان ينبغي أن يوجد. يبقى برقمه، ويُعكس قيده.',
    correction_reason: 'السبب',
    correction_reason_ph: 'سُجّلت مرتين: نفس الحوالة أُدخلت الأحد ثم الاثنين.',
    correction_reason_short: 'ما لا يقل عن {n} أحرف. يُحفظ على السند ويقرأه من يراجع الدفاتر.',
    correction_confirm: 'راجعتُ ذلك، وأعلم أنه يُقيَّد في الدفاتر.',
    correction_submit: 'جهّز هذا التصحيح',
    correction_none: 'لا يمكن تصحيح شيء من هنا',
    correction_staged: 'جاهز. لم يتغيّر شيء بعد: اقرأه بالأسفل ثم طبّقه.',
    correction_pending: 'تصحيح بانتظار التطبيق',
    correction_pending_sub: 'لم يُكتب شيء بعد. اقرأه ثم طبّقه أو تجاهله.',
    correction_apply: 'طبّق التصحيح',
    correction_discard: 'تجاهل',
    correction_applied: 'طُبّق.',
    correction_discarded: 'أُهمل. لم يتغيّر شيء.',
    correction_expires: 'ينتهي {time}',
    correction_not_yours: 'لا يطبّقه إلا من طلبه.',
    /* ───────────────────────────────────────────────────────────────────────
       المرحلة 2C · التقارير والمحاسبة والمكوّنات المشتركة. كتلة إضافية فقط؛
       أبقِ الإنجليزية والعربية متطابقتين.
       ─────────────────────────────────────────────────────────────────────── */
    stmt_total_revenue: 'إجمالي الإيرادات',
    stmt_total_expenses: 'إجمالي المصروفات',
    stmt_total_assets: 'إجمالي الأصول',
    stmt_total_liabilities: 'إجمالي الخصوم',
    stmt_total_equity: 'إجمالي حقوق الملكية',
    stmt_liabilities_and_equity: 'الخصوم وحقوق الملكية',
    stmt_net_margin: 'هامش الربح',
    stmt_net_movement: 'صافي الحركة',
    stmt_no_revenue: 'لا توجد إيرادات في هذه الفترة',
    stmt_no_expenses: 'لا توجد مصروفات في هذه الفترة',
    stmt_no_movement: 'لا توجد حركة في هذه الفترة',
    stmt_no_balances: 'لا توجد حسابات ذات رصيد',
    stmt_invoice_count: 'فاتورة',
    aging_by_bucket: 'المتبقي حسب العمر',
    aging_total_outstanding: 'إجمالي المتبقي',
    aging_nothing_outstanding: 'لا يوجد متبقٍّ',
    aging_all_settled: 'كل الفواتير المستحقة حتى هذا التاريخ مسدّدة.',
    days_overdue: 'يوم تأخير',
    coa_no_accounts: 'لا توجد حسابات من هذا النوع بعد',
    coa_expand: 'توسيع',
    coa_collapse: 'طي',
    journal_sub: 'كل قيد مُرحّل، ومصدره.',
    bank_accounts_sub: 'أين تُحفظ الأموال، ورصيد كل حساب.',
    total_in_base: 'الإجمالي، حسابات الدينار',
    total_excludes: 'غير محتسب، ولا يُطبَّق سعر صرف هنا:',
    upload: 'رفع',
    preview: 'معاينة',
    preview_unavailable: 'المعاينة غير متاحة لهذا النوع من الملفات.',
    close: 'إغلاق',
    aws_live_cost: 'تكلفة AWS حتى تاريخه',
    aws_not_configured: 'لم تُضبط مفاتيح فوترة AWS',
    edit_signature: 'تعديل التوقيع',

    /* ===== APPENDED — dashboard bands + post-checkout confirmation ===== */
    outstanding: 'المستحقات',
    performance: 'الأداء',
    activity: 'النشاط',
    checkout_confirming: 'جارٍ تأكيد اشتراكك…',
    checkout_confirming_sub: 'نستقبل التفاصيل من سترايب الآن، ولن يستغرق الأمر سوى لحظات.',
    checkout_done_title: 'أنت الآن على باقة {plan}',
    checkout_trial_line: 'مجاني حتى {date}، ثم {price} لكل {interval}.',
    checkout_renew_line: '{price} لكل {interval}. الخصم القادم في {date}.',
    checkout_charged_line: '{price} لكل {interval}، ابتداءً من اليوم.',
    checkout_first_month: 'شهرك الأول بـ {first}، ثم {price} شهريًا.',
    checkout_included: 'يشمل {seats} مستخدم · {credits} رصيد ذكاء اصطناعي شهريًا',
    checkout_cancel_note:
      'يمكنك الإلغاء في أي وقت من هذه الصفحة، وتبقى الخدمة فعّالة حتى نهاية المدة المدفوعة.',
    checkout_ack: 'تمام',
    checkout_pending_title: 'تم استلام الدفعة',
    checkout_pending_body:
      'وصلت دفعتك إلى سترايب. ستتحوّل باقتك خلال دقيقة — أعد تحميل الصفحة إن لم يحدث ذلك.',
    interval_month: 'شهر',
    interval_year: 'سنة',

    /* ===== APPENDED — تصميم المستندات (الإعدادات ← الهوية) =====
       كل ما يمكن للمنشأة تغييره في شكل ملفات PDF. كتلة إضافية فقط؛
       أبقِها مطابقة تمامًا للكتلة الإنجليزية أعلاه. */
    doc_design: 'تصميم المستندات',
    doc_design_sub: 'شكل ملفات PDF التي يستلمها عملاؤك. تُطبَّق التغييرات على كل مستند من الآن فصاعدًا.',
    doc_brand: 'الهوية',
    doc_logo: 'الشعار',
    doc_logo_hint: 'PNG أو JPEG أو WebP أو SVG. حتى 2 ميغابايت. يُطبع بالارتفاع الذي تختاره دون تمديد.',
    doc_logo_upload: 'رفع الشعار',
    doc_logo_replace: 'استبدال',
    doc_logo_remove: 'إزالة',
    doc_logo_none: 'لا يوجد شعار بعد — تستخدم المستندات اسم شركتك كعلامة نصية.',
    doc_logo_size: 'ارتفاع الطباعة',
    doc_logo_uploaded: 'تم تحديث الشعار',
    doc_logo_removed: 'تمت إزالة الشعار',
    doc_accent: 'اللون المميز',
    doc_accent_hint: 'يُستخدم لرقم المستند والإجماليات والخطوط.',
    doc_accent_inherit: 'استخدام لون هوية مساحة العمل',
    doc_header_style: 'شكل الترويسة',
    doc_header_rule: 'خط باللون المميز',
    doc_header_fill: 'شريط ملوّن',
    doc_header_fill_hint: 'الترويسة الملوّنة تحتاج شعارًا يظهر بوضوح فوق اللون.',
    doc_layout: 'التخطيط',
    doc_layout_classic: 'كلاسيكي',
    doc_layout_classic_desc: 'خطوط رفيعة وبطاقات محدّدة وتذييل داكن.',
    doc_layout_modern: 'حديث',
    doc_layout_modern_desc: 'اللون المميز يقود الصفحة. بطاقات ناعمة بلا حدود.',
    doc_layout_minimal: 'مبسّط',
    doc_layout_minimal_desc: 'بلا تعبئة. خطوط رفيعة وحجم صغير ومساحات واسعة.',
    doc_page: 'الصفحة',
    doc_language: 'لغة المستند',
    doc_language_en: 'الإنجليزية',
    doc_language_ar: 'العربية',
    doc_language_both: 'الاثنتان معًا',
    doc_language_hint: 'المستندات ثنائية اللغة تُبقي كل الأرقام من اليسار إلى اليمين، فلا تتحرك أعمدة المبالغ.',
    doc_paper: 'مقاس الورق',
    doc_paper_a4: 'A4',
    doc_paper_letter: 'Letter',
    doc_margin: 'الهوامش',
    doc_margin_hint: 'المسافة البيضاء حول الصفحة بالمليمتر.',
    doc_contents: 'ما يظهر على المستند',
    doc_show_cr: 'رقم السجل التجاري',
    doc_show_vat: 'الرقم الضريبي',
    doc_show_address: 'العنوان',
    doc_show_phone: 'الهاتف',
    doc_show_email: 'البريد الإلكتروني',
    doc_show_website: 'الموقع الإلكتروني',
    doc_website: 'عنوان الموقع',
    doc_show_iban: 'بيانات البنك والآيبان',
    doc_show_payment_terms: 'الشروط والملاحظات',
    doc_show_signature: 'سطر التوقيع',
    doc_show_watermark: 'ختم مسودة / مدفوعة',
    doc_show_watermark_hint: 'يضع ختمًا مائلًا على كل صفحة من المستندات غير النهائية والمسدّدة.',
    doc_text: 'النصوص',
    doc_terms_en: 'الشروط (بالإنجليزية)',
    doc_terms_ar: 'الشروط (بالعربية)',
    doc_terms_hint: 'تُطبع في خانة الاعتماد. اتركها فارغة لاستخدام الصيغة الافتراضية.',
    doc_footer_note_en: 'ملاحظة التذييل (بالإنجليزية)',
    doc_footer_note_ar: 'ملاحظة التذييل (بالعربية)',
    doc_footer_note_hint: 'سطر قصير واحد يتكرر أسفل كل صفحة.',
    doc_preview: 'معاينة حيّة',
    doc_preview_sub: 'القالب الحقيقي ببيانات تجريبية.',
    doc_preview_doc: 'مستند المعاينة',
    doc_preview_failed: 'تعذّر إنشاء المعاينة.',
    doc_preview_retry: 'إعادة المحاولة',
    doc_preview_open_pdf: 'فتح كملف PDF',
    doc_reset: 'استعادة الإعدادات الافتراضية',
    doc_reset_confirm: 'استعادة تصميم المستندات؟',
    doc_reset_body: 'ستعود كل إعدادات هذه الصفحة إلى الوضع الافتراضي، وسيُحذف الشعار المرفوع. المستندات المُرسلة سابقًا لا تتأثر.',
    doc_reset_done: 'تمت استعادة تصميم المستندات',
    doc_unsaved: 'تغييرات غير محفوظة',
    doc_invoice: 'فاتورة',
    doc_quote: 'عرض سعر',
    doc_bill: 'فاتورة مورّد',
    doc_payslip: 'قسيمة راتب',

    /* ===== APPENDED — إعادة بناء صفحة الاشتراك: الباقات والإضافات والرصيد ===== */
    plans_choose: 'اختر باقتك',
    plans_sub: 'غيّر باقتك متى شئت — يحتسب سترايب الفرق بالتناسب.',
    plan_current: 'باقتك الحالية',
    plan_switch_to: 'التحويل إليها',
    plan_upgrade_to: 'ترقية',
    plan_start_trial: 'ابدأ التجربة المجانية',
    interval_monthly: 'شهري',
    interval_yearly: 'سنوي',
    interval_yearly_note: 'شهران مجانًا',
    plan_billed_yearly: '{price} تُدفع مرة واحدة سنويًا',
    plan_seats_included: 'يشمل {n} مستخدمين',
    plan_seat_extra: 'ثم {price} لكل مستخدم إضافي',
    plan_ai_credits_month: '{n} رصيد ذكاء اصطناعي شهريًا',
    plan_whatsapp_included: 'واتساب مشمول',
    plan_no_ai: 'بلا رصيد ذكاء اصطناعي — خادم MCP فقط',
    plan_feat_mcp: 'خادم MCP لكلود وChatGPT',
    plan_feat_whatsapp: 'إمكانية إضافة مساعد واتساب',
    plan_feat_priority: 'دعم ذو أولوية',
    plan_feat_api: 'مفاتيح API لبرامجك الخاصة',
    offer_headline: 'خصم {percent}٪ على شهرك الأول',
    offer_sub: 'الشهر الأول بـ {price} بدل {full}. تُسجَّل البطاقة عند الدفع، ثم يتجدد بـ {full}.',
    offer_trial_first: 'أو جرّبه مجانًا {days} أيام أولًا — بلا بطاقة وبلا أي خصم.',
    offer_first_month: 'الشهر الأول {price}',
    offer_badge: 'وفّر {percent}٪',
    addons_title: 'الإضافات',
    addons_sub: 'خدمات إضافية يمكنك تفعيلها وإيقافها في أي وقت، وتُحتسب مع الاشتراك.',
    addon_brings: 'تشمل {messages} رسالة و{credits} رصيد ذكاء اصطناعي شهريًا.',
    addon_locked_on_plan: 'غير متاحة في باقة {plan}',
    addon_upgrade_to_add: 'متاحة ابتداءً من باقة {plan}',
    addon_on: 'مُفعّلة',
    credits_title: 'رصيد الذكاء الاصطناعي',
    credits_allowance: 'الرصيد الشهري',
    credits_left: 'بقي {left} من {quota}',
    credits_spent_this_period: 'استُهلك {n} هذا الشهر',
    credits_bought_balance: '{n} رصيد مُشترى — لا تنتهي صلاحيته',
    credits_exchanges: '≈ {n} محادثة مع المساعد',
    credits_burn: 'بمعدّل هذا الشهر يُستهلك نحو {n} يوميًا.',
    credits_days_cover: 'يكفي نحو {n} يومًا إضافيًا.',
    credits_runs_out: 'بمعدّل هذا الشهر سينفد الرصيد قبل نهاية الشهر.',
    credits_tier_title: 'ارفع الرصيد الشهري',
    credits_tier_sub: 'إضافة متكرّرة: تتجدّد مع الاشتراك ويُعاد ضبط الرصيد كل شهر.',
    credits_tier_included: 'المشمول في باقة {plan}',
    credits_tier_total: '{n} شهريًا',
    credits_tier_choose: 'اختيار',
    credits_tier_chosen: 'مختارة',
    credits_topup: 'شراء رصيد لمرة واحدة',
    credits_topup_sub: 'دفعة واحدة. يصل فورًا، ولا تنتهي صلاحيته، ولا يُستهلك إلا بعد نفاد رصيد الشهر.',
    credits_pack_credits: '{n} رصيد',
    credits_buy: 'شراء',
    credits_no_ai_plan: 'هذه الباقة بلا رصيد ذكاء اصطناعي — المساعد وواتساب والوكلاء مُعطّلون.',
    credits_no_ai_cta: 'الترقية إلى {plan}',
    topup_done_title: 'أُضيف الرصيد',
    topup_done_body: 'أُضيف {n} رصيد ذكاء اصطناعي إلى حسابك. لا تنتهي صلاحيته، ولا يُستهلك إلا بعد نفاد رصيد الشهر.',
    topup_pending_body: 'وصلت دفعتك إلى سترايب. سيصل الرصيد خلال دقيقة — أعد تحميل الصفحة إن لم يصل.',
    plan_change_title: 'التحويل إلى باقة {plan}',
    plan_change_new_price: 'السعر الجديد',
    plan_change_due_now: 'المستحق اليوم',
    plan_change_prorated: 'يحتسب سترايب فرق ما تبقّى من هذه الفترة بالتناسب.',
    plan_change_from_next: 'ومن الفاتورة القادمة تدفع {price} لكل {interval}.',
    plan_change_drops_addon: 'إضافة {addon} غير متاحة في هذه الباقة وسيتم إيقافها.',
    plan_change_confirm: 'تأكيد التحويل',
    plan_change_done: 'أنت الآن على باقة {plan}',
    billing_cancels_on: 'يُلغى في {date}',
    billing_what_you_pay: 'ما تدفعه',

    // ===== inventory: navigation and shells =====
    inventory: 'المخزون',
    products: 'المنتجات',
    products_sub: 'كل ما تبيعه من سلع وخدمات.',
    stock_levels: 'أرصدة المخزون',
    stock_levels_sub: 'ما لديك، وأين هو.',
    stock_movements: 'حركات المخزون',
    stock_movements_sub: 'كل وارد وصادر بالترتيب. لا يمكن تعديل هذا السجل.',
    stock_adjustments: 'تسويات المخزون',
    stock_adjustments_sub: 'الطريقة الوحيدة لتصحيح المخزون يدويًا، وكل عملية تترك سجلًا.',
    warehouses: 'المستودعات',
    warehouses_sub: 'أين يوجد المخزون فعليًا.',
    warehouse: 'المستودع',
    all_warehouses: 'كل المستودعات',
    new_warehouse: 'مستودع جديد',
    edit_warehouse: 'تعديل المستودع',
    warehouse_code: 'الرمز',
    warehouse_address: 'العنوان',
    close_warehouse: 'إغلاق المستودع',
    reopen_warehouse: 'إعادة الفتح',

    // ===== product =====
    product: 'المنتج',
    new_product: 'منتج جديد',
    edit_product: 'تعديل المنتج',
    product_details: 'تفاصيل المنتج',
    basics: 'الأساسيات',
    pricing: 'التسعير',
    kind_good: 'سلعة',
    kind_service: 'خدمة',
    kind_goods: 'السلع',
    kind_services: 'الخدمات',
    kind_good_hint: 'يُجرد ويُقيَّم ضمن المخزون.',
    kind_service_hint: 'يُباع بالوقت أو بالمهمة. لا شيء يُجرد.',
    sku: 'رمز الصنف',
    sku_hint: 'اتركه فارغًا ليُنشأ تلقائيًا.',
    unit: 'الوحدة',
    sale_price: 'سعر البيع',
    cost_hint: 'تُستخدم لتقييم المخزون وتسجيل تكلفة المبيعات.',
    cost_locked_hint: 'تُحتسب الآن من فواتير الشراء، والمتوسط أدناه هو قيمة المخزون.',
    average_cost: 'متوسط التكلفة',
    margin: 'هامش الربح',
    base_currency_hint: 'الأسعار بعملة الشركة الأساسية.',
    track_stock: 'تتبّع مخزون هذا المنتج',
    track_stock_hint: 'أوقفه لما تبيعه دون جرد، مثل كتيّب مطبوع.',
    track_stock_needs_module: 'فعّل منتج المخزون لتتمكن من جرد هذا الصنف.',
    opening_stock: 'الرصيد الافتتاحي للمخزون',
    opening_stock_hint: 'يُنشئ تسوية مخزون واحدة بتاريخ اليوم.',
    opening_unit_cost: 'تكلفة الوحدة الافتتاحية',
    income_account: 'حساب الإيراد',
    cogs_account: 'حساب تكلفة المبيعات',
    inventory_account: 'حساب المخزون',
    sold_last_90: 'المبيعات خلال آخر 90 يومًا',
    not_sold_yet: 'لم يُبَع بعد',
    revenue_90: 'الإيراد',
    cost_90: 'التكلفة',
    duplicate_product: 'نسخ',
    archive: 'أرشفة',
    unarchive: 'إلغاء الأرشفة',
    archived: 'مؤرشف',
    show_archived: 'إظهار المؤرشفة',
    export_csv: 'تصدير CSV',
    change_category: 'تغيير الفئة',
    manage_categories: 'الفئات',
    new_category: 'فئة جديدة',
    no_categories: 'لا توجد فئات بعد',
    n_selected: 'محدد',
    select_row: 'تحديد الصف',
    select_all_page: 'تحديد كل ما في هذه الصفحة',
    no_products: 'لا توجد منتجات بعد',
    no_products_sub:
      'أضِف السلع والخدمات التي تبيعها. يمكنك تسعيرها الآن وجرد الكميات لاحقًا.',
    no_products_match: 'لا توجد منتجات مطابقة',
    no_products_match_sub: 'جرّب بحثًا آخر أو أزِل عوامل التصفية.',
    product_search_ph: 'الاسم أو رمز الصنف',
    confirm_archive_products: 'أرشفة المنتجات المحددة؟ ستبقى كما هي في كل مستند ظهرت فيه.',
    confirm_delete_product: 'حذف هذا المنتج؟ لا يمكن حذفه إلا إذا لم يُستخدم في أي مستند.',

    // ===== product image =====
    image: 'الصورة',
    add_image: 'إضافة صورة',
    change_image: 'تغيير',
    remove_image: 'إزالة',
    image_drop_hint: 'أفلِت أو الصق أو انقر',
    image_removed: 'أُزيلت الصورة',
    image_pending: 'تُرفع عند الحفظ.',
    undo: 'تراجع',
    image_too_big: 'الصور فقط، بحد أقصى 5 ميغابايت.',

    // ===== stock =====
    stock: 'المخزون',
    on_hand: 'الكمية المتوفرة',
    committed: 'المحجوز',
    available: 'المتاح',
    inventory_value: 'قيمة المخزون',
    stock_value: 'قيمة المخزون',
    units_on_hand: 'إجمالي الوحدات',
    products_tracked: 'المنتجات المتتبَّعة',
    across_all_units: 'عبر كل الوحدات',
    moving_average: 'المتوسط المتحرك',
    stock_by_warehouse: 'المخزون حسب المستودع',
    negative_rows: 'أرصدة بالسالب',
    negative_stock_hint: 'بيع أكثر مما استُلم',
    no_stock_yet: 'لا يوجد مخزون مسجل بعد',
    no_stock_counted: 'لم يُجرد أي مخزون بعد',
    no_stock_counted_sub: 'اجرد مخزونك مرة واحدة وستُتتبع كل حركة بعدها تلقائيًا.',
    no_warehouses: 'لا توجد مستودعات بعد',
    no_warehouses_sub: 'أضِف مستودعًا قبل جرد المخزون.',
    add_warehouse: 'إضافة مستودع',
    stock_in_stock: 'متوفر',
    stock_low: 'منخفض',
    stock_out: 'نفد',
    stock_not_tracked: 'غير متتبَّع',
    below_reorder_only: 'تحت حد الطلب فقط',

    // ===== movements =====
    movement_change: 'التغيّر',
    movement_balance: 'الرصيد',
    movement_details: 'الحركة',
    recent_movements: 'أحدث الحركات',
    no_movements: 'لا توجد حركات بعد',
    no_movements_period: 'لا توجد حركات في هذه الفترة',
    no_movements_period_sub: 'جرّب فترة أوسع.',
    no_movements_ever: 'لا توجد حركات مخزون بعد',
    no_movements_ever_sub: 'تظهر الحركات هنا عند البيع والاستلام والجرد.',
    filtered_to_product: 'منتج واحد فقط',
    clear_filter: 'إزالة',
    mv_purchase: 'شراء',
    mv_sale: 'بيع',
    mv_adjustment: 'جرد',
    mv_write_off: 'إتلاف',
    mv_opening: 'رصيد افتتاحي',
    mv_transfer_in: 'تحويل وارد',
    mv_transfer_out: 'تحويل صادر',
    mv_reversal: 'عكس قيد',

    // ===== adjustments, the count flow =====
    adjustments: 'التسويات',
    stock_adjustment: 'تسوية مخزون',
    count_stock: 'جرد المخزون',
    new_adjustment: 'تسوية جديدة',
    edit_adjustment: 'تعديل التسوية',
    adjustment_number: 'رقم التسوية',
    adjustment_date: 'تاريخ التسوية',
    counted_by: 'سجّلها',
    warehouse_locked_hint: 'يُقفل بمجرد بدء الجرد.',
    system_qty: 'حسب النظام',
    counted_qty: 'المجرود',
    unit_cost: 'تكلفة الوحدة',
    line_value: 'القيمة',
    not_counted: 'لم يُجرد',
    n_counted: 'مجرود',
    filter_the_sheet: 'ابحث في كشف الجرد…',
    review_counts: 'مراجعة',
    post_count: 'ترحيل',
    post_count_title: 'ترحيل تسوية المخزون',
    post_count_warning: 'أنت على وشك تغيير المخزون. لا يمكن التراجع.',
    products_change: 'منتجات ستتغير',
    products_no_change: 'منتجات مطابقة ولن تتحرك.',
    net_value_change: 'الصافي',
    value_change: 'تغيّر القيمة',
    products_counted: 'المنتجات المجرودة',
    products_changed: 'المنتجات المتغيرة',
    count_reason: 'السبب',
    reason_count: 'تصحيح جرد',
    reason_write_off: 'تالف أو مفقود أو منتهي الصلاحية',
    reason_opening: 'رصيد افتتاحي',
    reason_count_hint: 'اكتب ما جردته فعليًا، ونحن نحسب الفرق.',
    reason_write_off_hint: 'اكتب الفاقد برقم سالب. الإتلاف لا يزيد المخزون أبدًا.',
    reason_opening_hint: 'مخزون كان موجودًا قبل استخدام Railit. يُسجَّل رأس مال للمالك لا إيرادًا.',
    count_attest: 'أُقر بأنني جردت هذا المخزون بنفسي.',
    status_posted: 'مرحّل',
    adjust_stock: 'تسوية المخزون',
    nothing_to_count: 'لا يوجد ما يُجرد في هذا المستودع',
    nothing_to_count_sub: 'أضِف منتجات أولًا أو اختر مستودعًا آخر.',
    no_adjustments: 'لا توجد تسويات مخزون بعد',
    no_adjustments_sub:
      'التسوية هي الطريقة الوحيدة لتصحيح المخزون يدويًا، وكل عملية تترك سجلًا.',
    no_adjustment_lines: 'لم يُجرد شيء بعد',
    confirm_post_adjustment:
      'ترحيل هذه التسوية؟ ستُسجَّل حركات المخزون والقيد المحاسبي، ولا يمكن التراجع.',
    confirm_void_adjustment: 'إلغاء هذه التسوية؟ تُعكس الحركات والقيد معًا.',
    confirm_delete_adjustment: 'حذف هذه المسودة؟ لم يُرحّل منها شيء بعد.',
    pick_warehouse_first: 'اختر مستودعًا ليظهر كشف الجرد.',

    // ===== low stock =====
    low_stock: 'مخزون منخفض',
    out_of_stock: 'نفد المخزون',
    below_reorder: 'تحت حد الطلب',
    reorder_point: 'حد إعادة الطلب',
    reorder_point_hint: 'ننبّهك عند النزول تحت هذا الحد.',
    reorder_qty: 'كمية إعادة الطلب',
    suggested_order: 'الطلب المقترح',
    low_stock_band: 'منتجات عند حد إعادة الطلب أو تحته.',
    out_of_stock_band: 'منتجات نفد مخزونها.',
    show_them: 'اعرضها',
    dismiss: 'إخفاء',

    // ===== من يبيعنا هذا الصنف =====
    suppliers: 'الموردون',
    supplier: 'المورّد',
    add_supplier: 'إضافة مورّد',
    select_supplier: 'اختر المورّد',
    no_suppliers_yet: 'لا يوجد مورّد مسجّل لهذا الصنف بعد.',
    supplier_sku: 'رمزه لدى المورّد',
    last_price: 'آخر سعر',
    never_purchased: 'لم نشترِ منه من قبل',
    lead_time_days: 'مدة التوريد (أيام)',
    preferred_supplier: 'المفضّل',
    make_preferred: 'اجعله المفضّل',
    supplier_price_hint:
      'يتحدث آخر سعر تلقائيًا من كل فاتورة مورّد تُرحّلها، فيبقى صحيحًا دون أن يتابعه أحد. والمورّد المفضّل هو الذي يذهب إليه الطلب.',

    // ===== units, the closed list from lib/units.ts =====
    unit_unit: 'وحدة',
    unit_piece: 'قطعة',
    unit_kg: 'كيلوغرام',
    unit_g: 'غرام',
    unit_litre: 'لتر',
    unit_ml: 'مليلتر',
    unit_metre: 'متر',
    unit_cm: 'سنتيمتر',
    unit_box: 'صندوق',
    unit_pack: 'عبوة',
    unit_carton: 'كرتون',
    unit_hour: 'ساعة',
    unit_day: 'يوم',

    // ===== server error codes =====
    item_sku_taken: 'هذا الرمز مستخدم بالفعل.',
    item_type_required: 'حدّد إن كان هذا سلعة أم خدمة.',
    item_in_use: 'هذا المنتج مستخدم في مستندات ولا يمكن حذفه. يمكنك أرشفته.',
    item_has_stock: 'لا يزال لدى هذا المنتج مخزون.',
    insufficient_stock: 'الكمية المتوفرة لا تكفي.',
    module_off: 'منتج المخزون غير مفعّل لهذه الشركة.',
    category_in_use: 'لا تزال هناك منتجات تستخدم هذه الفئة.',

    // ===== activity log and undo =====
    files: 'الملفات',
    onboarding: 'الإعداد',
    onboarding_offer: 'انقل بياناتك، أو ابدأ مع المساعد',

    // ── إغلاق الشركة ─────────────────────────────────────────────────────────
    org_delete_title: 'حذف هذه الشركة',
    org_delete_lede:
      'كل ما في مساحة العمل يذهب: الدفاتر وكل مستند وكل سجل موظف ووصول الجميع إليها. أمامك {n} يومًا لتغيّر رأيك، وبعدها لا يمكن إرجاعها.',
    org_delete_cta: 'احذف هذه الشركة',
    org_delete_owner_only: 'مالك الشركة وحده يمكنه حذفها.',
    org_delete_continue: 'فهمت، تابِع',
    org_delete_step1_title: 'هذا ما سيُحذف',
    org_delete_step1_lede: 'كل ما في الأسفل موجود في مساحة العمل الآن. كله يذهب.',
    org_delete_step2_title: 'ماذا يحدث بعدها',
    org_delete_step2_a: 'تعمل الشركة كالمعتاد {n} يومًا. لا أحد يفقد وصوله ولا شيء يتوقف.',
    org_delete_step2_b:
      'يمكنك التراجع في أي لحظة خلالها، من هذه الشاشة نفسها، ويستمر كل شيء كأنك لم تطلب.',
    org_delete_step2_c: 'بعد ذلك تُتلَف. لا نستطيع إرجاعها لك، ولا يستطيع أحد.',
    org_delete_step3_title: 'اكتب اسم الشركة',
    org_delete_step3_lede: 'للتأكيد، اكتب {name} في الأسفل.',
    org_delete_final: 'جدوِل الحذف',
    org_delete_name_wrong: 'هذا ليس اسم هذه الشركة.',
    org_delete_scheduled: 'تمت الجدولة. ستُحذف هذه الشركة بعد {n} يومًا.',
    org_delete_cancelled: 'أُلغي الحذف. الشركة باقية.',
    org_delete_keep: 'أبقِ هذه الشركة',
    org_delete_pending: 'هذه الشركة مجدولة للحذف بعد {n} يومًا، في {date}.',
    org_delete_pending_note:
      'كل شيء يعمل حتى ذلك الحين. اضغط الزر أدناه ويستمر كأنك لم تطلب.',
    org_delete_banner: 'ستُحذف هذه الشركة بعد {n} يومًا. كل شيء يعمل كالمعتاد حتى ذلك الحين.',
    org_delete_banner_undo: 'أبقِها',

    // ── لا شركة لديه ────────────────────────────────────────────────────────
    no_company_title: 'لست في أي شركة بعد',
    no_company_lede:
      'الشركة هي مساحة العمل التي تعيش فيها دفاترك. أنشئ واحدة وتكون مالكها، أو انتظر دعوة من زميل إلى شركته.',
    no_company_cta: 'أنشئ شركة',
    no_company_invited: 'تنتظر دعوة؟ تصل على بريدك وتُدخلك مباشرة.',

    // ── الشركة مغلقة بانتظار الحذف ──────────────────────────────────────────
    closed_title: 'هذه الشركة مغلقة',
    closed_lede:
      'طلبت حذفها، فلم يعد أحد يستطيع العمل فيها. لم يُتلَف شيء بعد: كل شيء في مكانه تمامًا، ويبقى كذلك {n} يومًا أخرى، حتى {date}.',
    closed_restore_lede: 'لإرجاعها، اكتب {name} في الأسفل.',
    closed_restore_cta: 'أرجِع هذه الشركة',
    closed_restore_note:
      'يعود كل شيء كما كان عدا الاشتراك. أُلغي عند إغلاق الشركة ولا يمكن التراجع عن إلغائه، فستحتاج إلى الاشتراك من جديد.',
    closed_ask_owner:
      'مالك هذه الشركة طلب حذفها. هو وحده من يستطيع إرجاعها، وقبل التاريخ أعلاه فقط.',
    closed_other_companies: 'أنت أيضًا في:',
    org_switch_deleting: 'تُحذف بعد {n} يومًا',
    files_lede: 'كل مستند لدى الشركة، مرتّبًا بالشهر الذي يخصّه.',
    new_folder: 'مجلد جديد',
    all_files: 'كل الملفات',
    not_filed_yet: 'لم يُرتَّب بعد',
    folders: 'المجلدات',
    files_stored: 'ملفًا محفوظًا',
    search_files: 'ابحث في الملفات',
    no_file_matches: 'لا ملف يطابق ذلك',
    nothing_filed_here: 'لا شيء هنا',
    files_empty_lede: 'كل إيصال وفاتورة وصورة مرفقة بسجل تظهر هنا.',
    try_a_shorter_word: 'جرّب كلمة أقصر، أو امسح البحث.',
    unlink: 'فكّ الربط',
    not_linked_title: 'غير مربوط بشيء',
    not_linked_lede: 'هذا الملف غير مرفق بأي سجل بعد.',
    added: 'أُضيف',
    activity_log: 'سجل النشاط',
    activity_log_sub: 'كل تغيير، ومن أجراه، وكيف.',
    filter_person: 'الشخص',
    filter_channel: 'القناة',
    filter_how: 'الطريقة',
    filter_entity: 'نوع السجل',
    filter_action: 'الإجراء',
    all_people: 'الجميع',
    all_channels: 'كل القنوات',
    all_ways: 'كل الطرق',
    all_entities: 'كل السجلات',
    all_actions: 'كل الإجراءات',
    only_assistant: 'المساعد فقط',
    only_undone: 'ما تم التراجع عنه فقط',
    channel_web: 'لوحة الويب',
    channel_whatsapp: 'واتساب',
    channel_telegram: 'تيليجرام',
    channel_api: 'تطبيق مرتبط',
    channel_system: 'النظام',
    by_person: 'أُدخل يدويًا',
    by_ai: 'اقترحه المساعد ووافق عليه شخص',
    by_ai_short: 'المساعد',
    by_system: 'تلقائي',
    actor_system: 'النظام',
    field: 'الحقل',
    what_changed: 'ما الذي تغيّر',
    where_from: 'مصدر الإجراء',
    before_: 'قبل',
    after_: 'بعد',
    their_words: 'نص الرسالة',
    approval_preview: 'ما تمت الموافقة عليه',
    also_recorded: 'سُجّل أيضًا',
    lines_unchanged: '{count} بندًا، دون تغيير',
    lines_changed: '{count} بندًا، تغيّر منها {n}',
    and_more: 'و{n} أخرى',
    undo_review: 'مراجعة التراجع',
    undo_effects: 'سيتم تنفيذ التالي',
    undo_creates: 'سيتم إنشاء',
    undo_keeps: 'سيبقى كما هو',
    undo_warnings: 'انتبه',
    undo_reason: 'السبب',
    undo_reason_ph: 'أُدخل بالخطأ',
    undo_reason_required: 'السبب مطلوب.',
    undone: 'تم التراجع',
    undone_by: 'تراجع عنه {name} بتاريخ {date}',
    undone_ok: 'تم التراجع.',
    undo_not_possible: 'لا يمكن التراجع عن هذا',
    undo_no_ALREADY_UNDONE: 'سبق التراجع عنه.',
    undo_no_SUPERSEDED: 'حلّ محلّه تغيير أحدث. تراجع عنه أولًا.',
    undo_no_HAS_PAYMENTS: 'سُجّلت مدفوعات على هذا المستند. اعكسها أولًا.',
    undo_no_NOT_UNDOABLE: 'لا توجد طريقة آمنة لعكس هذا الإجراء.',
    undo_no_UNDO_OF_UNDO: 'هذا الإجراء هو تراجع بحد ذاته.',
    undo_no_TOO_OLD: 'مضى على هذا أكثر من ٩٠ يومًا. عدّل السجل مباشرة.',
    undo_no_NOT_PERMITTED: 'لا يمكن التراجع إلا لمن قام بالإجراء أو لمالك أو مدير.',
    undo_no_STALE: 'تغيّر السجل أثناء اطلاعك على هذه الصفحة.',
    undo_no_RECORD_GONE: 'لم يعد السجل موجودًا.',
    undo_no_DOWNSTREAM_DOCUMENT: 'هذا القيد يخص مستندًا. تراجع عن المستند نفسه.',
    undo_no_PLAN_NOT_ENTITLED: 'خطتك الحالية لا تشمل هذه الوحدة.',
    undo_no_PERIOD_CLOSED: 'الفترة المحاسبية مقفلة.',
    undo_no_sent: 'أُرسلت الرسالة بالفعل، ولا يمكن استرجاعها من هنا.',
    undo_no_deleted: 'حُذف السجل نهائيًا.',
    undo_no_file: 'حُذف الملف نهائيًا من وحدة التخزين.',
    no_activity: 'لا يوجد نشاط مسجّل بعد',
    no_activity_sub: 'كل تغيير يحدث في مساحة العمل يظهر هنا.',
    no_activity_match: 'لا نتائج مطابقة لهذه الفلاتر',
    view_record: 'افتح السجل',
    act_create: 'إنشاء',
    act_update: 'تعديل',
    act_delete: 'حذف',
    act_post: 'ترحيل',
    act_pay: 'سداد',
    act_send: 'إرسال',
    act_sign: 'توقيع',
    act_void: 'إلغاء',
    act_reverse: 'عكس قيد',
    act_submit: 'تقديم',
    act_approve: 'اعتماد',
    act_reject: 'رفض',
    act_terminate: 'إنهاء',
    act_deactivate: 'تعطيل',
    act_restore: 'استعادة',
    act_reinstate: 'إعادة إلى الخدمة',
    act_enable: 'تفعيل',
    act_disable: 'إيقاف',
    act_grant: 'منح',
    act_revoke: 'إبطال',
    act_login: 'تسجيل دخول',
    act_logout: 'تسجيل خروج',
    act_password_reset: 'إعادة تعيين كلمة المرور',
    act_reset: 'إعادة ضبط سنة الإجازات',
    act_undo: 'تراجع',
    ent_Invoice: 'فاتورة',
    ent_Bill: 'فاتورة مورّد',
    ent_Expense: 'مصروف',
    ent_Payment: 'دفعة',
    ent_JournalEntry: 'قيد يومية',
    ent_Quotation: 'عرض سعر',
    ent_Contract: 'عقد',
    ent_ContractTemplate: 'نموذج عقد',
    ent_Customer: 'عميل',
    ent_Vendor: 'مورّد',
    ent_Employee: 'موظف',
    ent_PayrollRun: 'مسير رواتب',
    ent_Payslip: 'قسيمة راتب',
    ent_Item: 'صنف أو خدمة',
    ent_ItemCategory: 'فئة',
    ent_ItemVendor: 'مورّد صنف',
    ent_StockAdjustment: 'تسوية مخزون',
    ent_Warehouse: 'مستودع',
    ent_Account: 'حساب في الدليل',
    ent_BankAccount: 'حساب بنكي',
    ent_Organization: 'مساحة العمل',
    ent_Membership: 'عضوية',
    ent_Invitation: 'دعوة',
    ent_Setting: 'الإعدادات',
    ent_User: 'مستخدم',
    ent_ApiToken: 'مفتاح تطبيق مرتبط',
    ent_Attachment: 'مرفق',
    ent_RecordComment: 'ملاحظة',
    ent_Subscription: 'اشتراك متكرر',
    ent_OrgSubscription: 'الخطة والفوترة',
    ent_WhatsappAccount: 'ربط واتساب',
    ent_WhatsappContact: 'جهة اتصال واتساب',
    ent_Memory: 'ذاكرة المساعد',
    ent_FxRate: 'سعر صرف',
    ent_Email: 'بريد إلكتروني',
    ent_OAuthClient: 'تطبيق مرتبط',
    f_modules: 'الوحدات',
    f_locale: 'اللغة',
    f_timezone: 'المنطقة الزمنية',
    f_base_salary: 'الراتب',
    f_track_stock: 'تتبّع المخزون',
    f_is_default: 'الافتراضي',
    f_is_active: 'نشط',

    // ── إدخال بيانات الشركة ──────────────────────────────────────────────────
    start_title: 'أدخِل بيانات شركتك',
    start_lede: 'لن تبقى الشاشات فارغة طويلًا. اختر ما لديك أصلًا.',
    start_skip: 'ابدأ بمساحة عمل فارغة',
    start_zoho_title: 'اجلبها من زوهو',
    start_zoho_lede:
      'اربط حسابك في زوهو بوكس، وتنتقل عملاؤك ومورّدوك وأصنافك ومستنداتك، وتستمر في الانتقال.',
    start_zoho_needs: 'تحتاج إلى تسجيل الدخول في زوهو',
    start_files_title: 'أفلِت ملفاتك هنا',
    start_files_lede:
      'جداول وملفات PDF وصور، أو ملف مضغوط بالمجلد كاملًا. كل ملف يُقرأ أو يُحفظ، ونخبرك بما جرى لكل واحد منها بالضبط.',
    start_files_needs: 'أي شيء لديك. ولا حاجة لترتيبه أولًا',
    start_agent_title: 'ابدأ من الصفر مع المساعد',
    start_agent_lede:
      'لا ملفات بعد؟ يسألك المساعد عن الشركة ويُعِدّها معك، سؤالين في كل مرة.',
    start_agent_needs: 'نحو خمس دقائق',
    start_drop_anywhere: 'أفلِت الملفات لبدء القراءة',

    // ── صندوق الملفات ────────────────────────────────────────────────────────
    intake_title: 'أفلِت ملفاتك هنا',
    intake_lede:
      'أي شيء لديك. تُقرأ الجداول صفًا صفًا، ويُحفظ ما عداها كمستندات، ولا يُكتب شيء في دفاترك حتى توافق أنت.',
    intake_drop: 'أفلِت الملفات هنا',
    intake_drop_hint: 'أو اخترها',
    intake_drop_kinds: 'إكسل أو CSV أو PDF أو صور أو ملف zip بها جميعًا',
    intake_choose: 'اختر ملفات',
    intake_start: 'اقرأ هذه الملفات',
    intake_reading: 'جارٍ قراءة',
    intake_sheet_of: 'الورقة {i} من {n}',
    intake_rows: '{n} صفًا',
    intake_waiting: 'في الانتظار',
    intake_of_files: '{done} من {total} ملفًا',

    intake_outcome_imported: 'قُرِئ',
    intake_outcome_stored: 'حُفِظ',
    intake_outcome_refused: 'لم يُقرأ',
    intake_outcome_pending: 'جارٍ القراءة',
    intake_files_read: 'ملفًا قُرِئت',
    intake_files_kept: 'حُفِظت كمستندات',
    intake_files_refused: 'لم تُفهَم',
    intake_all_accounted: 'كل ملف محسوب.',

    // ── ما جرى لكل ملف، بلغة القارئ ──────────────────────────────────────────
    io_PATH_ESCAPE: 'اسم هذا العنصر «{name}»، وهو يشير إلى موضع خارج الأرشيف، فلم يُفتح.',
    io_ENCRYPTED: 'هذا الملف محمي بكلمة مرور. أزِل الحماية وأرسله مرة أخرى.',
    io_UNSUPPORTED_METHOD: 'هذا الملف مضغوط بطريقة لا يقرؤها النظام. أعد ضغطه بالإعدادات الاعتيادية.',
    io_ENTRY_TOO_LARGE: 'حجم هذا الملف بعد فكّ الضغط يتجاوز حدّ {limit} للملف الواحد.',
    io_ARCHIVE_TOO_LARGE: 'حجم الأرشيف بعد فكّ الضغط يتجاوز {limit}، فتوقّفت القراءة هنا بدل ملء القرص.',
    io_TOO_MANY_FILES: 'يحوي هذا الأرشيف أكثر من {limit} ملف. أرسله على دفعات.',
    io_TOO_DEEP: 'هذا الأرشيف متداخل بعمق {depth}، وهو يتجاوز الحدّ {max}. حُفِظ كملف بدل فكّه.',
    io_CORRUPT: 'تعذّر فتح هذا. قد يكون تالفًا أو رُفع جزئيًا.',
    io_EMPTY: 'هذا الملف فارغ (صفر بايت)، فلا شيء فيه ليُقرأ.',
    io_NO_STORAGE: 'تخزين المستندات غير مُعدّ على هذا الخادم، فتعذّر حفظ الملف.',
    io_STORE_FAILED: 'تعذّر حفظ هذا الملف: {why}',
    io_IMPORTED: 'قُرِئ بـ{sheets} ورقة، منها {ready} جاهزة للاستيراد.',
    io_STORED_DOCUMENT: 'حُفِظ كمستند.',
    io_STORED_UNKNOWN: 'النظام لا يقرأ هذا النوع من الملفات، فحُفِظ كملف.',
    io_STORED_UNREADABLE_SHEET: 'تعذّرت قراءته كجدول، فحُفِظ كملف.',
    io_STORED_NO_ROWS: 'فُتِح كجدول لكن لا صفوف في أي ورقة، فحُفِظ كملف.',
    io_STORED_NO_IMPORTABLE_SHEET:
      'قُرِئ بـ{sheets} ورقة، وليس فيها قائمة يستطيع النظام استيرادها. حُفِظ كملف.',

    io_note: 'امتداده ‎.{ext}‎ وهو في الحقيقة {real}',
    io_note_noext: 'بلا امتداد، وهو في الحقيقة {real}',
    io_real_pdf: 'ملف PDF',
    io_real_png: 'صورة PNG',
    io_real_jpeg: 'صورة JPEG',
    io_real_gif: 'صورة GIF',
    io_real_tiff: 'صورة ممسوحة TIFF',
    io_real_heic: 'صورة HEIC',
    io_real_zip: 'أرشيف مضغوط',
    io_real_not_a_workbook: 'ليس جدولًا',

    is_SHEET_NOT_A_LIST:
      'هذه الورقة ليست قائمة عملاء أو موردين أو موظفين أو منتجات، فلا شيء فيها ليُستورد.',
    is_SHEET_BYTES_GONE: 'الملف الذي جاءت منه هذه الورقة لم يعد متاحًا. أفلِته من جديد.',
    is_SHEET_NO_COLUMNS: 'لا أعمدة مطابَقة، فلا شيء ليُقرأ من هذه الورقة.',
    is_SHEET_ALL_EMPTY: 'كل صفوف هذه الورقة جاءت فارغة بعد قراءة الأعمدة المطابَقة.',
    is_SHEET_NONE_VALID: 'لم يجتز أي صف في هذه الورقة التحقق. الأرجح أن مطابقة الأعمدة خاطئة.',
    is_SHEET_FAILED: 'تعذّرت قراءة هذه الورقة: {why}',

    // ── مراجعة الأعمدة ───────────────────────────────────────────────────────
    intake_review_title: 'راجِع معنى الأعمدة',
    intake_review_lede:
      'هذه الخطوة الوحيدة التي تستحق نظرك. كل ما تحته يقرؤه النظام بنفسه ولا يُعاد كتابته، فالعمود الخطأ هنا هو الطريق الوحيد لأن ينتهي رقم في مكان لا يخصّه.',
    intake_review_none: 'لا شيء هنا يحتاج إلى استيراد.',
    intake_column: 'العمود في ملفك',
    intake_field: 'يذهب إلى',
    intake_ignore: 'اتركه',
    intake_constant: 'لا عمود له في ملفك',
    intake_entity: 'هذه الورقة قائمة',
    intake_entity_customer: 'عملاء',
    intake_entity_vendor: 'موردين',
    intake_entity_employee: 'موظفين',
    intake_entity_item: 'منتجات',
    intake_entity_supplier_bill: 'فواتير موردين',
    intake_entity_customer_invoice: 'فواتير أصدرتها',
    intake_entity_document_lines: 'بنود فواتير في ورقة أخرى',
    intake_entity_none: 'ليست قائمة للاستيراد',
    intake_money_like: 'هذه الأعمدة تحمل مبالغ ولا شيء يقرؤها: {columns}',
    intake_unmapped: 'لن تُقرأ: {columns}',
    intake_lines_for: 'هذه بنود فواتير ورقة',
    intake_lines_for_none: 'لا تتبع أي ورقة أخرى',
    intake_lines_key: 'المطابقة على',
    intake_lines_coverage: '{n}٪ منها وجدت فاتورتها',
    intake_lines_by_person: 'أنت اخترت هذا',
    intake_by_model: 'طُوبِقت تلقائيًا',
    intake_by_person: 'غيّرتها أنت',
    intake_skip_sheet: 'تخطَّ هذه الورقة',
    intake_sample: 'أول الصفوف',
    intake_import: 'استورد {n} صفًا',
    intake_importing: 'جارٍ قراءة كل صف…',

    // ── الإيصال ──────────────────────────────────────────────────────────────
    intake_done_title: 'جاهزة للدخول في دفاترك',
    intake_done_lede:
      'قُرِئ كل صف وفُحِص. لم يُكتب شيء بعد: اضغط الزر أدناه وتدخل.',
    intake_staged: '{n} صفًا جاهزًا',
    intake_rejected: '{n} صفًا تعذّرت قراءتها',
    intake_apply: 'أضِف {n} صفًا إلى دفاتري',
    intake_applying: 'جارٍ كتابتها…',
    intake_applied_title: 'دخلت دفاترك',
    intake_applied_lede: 'أُضيف {n} صفًا. يمكنك فتح مساحة عملك ورؤيتها.',
    intake_written: 'أُضيف {n} صفًا',
    intake_open_dashboard: 'افتح مساحة عملي',
    intake_receipt: 'ما الذي دخل',
    intake_nothing_staged: 'لم تُجهَّز أي صفوف من هذه الملفات.',
    intake_failed: 'توقّف هذا الاستيراد',
    intake_retry: 'أعد المحاولة',
    intake_reconnecting: 'جارٍ إعادة الاتصال…',
    intake_finished: 'انتهى',
    intake_open_files: 'افتح المستندات',
    intake_go_dashboard: 'اذهب إلى الرئيسية',

    // ── شاشة القائمة والتفاصيل ───────────────────────────────────────────────
    clear_selection: 'إلغاء التحديد',
    resize_list: 'تغيير عرض القائمة',
    back_to_list: 'العودة إلى القائمة',
    close_record: 'إغلاق هذا السجل',
    more: 'المزيد',
    more_new: 'شيء آخر',
    attach_files: 'إرفاق ملفات',
    comments_history: 'الملاحظات والسجل',
    date_range: 'المدى الزمني',
    no_customer: 'بلا عميل',

    /*
      كم تأخّر المستند. انظر lib/documentAge.ts.

      العربية تعدّ الأيام بخمس صيغ، وهذه هي: «يوم واحد» للواحد، و«يومين»
      للاثنين بلا رقم، و«3 أيام» لجمع القلة من ثلاثة إلى عشرة، و«22 يومًا»
      تمييزًا مفردًا منصوبًا من أحد عشر فصاعدًا، و«100 يوم» لما بقي.
      اختيار الصيغة يتركه الكود لـ Intl.PluralRules.
    */
    doc_age_overdue_zero: 'تأخّر {n} يوم',
    doc_age_overdue_one: 'تأخّر يوم واحد',
    doc_age_overdue_two: 'تأخّر يومين',
    doc_age_overdue_few: 'تأخّر {n} أيام',
    doc_age_overdue_many: 'تأخّر {n} يومًا',
    doc_age_overdue_other: 'تأخّر {n} يوم',
    doc_age_due_in_zero: 'الاستحقاق بعد {n} يوم',
    doc_age_due_in_one: 'الاستحقاق بعد يوم واحد',
    doc_age_due_in_two: 'الاستحقاق بعد يومين',
    doc_age_due_in_few: 'الاستحقاق بعد {n} أيام',
    doc_age_due_in_many: 'الاستحقاق بعد {n} يومًا',
    doc_age_due_in_other: 'الاستحقاق بعد {n} يوم',
    doc_age_due_today: 'الاستحقاق اليوم',
    doc_age_due_tomorrow: 'الاستحقاق غدًا',

    // ── العروض المحفوظة ──────────────────────────────────────────────────────
    view_all_invoices: 'كل الفواتير',
    view_overdue_invoices: 'الفواتير المتأخرة',
    view_draft_invoices: 'المسودات',
    view_all_customers: 'كل العملاء',
    view_active_customers: 'العملاء النشطون',
    view_inactive_customers: 'العملاء غير النشطين',

    // ── الخطوة التالية ───────────────────────────────────────────────────────
    next_invoice_draft: 'لم يرَ أحد هذه الفاتورة بعد. {action}.',
    next_invoice_draft_action: 'أرسلها إلى {name}',
    next_invoice_due: 'ما زال {amount} من هذه الفاتورة غير مسدَّد. {action}.',
    next_invoice_overdue:
      'تجاوزت هذه الفاتورة تاريخ استحقاقها وما زال {amount} غير مسدَّد. {action}.',
    next_invoice_pay_action: 'سجّل دفعة',
    next_customer_email: 'لا يوجد بريد إلكتروني مسجَّل، فلا يمكن إرسال شيء إلى {name}. {action}.',
    next_customer_email_action: 'أضِف واحدًا',
    next_customer_invoice: 'لم تُصدر أي فاتورة لـ{name} قط. {action}.',
    next_customer_invoice_action: 'أنشئ أول فاتورة',

    // ── الأشخاص والمنتجات: قوائم الشاشة المقسومة ─────────────────────────────
    view_all_products: 'كل المنتجات',
    view_goods: 'السلع',
    view_services: 'الخدمات',
    view_archived_products: 'المنتجات المؤرشفة',
    view_all_employees: 'كل الموظفين',
    view_active_employees: 'الموظفون النشطون',
    view_paused_employees: 'الموقوفون',
    view_terminated_employees: 'المنتهية خدمتهم',

    next_item_price: 'لا يوجد سعر بيع لـ{name}، فلا يمكن إدراجه في فاتورة. {action}.',
    next_item_price_action: 'حدِّد سعرًا',
    next_item_low: 'انخفض مخزون {name} تحت حدّ إعادة الطلب. {action}.',
    next_item_out: 'لم يتبقَّ شيء من {name} في المخزن. {action}.',
    next_item_stock_action: 'سجّل تسوية مخزون',
    next_employee_contract: 'لا يوجد عقد موقَّع لـ{name}. {action}.',
    next_employee_contract_action: 'أنشئ أول عقد',

    // ── المشتريات: قوائم الشاشة المقسومة ─────────────────────────────────────
    no_vendor: 'بلا مورّد',
    no_bank_account: 'بلا حساب',
    unlinked_payment: 'بلا طرف مقابل',

    view_all_bills: 'كل الفواتير الواردة',
    view_overdue_bills: 'الفواتير الواردة المتأخرة',
    view_draft_bills: 'المسودات',
    view_all_expenses: 'كل المصروفات',
    view_awaiting_expenses: 'بانتظار الاعتماد',
    view_draft_expenses: 'المسودات',
    view_all_vendors: 'كل الموردين',
    view_active_vendors: 'الموردون النشطون',
    view_inactive_vendors: 'الموردون غير النشطين',
    view_all_payments: 'كل المدفوعات',
    view_inbound_payments: 'الوارد',
    view_outbound_payments: 'الصادر',

    next_bill_draft: 'لم تُقيَّد هذه الفاتورة في الدفاتر بعد. {action}.',
    next_bill_draft_action: 'قيّدها',
    next_bill_due: 'ما زال {amount} من هذه الفاتورة غير مسدَّد. {action}.',
    next_bill_overdue: 'تجاوزت هذه الفاتورة تاريخ استحقاقها وما زال {amount} غير مسدَّد. {action}.',
    next_bill_pay_action: 'سجّل دفعة',
    next_expense_draft: 'لم يُطلب اعتماد هذه المطالبة بعد. {action}.',
    next_expense_rejected: 'أُعيدت هذه المطالبة مرفوضة. صحّح ما فيها ثم {action}.',
    next_expense_submit_action: 'أرسلها للاعتماد',
    next_expense_submitted: 'هذه المطالبة بانتظار قرار. {action}.',
    next_expense_approve_action: 'اعتمدها',
    next_expense_approved: 'اعتُمد هذا المصروف ولم يُقيَّد في الدفاتر بعد. {action}.',
    next_expense_post_action: 'قيّده',
    next_vendor_email: 'لا يوجد بريد إلكتروني مسجَّل، فلا يمكن إرسال شيء إلى {name}. {action}.',
    next_vendor_email_action: 'أضِف واحدًا',

    // ── تقسيم شاشات المبيعات: العروض المحفوظة ────────────────────────────────
    view_all_quotations: 'كل عروض الأسعار',
    view_draft_quotations: 'المسودات',
    view_all_credit_notes: 'كل الإشعارات الدائنة',
    view_draft_credit_notes: 'المسودات',
    view_all_contracts: 'كل العقود',
    view_all_subscriptions: 'كل الاشتراكات',

    // ── تقسيم شاشات المبيعات: الخطوة التالية ─────────────────────────────────
    next_quotation_draft: 'لم يرَ أحد عرض السعر هذا بعد. {action}.',
    next_quotation_draft_action: 'أرسله إلى {name}',
    next_quotation_sent: 'لم يردّ {name} على عرض السعر هذا بعد. {action}.',
    next_quotation_sent_action: 'سجّله موقَّعًا',
    next_quotation_signed: 'قُبل عرض السعر هذا ولم يتحوّل إلى فاتورة بعد. {action}.',
    next_quotation_signed_action: 'حوّله',
    next_credit_note_draft: 'هذا الإشعار مسودة، فهو ليس رصيدًا بعد. {action}.',
    next_credit_note_draft_action: 'أصدره إلى {name}',
    next_credit_note_unused: 'لم يُستخدم {amount} من هذا الإشعار الدائن. {action}.',
    next_credit_note_unused_action: 'اخصمه من فاتورة',
    next_contract_unsigned: 'لا شيء يُلزم {name} ما لم يُوقَّع هذا العقد. {action}.',
    next_contract_unsigned_action: 'وقّعه',
    next_subscription_due: 'كان من المفترض تحصيل {name} في {date} ولم يحدث. {action}.',
    next_subscription_due_action: 'حصّله الآن',
    next_subscription_paused: '{name} موقوف مؤقتًا، فلا يجري تحصيل شيء. {action}.',
    next_subscription_paused_action: 'أعِد تشغيله',

    // ── صفحة العميل: المستحقات والدخل والحركات وكشف الحساب ───────────────────
    cx_tab_overview: 'نظرة عامة',
    cx_tab_transactions: 'الحركات',
    cx_tab_statement: 'كشف الحساب',

    cx_receivables: 'المستحقات',
    cx_unused_credits: 'أرصدة غير مستخدمة',
    cx_open_invoices: 'فواتير مفتوحة',
    cx_credits_note:
      'تُسجَّل كل دفعة مقابل مستند بعينه ولا تتجاوز ما تبقّى عليه، فلا يبقى شيء ليصبح رصيدًا غير مستخدم.',
    cx_never_invoiced: 'لم تُصدر أي فاتورة على {name}، فلا شيء مستحقًا.',

    cx_lifetime: 'الإجمالي التاريخي',
    cx_invoiced: 'ما صدرت به فواتير',
    cx_received: 'ما تم تحصيله',
    cx_first_invoice: 'أول فاتورة',
    cx_last_invoice: 'آخر فاتورة',
    cx_last_payment: 'آخر دفعة',
    cx_none_yet: 'لا شيء بعد',

    cx_income: 'الدخل',
    cx_basis: 'الأساس',
    cx_basis_accrual: 'الاستحقاق',
    cx_basis_cash: 'النقدي',
    cx_basis_accrual_note:
      'شهر إصدار كل فاتورة، بعد خصم ضريبتها، سواء سُدِّدت أم لا.',
    cx_basis_cash_note: 'شهر وصول كل دفعة، بعد خصم حصة الضريبة من الفاتورة التي سدّدتها.',
    cx_last_n_months: 'آخر {n} أشهر',
    cx_income_none: 'لم تُصدر أي فاتورة على {name}، فلا يوجد ما يُرسم.',
    cx_income_total: 'الإجمالي، من {from} إلى {to}',

    cx_show: 'اعرض',
    cx_everything: 'كل شيء',
    cx_show_all: 'الكل ({n})',
    cx_against: 'مقابل',
    cx_became: 'أصبح',
    cx_ends: 'ينتهي',
    cx_next_charge: 'التحصيل القادم',

    cx_statement_summary: 'كشف الحساب',
    cx_statement_lines: 'الحركات',
    cx_statement_empty: 'لم تصدر فواتير ولم تُحصَّل دفعات في هذه الفترة.',
    cx_transaction: 'الحركة',
    cx_balance: 'الرصيد',
    cx_balance_due: 'الرصيد المستحق',
    cx_stmt_invoice: 'فاتورة',
    cx_stmt_due_on: 'تستحق في {date}',
    cx_stmt_settles: 'مقابل {number}',
    cx_stmt_payment: 'دفعة واردة',

    /* ------------------------------------------------------------- drafts/ar
       نصوص صفحة التغييرات المعلّقة. لا يوجد هنا أي وصف للتغيير نفسه: ذلك يأتي
       من عمود preview كما كُتب لحظة تجهيز التغيير، وترجمته هنا تعني وصفاً
       ثانياً للصف نفسه قد يخالف البطاقة في المحادثة. */
    drafts: 'التغييرات المعلّقة',
    drafts_lede: 'كل تغيير جاهز وينتظر الاعتماد، أياً كانت الجهة التي طُلب منها. لم يمسّ أيٌّ مما في هذه الصفحة الدفاتر بعد.',
    drafts_waiting: '{n} في الانتظار',
    drafts_search_ph: 'ابحث في النص أو المبلغ أو الاسم\u2026',
    drafts_none: 'لا شيء في الانتظار',
    drafts_none_sub: 'حين يُجهّز المساعد أو محادثة واتساب أو عملية استيراد أو تصحيح دفعة تغييراً، ينتظر هنا حتى يعتمده أحد.',
    drafts_none_but_decided:
      'لا شيء في الانتظار. هناك {n} تغييراً جرى البتّ فيه من قبل، تجده في التبويبات أعلاه.',
    drafts_none_match: 'لا يوجد تغيير يطابق هذه المرشّحات',
    draft_all_statuses: 'كل الحالات',
    draft_status_pending: 'في الانتظار',
    draft_status_expired: 'انتهت مهلته',
    draft_status_approved: 'مُعتمد',
    draft_status_rejected: 'مرفوض',
    draft_status_superseded: 'استُبدل',
    draft_kind: 'النوع',
    draft_all_kinds: 'كل الأنواع',
    draft_sort: 'الترتيب',
    draft_sort_newest: 'الأحدث أولاً',
    draft_sort_oldest: 'الأقدم أولاً',
    draft_sort_expiring: 'الأقرب انتهاءً',
    draft_sort_largest: 'الأكبر مبلغاً',
    draft_only_mine: 'ما يخصّني فقط',
    draft_apply: 'اعتماد',
    draft_reject: 'رفض',
    draft_reject_lede: 'يبقى التغيير في هذه القائمة موسوماً بالرفض ومعه سببك. لا يُحذف شيء.',
    draft_reject_reason: 'السبب',
    draft_reject_reason_ph: 'لماذا لا ينبغي تسجيل هذا',
    draft_reject_reason_short: '{n} أحرف على الأقل، بكلماتك أنت.',
    draft_applied: 'تم الاعتماد.',
    draft_rejected: 'رُفض، وبقي في القائمة.',
    draft_rejected_because: 'سبب الرفض:',
    draft_blocked_expired: 'انتهت المهلة قبل أن يعتمده أحد. اطلب التغيير من جديد للحصول على واحد صالح.',
    draft_blocked_decided: 'سبق البتّ فيه.',
    draft_blocked_not_yours: 'لا يعتمده إلا {who}.',
    draft_blocked_role: 'دورك يتيح الاطّلاع على التغييرات المعلّقة دون اعتمادها. الاعتماد للمالك أو المدير أو المالية.',
    draft_window_closed: 'انتهت المهلة',
    draft_minutes_left: 'بقيت {n} دقيقة',
    draft_hours_left: 'بقيت {n} ساعة',
    draft_attachments: 'المرفقات',
    draft_origin_assistant: 'المساعد',
    draft_origin_payment: 'تصحيح دفعة',
    draft_origin_intake: 'استيراد',
    draft_origin_telegram: 'تيليجرام',
    draft_staged_from: 'مصدر الطلب',
    draft_expires: 'ينتهي في',
    draft_payload: 'ما سيُكتب بالضبط',
    intent_create_expense: 'مصروف جديد',
    intent_create_invoice: 'فاتورة جديدة',
    intent_create_quotation: 'عرض سعر جديد',
    intent_record_invoice_payment: 'تسجيل دفعة',
    intent_bulk_create: 'عدة سجلات دفعة واحدة',
    intent_update_employee: 'تعديل بيانات موظف',
    intent_record_adjustment: 'قيد يدوي',
    intent_record_customer_refund: 'إرجاع مبلغ لعميل',
    intent_reverse_payment: 'التراجع عن دفعة مستلمة',
    intent_void_document: 'إلغاء مستند',
    intent_reverse_expense: 'عكس مصروف',
    intent_update_party: 'تعديل عميل أو مورّد',
    intent_update_company_profile: 'تعديل بيانات الشركة',
    intent_create_bank_account: 'حساب بنكي جديد',
    intent_close_bank_account: 'إغلاق حساب بنكي',
    intent_create_item: 'منتج جديد',
    intent_update_item: 'تعديل منتج',
    intent_adjust_stock: 'تسوية مخزون',
    intent_create_purchase: 'عملية شراء جديدة',
    intent_book_leave: 'طلب إجازة',
    intent_decide_leave: 'البتّ في طلب إجازة',
    intent_reverse_journal: 'عكس قيد يومية',

    /* ───────────────────────────────────────────────────────────────────────
       عكس قيد اليومية، من القيد نفسه. كتلة إضافية فقط؛ أبقِ الإنجليزية
       والعربية متطابقتين. */
    reverse_entry: 'اعكس هذا القيد',
    reverse_entry_lede:
      'لا يُحذف شيء. يُقيَّد قيد ثانٍ يعكس كل سطر من هذا القيد، بتاريخ القيد نفسه، فلا تتحرك فترة سبق أن أصدرت عنها تقاريرك. يبقى الاثنان في الحساب ويشير كلٌّ منهما إلى الآخر.',
    reverse_entry_reason: 'السبب',
    reverse_entry_reason_ph: 'قُيِّد مرتين: نفس الرسوم البنكية أُدخلت من رسالة واحدة الأحد ثم الاثنين.',
    reverse_entry_reason_short: 'ما لا يقل عن {n} أحرف. يُكتب في القيد العكسي ويقرأه من يراجع الدفاتر.',
    reverse_entry_confirm: 'راجعتُ ذلك، وأعلم أنه يُقيَّد في الدفاتر.',
    reverse_entry_submit: 'جهّز هذا العكس',
    reverse_entry_staged: 'جاهز. لم يتغيّر شيء بعد: اقرأه بالأسفل ثم طبّقه.',
    reverse_entry_pending: 'عكس بانتظار التطبيق',
    reverse_entry_pending_sub: 'لم يُكتب شيء بعد. اقرأه ثم طبّقه أو تجاهله.',
    reverse_entry_apply: 'طبّق العكس',
    reverse_entry_discard: 'تجاهل',
    reverse_entry_applied: 'طُبّق.',
    reverse_entry_discarded: 'أُهمل. لم يتغيّر شيء.',
    reverse_entry_not_yours: 'لا يطبّقه إلا من طلبه.',
    reversed_by_entry: 'عُكس بالقيد {number}',
    reverses_entry: 'هذا القيد يعكس {number}',
    reverse_entry_duplicate_note:
      'يذكر تدقيق الدفاتر أن هذا القيد يقول ما يقوله القيد {number}. إن كان ذلك خطأً، فهذه طريقة التراجع عنه.',
  },
};

export type TranslationKey = keyof typeof I18N['en'];

/**
 * API error codes that have a friendly translation. Anything not listed here
 * falls back to the message the server sent.
 */
export const API_ERROR_KEY: Record<string, string> = {
  INVOICE_DISCOUNT_EXCEEDS_SUBTOTAL: 'discount_exceeds_subtotal',
  INVOICE_ADVANCE_VALUE_REQUIRED: 'advance_value_required',
  INVOICE_ADVANCE_INVALID: 'advance_invalid',
  INVOICE_ADVANCE_PERCENT_INVALID: 'advance_percent_invalid',
  INVOICE_ADVANCE_EXCEEDS_NET: 'advance_exceeds_net',
  INVOICE_ADVANCE_TYPE_REQUIRED: 'advance_type_required',
  ITEM_SKU_TAKEN: 'item_sku_taken',
  ITEM_TYPE_REQUIRED: 'item_type_required',
  ITEM_IN_USE: 'item_in_use',
  ITEM_HAS_STOCK: 'item_has_stock',
  INSUFFICIENT_STOCK: 'insufficient_stock',
  CATEGORY_IN_USE: 'category_in_use',
  MODULE_OFF: 'module_off',
  CREDIT_NOTE_DISCOUNT_EXCEEDS_SUBTOTAL: 'discount_exceeds_subtotal',
  CREDIT_NOTE_EXCEEDS_INVOICE_BALANCE: 'credit_exceeds_invoice_balance',
  CREDIT_NOTE_EXCEEDS_REMAINING: 'credit_exceeds_remaining',
  CREDIT_NOTE_CUSTOMER_MISMATCH: 'credit_customer_mismatch',
  CREDIT_NOTE_CURRENCY_MISMATCH: 'credit_currency_mismatch',
  CREDIT_NOTE_NOT_APPLICABLE: 'credit_note_not_applicable',
  INVOICE_NOT_CREDITABLE: 'invoice_not_creditable',
  INVOICE_HAS_CREDIT: 'invoice_has_credit',
};

const STORAGE_KEY = 'jf_lang';

function readStoredLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'ar' || v === 'en') return v;
  } catch {
    // ignore
  }
  return 'en';
}

export interface I18nState {
  lang: Lang;
  dir: Dir;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

export function useI18nState(): I18nState {
  const [lang, setLangState] = useState<Lang>(() => readStoredLang());

  useEffect(() => {
    const root = document.documentElement;
    root.lang = lang;
    root.dir = lang === 'ar' ? 'rtl' : 'ltr';
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
  }, []);

  const t = useCallback(
    (key: string): string => {
      const dict = I18N[lang];
      const value: string | undefined = dict[key];
      return value ?? key;
    },
    [lang],
  );

  return {
    lang,
    dir: lang === 'ar' ? 'rtl' : 'ltr',
    setLang,
    t,
  };
}
